import {
  UserMessageAttachments,
} from "@/components/assistant-ui/attachment";
import { usePhotoViewer } from "@/components/photos/PhotoViewerProvider";
import { MessageText } from "@/components/assistant-ui/message-text";
import { ToolFallback } from "@/components/assistant-ui/tool-fallback";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { Button } from "@penmate/react-shared/ui/button";
import { SendGlow } from "@/components/ui/send-glow";
import { cn } from "@penmate/react-shared/lib/utils";
import { getIsResend } from "@/lib/flags";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  AssistantIf,
  BranchPickerPrimitive,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAssistantState,
  useAssistantApi,
} from "@assistant-ui/react";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  AlertTriangleIcon,
  CheckCircle2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  ImageIcon,
  ImagePlusIcon,
  InfoIcon,
  MessageCircleIcon,
  Maximize2Icon,
  RotateCcwIcon,
  TruckIcon,
  SquareIcon,
  RefreshCwIcon,
  TagIcon,
} from "lucide-react";
import type { FC } from "react";
import type { MediaItem, ThreadEvent } from "@/domain/threadEvent";
import { MessageFrame } from "@/components/messages/MessageFrame";
import { PmMessageContent } from "@/components/messages/PmMessageContent";
import { saveDraft, saveDraftText } from "@/api/drafts";
import { PostcardEditorModal } from "@/components/postcards/PostcardEditorModal";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { deleteMessage } from "@/api/deleteMessage";
import { useQueryClient } from "@tanstack/react-query";
import type { PmUser } from "@/api/user";
import type { RawMessage } from "@/api/messages";
import type { PmReactNavigateIntent } from "@/PmReactMessagingApp";
import { formatUsd, getCheckoutRequirements, type CheckoutSectionKey } from "@/checkout/requirements";
import { evaluatePreattach, fetchPreattachMethod, hasPaymentReturnParams } from "@/checkout/preattach";
import { mapOffersByBundle, getOfferBonusCredits, getOfferSavingsCents, getOfferEffectiveCredits, offerContextKey } from "@/checkout/dynamicPricing";
import { DeliveryFacilitySheet } from "@/components/checkout/DeliveryFacilitySheet";
import { ReturnAddressSheet } from "@/components/checkout/ReturnAddressSheet";
import { PhotoTypeSheet, type PhotoTypeSelection } from "@/components/checkout/PhotoTypeSheet";
import { CreditsSheet } from "@/components/checkout/CreditsSheet";
import { PaymentSheet } from "@/components/checkout/PaymentSheet";
import { CheckoutRecoverySheet } from "@/components/checkout/CheckoutRecoverySheet";
import {
  acceptRecoveryOffer,
  isRescueUnavailableError,
  markRecoveryOfferDisplayed,
  redeemFreeRescue,
  requestRecoveryOffer,
  type RecoveryOffer,
  type RecoveryTrigger,
} from "@/api/recoveryOffer";
import {
  checkoutRecoveryEnabled,
  consumeRecoveryReturnFlag,
  evaluateCheckoutAbandonGate,
  evaluateRecoveryTrigger,
  letterHasRecoveryOffer,
  letterRecoveryApplied,
  letterRecoveryReminder,
  letterRescueAvailable,
  letterRescueCopy,
  type RescueCopy,
  markRecoveryIssuanceDisplayed,
  markRecoverySweetenerDisplayed,
  recordRecoveryAbandonment,
  recoveryRescueIntent,
  RECOVERY_RESCUE_ERROR_MESSAGE,
  RECOVERY_RESCUE_UNAVAILABLE_MESSAGE,
  type RecoveryReminder,
  RECOVERY_ACCEPT_ERROR_MESSAGE,
  RECOVERY_REQUEST_TIMEOUT_MS,
  RECOVERY_RETURN_TIMEOUT_MS,
  recoveryTriggerKind,
  withRecoveryTimeout,
} from "@/checkout/recovery";
import { fetchLetterOffers, type DynamicPricingOffer } from "@/api/offers";
import { trackMetric } from "@/api/metrics";
import { fetchBillingCheckoutStatus, fetchBillingPaymentMethod, toggleSaveBilling } from "@/api/billing";
import {
  clearSaveBillingChoice,
  readSaveBillingChoice,
  saveBillingChoiceEnabled,
} from "@/checkout/saveBillingChoice";
import { confirmLetterPayment, fetchLetter, sendLetter } from "@/api/letters";
import { createUploadPhotoUrl, putFileToPresignedUrl, uploadPhotoComplete } from "@/api/uploads";
import { loadStripe } from "@stripe/stripe-js";
import { PhoneVerificationSheet } from "@/components/checkout/PhoneVerificationSheet";
import {
  compressImageFile,
  DEFAULT_MAX_SELECTED_PHOTOS,
  EMESSAGE_MAX_SELECTED_PHOTOS,
  filterImageFiles,
} from "@/lib/images";
import { buildPenmateMetricsPayload } from "@/lib/metricsPayload";
import {
  cancelScheduledStopCheckoutReplay,
  maybeStartCheckoutReplay,
  scheduleStopCheckoutReplay,
  stopAllReplays,
  trackReplayError,
  trackReplayStage,
} from "@/lib/posthogReplay";

export type ThreadProps = {
  viewportRef?: React.Ref<HTMLDivElement>;
  onViewportScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  emptyState?: "loading" | "empty" | "none";
  draftsLoading?: boolean;
  messageType?: string;
  penmateId?: string;
  penmate?: any;
  sessionId?: number;
  onNavigate?: (intent: PmReactNavigateIntent) => void;
  hasIncomingReplies?: boolean;
  checkoutDisabled?: boolean;
  existingDraftsByType?: {
    letter: { id: string; type: "letter"; raw: any } | null;
    postcard: { id: string; type: "postcard"; raw: any } | null;
  };
  checkoutDraftId?: string | null;
  checkoutEntryNotice?: string | null;
  checkoutUser?: PmUser | null;
  checkoutUserLoading?: boolean;
  checkoutLetter?: RawMessage | null;
  checkoutLetterLoading?: boolean;
  onEnterCheckout?: (draftId: string) => void;
  onCancelCheckout?: () => void;
  onResendDraftDeleted?: (draftId: string) => void;
};

const POSTCARD_MAX_CHARS = 200;
const GTM_SEND_LETTER_EVENT = "send_letter";

const shouldLogTrackingErrors = (): boolean => {
  if (typeof window === "undefined") return false;
  const env = (window as any)?.Penmate?.env?.name;
  return env === "development";
};

const pushDataLayerEvent = (eventName: string, payload: Record<string, unknown> = {}) => {
  try {
    if (typeof window === "undefined") return;
    const win = window as any;
    win.dataLayer = win.dataLayer || [];
    win.dataLayer.push({ ...payload, event: eventName });
  } catch (error) {
    if (shouldLogTrackingErrors()) {
      // eslint-disable-next-line no-console
      console.warn("GTM dataLayer push failed", error);
    }
  }
};

type DraftOverride = {
  text: string;
  photos: readonly MediaItem[];
  selectedPhotosPayload: readonly any[];
  payloadBase: Record<string, unknown>;
};

type DraftCreateResult = {
  draftId: string;
  override: DraftOverride;
};

type DraftPhotoUpload = {
  id: string;
  filename: string;
  previewUrl: string;
  status: "queued" | "preparing" | "uploading" | "finalizing" | "error";
  percent: number;
  error?: string;
};

type DraftUiApi = {
  editingDraftId: string | null;
  getDraftOverride: (event: ThreadEvent) => DraftOverride;
  getDraftOverrideById: (draftId: string) => DraftOverride | null;
  syncDraftOverrideFromRaw: (draftId: string, raw: any, opts?: { preserveText?: boolean }) => void;
  setEditingDraftId: (id: string | null) => void;
  primeDraftAutosave: (draftId: string, message: string) => void;
  openPostcardEditor: (draftId: string) => void;
  closePostcardEditor: () => void;
  ensureDraftOverride: (event: ThreadEvent) => void;
  ensureDraftOverrideFromRaw: (draftId: string, raw: any) => DraftOverride;
  setDraftText: (draftId: string, text: string) => void;
  appendDraftSelectedPhotos: (draftId: string, photos: readonly any[]) => Promise<boolean>;
  removeDraftPhoto: (draftId: string, index: number) => void;
  setDraftPostcardFrontUrl: (draftId: string, url: string) => void;
  persistDraftNow: (draftId: string, snapshot: DraftOverride) => Promise<boolean>;
  requestDeleteDraft: (draftId: string) => void;
  scheduleDraftTextSave: (draftId: string, message: string) => void;
  cancelDraftAutosave: (draftId: string) => void;
  focusComposer: () => void;
  scrollToBottom: () => void;
};

const DraftUiContext = createContext<DraftUiApi | null>(null);
const CheckoutContext = createContext<boolean>(false);
// True only while a send is actually submitting (slice 2.2, D-012): the
// draft bubble's `Sending…` must reflect a real send, not an open checkout.
const CheckoutSendingContext = createContext<boolean>(false);
const useCheckoutSending = (): boolean => useContext(CheckoutSendingContext);
const CheckoutDisabledContext = createContext<boolean>(false);
const HostNavContext = createContext<{ penmateId: string | null; onNavigate?: (intent: PmReactNavigateIntent) => void }>({
  penmateId: null,
});
const ComposeModeContext = createContext<{
  composeMode: "letter" | "postcard";
  setComposeMode: (mode: "letter" | "postcard") => void;
} | null>(null);

const useDraftUi = (): DraftUiApi => {
  const ctx = useContext(DraftUiContext);
  if (!ctx) throw new Error("useDraftUi must be used within DraftUiContext");
  return ctx;
};

const useCheckoutActive = (): boolean => useContext(CheckoutContext);
const useCheckoutDisabled = (): boolean => useContext(CheckoutDisabledContext);
const useHostNav = (): { penmateId: string | null; onNavigate?: (intent: PmReactNavigateIntent) => void } =>
  useContext(HostNavContext);
const useComposeMode = (): { composeMode: "letter" | "postcard"; setComposeMode: (mode: "letter" | "postcard") => void } => {
  const ctx = useContext(ComposeModeContext);
  if (!ctx) throw new Error("useComposeMode must be used within ComposeModeContext");
  return ctx;
};

const usePrefersReducedMotion = (): boolean => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(!!mq.matches);
    update();
    // Safari < 14 uses addListener/removeListener.
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    }
    // eslint-disable-next-line deprecation/deprecation
    mq.addListener(update);
    // eslint-disable-next-line deprecation/deprecation
    return () => mq.removeListener(update);
  }, []);
  return reduced;
};

const getRawDeliveryState = (raw: any): string => {
  const state = raw?.status ?? raw?.data?.status;
  return typeof state === "string" ? state : "";
};

const resolveMessageValue = (value: unknown): { text: string; hasValue: boolean } => {
  if (typeof value === "string") return { text: value, hasValue: true };
  if (!value || typeof value !== "object") return { text: "", hasValue: false };
  const text = (value as any).text;
  return typeof text === "string" ? { text, hasValue: true } : { text: "", hasValue: false };
};

const hasMessageKey = (value: unknown): boolean => {
  return (
    !!value &&
    typeof value === "object" &&
    Object.prototype.hasOwnProperty.call(value as Record<string, unknown>, "message")
  );
};

const resolveDraftMessageText = (
  payloadBase: unknown,
  raw: unknown,
): { text: string; hasValue: boolean } => {
  if (hasMessageKey(payloadBase)) {
    const value = (payloadBase as any).message;
    const resolved = resolveMessageValue(value);
    if (resolved.hasValue) return resolved;
  }
  if (hasMessageKey(raw)) {
    const value = (raw as any).message;
    const resolved = resolveMessageValue(value);
    if (resolved.hasValue) return resolved;
  }
  return { text: "", hasValue: false };
};

const hasAddressLine1 = (addr: any | null): boolean => {
  if (!addr || typeof addr !== "object") return false;
  const l1 = (addr as any).address_line1 ?? (addr as any).line1 ?? (addr as any).email_address_line1 ?? (addr as any).formatted_address;
  return typeof l1 === "string" && l1.trim().length > 0;
};

const attachPenmatePayload = (
  payload: Record<string, unknown>,
  penmate: unknown,
  penmateId: string | null | undefined,
): Record<string, unknown> => {
  const next: Record<string, unknown> = { ...payload };
  if (penmate && typeof penmate === "object") {
    const penmatePayload = { ...(penmate as Record<string, unknown>) };
    delete (penmatePayload as Record<string, unknown>).last_message_from_user;
    next.penmate = penmatePayload;
  }
  const resolvedId = (penmate as any)?.id ?? penmateId ?? null;
  if (resolvedId != null && !next.penmate_id) {
    next.penmate_id = resolvedId;
  }
  return next;
};

const metricBoolOrNull = (value: unknown): boolean | null => {
  if (typeof value === "boolean") return value;
  return null;
};

const metricNumberOrNull = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const n = Number.parseFloat(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const trackReplayStageFromMetricPayload = (
  stage: Parameters<typeof trackReplayStage>[0],
  sourceEvent: string,
  payload: Record<string, unknown>,
): void => {
  trackReplayStage(stage, {
    sourceEvent,
    letterId: payload.letter_id as any,
    penmateId: payload.penmate_id as any,
    messageType: payload.message_type as any,
    shippingMethod: payload.shipping_method as any,
    paymentRequired: metricBoolOrNull(payload.payment_required),
    creditOption: payload.credit_option as any,
    priceCents: metricNumberOrNull(payload.price_after_coupon ?? payload.price),
    offerRedemptionId: payload.offer_redemption_id as any,
    dpHasOffers: metricBoolOrNull(payload.dp_has_offers),
  });
};

type ReplaySubmitErrorCategory = "payment" | "validation" | "send" | "exception";

const trackReplaySubmitError = (
  basePayload: Record<string, unknown>,
  category: ReplaySubmitErrorCategory,
): void => {
  trackReplayError({
    sourceEvent: "submit-letter-error",
    errorCategory: category,
    messageType: basePayload.message_type as any,
    shippingMethod: basePayload.shipping_method as any,
    paymentRequired: metricBoolOrNull(basePayload.payment_required),
  });
};

const isDraftEvent = (event: ThreadEvent): boolean => {
  return getRawDeliveryState(event.raw) === "draft";
};

const getEventMessageType = (event: ThreadEvent): string => {
  const fromRaw = (event.raw as any)?.data?.messageType;
  if (typeof fromRaw === "string" && fromRaw) return fromRaw.toLowerCase();
  return event.kind;
};

const toMessageTypeKey = (value: unknown): "letter" | "postcard" => {
  const s = typeof value === "string" ? value.toLowerCase() : "";
  return s === "postcard" ? "postcard" : "letter";
};

const parseDateToTs = (value: unknown): number => {
  if (typeof value !== "string" || !value) return 0;
  const ts = Date.parse(value);
  return Number.isFinite(ts) ? ts : 0;
};

const getDraftRecencyTs = (raw: any): number => {
  return (
    parseDateToTs(raw?.updated_at) ||
    parseDateToTs(raw?.created_at) ||
    parseDateToTs(raw?.timestamp)
  );
};

const getDraftPostcardFrontUrl = (payloadBase: Record<string, unknown> | null | undefined): string => {
  const url =
    (payloadBase as any)?.postcard?.frontImage?.url ??
    (payloadBase as any)?.postcard?.frontImageUrl ??
    (payloadBase as any)?.postcard?.front_image_url;
  return typeof url === "string" ? url : "";
};

const toMediaItemsFromPayload = (payload: readonly any[]): MediaItem[] => {
  return payload
    .map((p: any) => {
      const url = p && typeof p.url === "string" ? p.url : "";
      if (!url) return null;
      return {
        url,
        filename: typeof p.filename === "string" ? p.filename : undefined,
        img_path: typeof p.img_path === "string" ? p.img_path : undefined,
        portrait_url: typeof p.portrait_url === "string" ? p.portrait_url : undefined,
      } satisfies MediaItem;
    })
    .filter((p): p is MediaItem => !!p);
};

// The API returns expiring, signed S3 URLs for photos. When saving draft text we may receive
// fresh URLs with new signatures; updating <img src> causes visible flicker. Preserve the
// existing URLs when the underlying image (img_path) hasn't changed.
const stabilizeSelectedPhotoUrls = (
  existing: readonly any[],
  incoming: readonly any[],
): readonly any[] => {
  if (!existing.length || !incoming.length) return incoming;
  const byImgPath = new Map<string, any>();
  for (const p of existing) {
    const imgPath = p && typeof p.img_path === "string" ? p.img_path : "";
    if (imgPath) byImgPath.set(imgPath, p);
  }
  if (!byImgPath.size) return incoming;

  return incoming.map((p) => {
    const imgPath = p && typeof p.img_path === "string" ? p.img_path : "";
    if (!imgPath) return p;
    const prev = byImgPath.get(imgPath);
    if (!prev) return p;
    const next = { ...(p as any) };
    const prevUrl = prev && typeof prev.url === "string" ? prev.url : "";
    const nextUrl = p && typeof (p as any).url === "string" ? (p as any).url : "";
    if (prevUrl && (!nextUrl || prevUrl !== nextUrl)) next.url = prevUrl;
    const prevPortrait = prev && typeof prev.portrait_url === "string" ? prev.portrait_url : "";
    const nextPortrait = p && typeof (p as any).portrait_url === "string" ? (p as any).portrait_url : "";
    if (prevPortrait && (!nextPortrait || prevPortrait !== nextPortrait)) next.portrait_url = prevPortrait;
    return next;
  });
};

const getDraftSelectedPhotosPayload = (event: ThreadEvent): readonly any[] => {
  const raw = event.raw as any;
  const fromData = raw?.data?.selectedPhotos;
  if (Array.isArray(fromData)) return fromData;
  const fromTop = raw?.photos;
  if (Array.isArray(fromTop)) return fromTop;
  return [];
};

const toComposerMode = (value: unknown): "letter" | "postcard" => {
  const s = typeof value === "string" ? value.toLowerCase().trim() : "";
  return s === "postcard" ? "postcard" : "letter";
};

const clampDraftText = (args: { messageType: "letter" | "postcard"; text: string }): string => {
  if (args.messageType !== "postcard") return args.text;
  return args.text.length > POSTCARD_MAX_CHARS ? args.text.slice(0, POSTCARD_MAX_CHARS) : args.text;
};

const extractDraftMessageText = (raw: any): string => {
  const msg =
    (raw?.data && typeof raw.data === "object" ? (raw.data as any).message : undefined) ??
    raw?.message ??
    raw?.summary ??
    "";
  return typeof msg === "string" ? msg : "";
};

export const Thread: FC<ThreadProps> = ({
  viewportRef,
  onViewportScroll,
  emptyState = "none",
  draftsLoading = false,
  messageType,
  penmateId,
  penmate,
  sessionId,
  onNavigate,
  hasIncomingReplies = false,
  checkoutDisabled = false,
  existingDraftsByType,
  checkoutDraftId,
  checkoutEntryNotice,
  checkoutUser,
  checkoutUserLoading = false,
  checkoutLetter,
  checkoutLetterLoading,
  onEnterCheckout,
  onCancelCheckout,
  onResendDraftDeleted,
}) => {
  const queryClient = useQueryClient();
  const api = useAssistantApi();
  const checkoutActive = !!checkoutDraftId;
  const prefersReducedMotion = usePrefersReducedMotion();
  const [composeMode, setComposeModeRaw] = useState<"letter" | "postcard">(() => toComposerMode(messageType));
  const setComposeMode = useCallback((mode: "letter" | "postcard") => {
    setComposeModeRaw(mode);
  }, []);
  const didAutoSelectComposeModeRef = useRef(false);
  const isEMessageMode = typeof messageType === "string" && messageType.toLowerCase().trim() === "emessage";
  const explicitModeFromProps = useMemo(() => {
    if (typeof messageType !== "string") return null;
    const m = messageType.toLowerCase().trim();
    if (m !== "letter" && m !== "postcard") return null;
    return toComposerMode(m);
  }, [messageType]);
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [postcardEditorDraftId, setPostcardEditorDraftId] = useState<string | null>(null);
  const [draftOverrides, setDraftOverrides] = useState<Map<string, DraftOverride>>(
    () => new Map(),
  );
  const [draftSaveError, setDraftSaveError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteTargetIsResend, setDeleteTargetIsResend] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const composerInputRef = useRef<HTMLTextAreaElement | null>(null);
  const viewportElRef = useRef<HTMLDivElement | null>(null);
  const setViewportEl = useCallback(
    (node: HTMLDivElement | null) => {
      viewportElRef.current = node;
      if (!viewportRef) return;
      if (typeof viewportRef === "function") {
        viewportRef(node);
        return;
      }
      if (typeof viewportRef === "object" && viewportRef && "current" in viewportRef) {
        (viewportRef as any).current = node;
      }
    },
    [viewportRef],
  );
  const draftAutosaveTimerRef = useRef<Map<string, number>>(new Map());
  const lastAutosavedTextRef = useRef<Map<string, string>>(new Map());
  const draftAutosaveSeqRef = useRef<Map<string, number>>(new Map());

  const applySavedDraft = useCallback((draftId: string, raw: any) => {
    setDraftOverrides((current) => {
      const existing = current.get(draftId);
      if (!existing) return current;
      const payloadBase =
        raw?.data && typeof raw.data === "object" ? (raw.data as Record<string, unknown>) : existing.payloadBase;
      const hasSelectedPhotosField = Array.isArray((payloadBase as any)?.selectedPhotos);
      const incomingSelected = hasSelectedPhotosField
        ? (((payloadBase as any).selectedPhotos as readonly any[]) ?? [])
        : existing.selectedPhotosPayload;
      const selectedPhotosPayload = hasSelectedPhotosField
        ? stabilizeSelectedPhotoUrls(existing.selectedPhotosPayload, incomingSelected)
        : incomingSelected;
      const photos =
        hasSelectedPhotosField || selectedPhotosPayload.length
          ? toMediaItemsFromPayload(selectedPhotosPayload)
          : existing.photos;
      const resolved = resolveDraftMessageText(payloadBase, raw);
      const text = resolved.hasValue ? resolved.text : existing.text;
      const next = new Map(current);
      next.set(draftId, { ...existing, payloadBase, selectedPhotosPayload, photos, text });
      return next;
    });
  }, []);

  const syncDraftOverrideFromRaw = useCallback(
    (draftId: string, raw: any, opts?: { preserveText?: boolean }) => {
      const preserveText = opts?.preserveText !== false;
      setDraftOverrides((current) => {
        const existing = current.get(draftId);
        if (!existing) return current;
        const payloadBase =
          raw?.data && typeof raw.data === "object" ? (raw.data as Record<string, unknown>) : existing.payloadBase;
        const hasSelectedPhotosField = Array.isArray((payloadBase as any)?.selectedPhotos);
        const incomingSelected = hasSelectedPhotosField
          ? (((payloadBase as any).selectedPhotos as readonly any[]) ?? [])
          : existing.selectedPhotosPayload;
        const selectedPhotosPayload = hasSelectedPhotosField
          ? stabilizeSelectedPhotoUrls(existing.selectedPhotosPayload, incomingSelected)
          : incomingSelected;
        const messageType = toMessageTypeKey((payloadBase as any)?.messageType ?? (payloadBase as any)?.order_type);
        const photos =
          messageType === "postcard"
            ? []
            : hasSelectedPhotosField
              ? toMediaItemsFromPayload(selectedPhotosPayload)
              : existing.photos;
        const resolved = resolveDraftMessageText(payloadBase, raw);
        const nextText = preserveText ? existing.text : resolved.hasValue ? resolved.text : existing.text;
        const next = new Map(current);
        next.set(draftId, { ...existing, payloadBase, selectedPhotosPayload, photos, text: nextText });
        return next;
      });
    },
    [],
  );

  const withPenmatePayload = useCallback(
    (payload: Record<string, unknown>): Record<string, unknown> =>
      attachPenmatePayload(payload, penmate, penmateId),
    [penmate, penmateId],
  );

  const persistDraftNow = useCallback(
    async (draftId: string, snapshot: DraftOverride) => {
      try {
        const payloadBase: Record<string, unknown> = {
          ...snapshot.payloadBase,
          id: draftId,
          message: snapshot.text,
          summary: snapshot.text,
          // Preserve legacy draft selectedPhotos even when converting to postcard.
          selectedPhotos: snapshot.selectedPhotosPayload,
        };
        const payload = withPenmatePayload(payloadBase);
        const saved = await saveDraft({ draft: payload });
        applySavedDraft(draftId, saved);
        setDraftSaveError(null);
        return true;
      } catch (e) {
        setDraftSaveError(e instanceof Error ? e.message : String(e));
        return false;
      }
    },
    [applySavedDraft, withPenmatePayload],
  );

  const getDraftOverride = useCallback(
    (event: ThreadEvent): DraftOverride => {
      const existing = draftOverrides.get(event.id);
      const messageType = getEventMessageType(event);
      if (existing) {
        const existingTypeRaw = (existing.payloadBase as any)?.messageType;
        const existingType =
          typeof existingTypeRaw === "string" && existingTypeRaw
            ? existingTypeRaw.toLowerCase()
            : "";
        if (messageType && existingType && messageType !== existingType) {
          // If the draft was converted (e.g. letter -> postcard), ignore stale override data.
        } else {
          return messageType === "postcard"
            ? { ...existing, photos: [], selectedPhotosPayload: [] }
            : existing;
        }
      }
      const selectedPhotosPayload = getDraftSelectedPhotosPayload(event);
      const payloadBase = {
        ...(event.raw?.data && typeof event.raw.data === "object" ? event.raw.data : {}),
        id: event.raw?.id ?? event.id,
      } as Record<string, unknown>;
      return {
        text: event.text,
        photos:
          messageType === "postcard"
            ? []
            : selectedPhotosPayload.length
              ? toMediaItemsFromPayload(selectedPhotosPayload)
              : event.photos,
        selectedPhotosPayload,
        payloadBase,
      };
    },
    [draftOverrides],
  );

  const getDraftOverrideById = useCallback(
    (draftId: string): DraftOverride | null => {
      return draftOverrides.get(draftId) ?? null;
    },
    [draftOverrides],
  );

  const buildDraftOverrideFromRaw = useCallback((draftId: string, raw: any): DraftOverride => {
    const payloadBase =
      raw?.data && typeof raw.data === "object"
        ? ({ ...(raw.data as Record<string, unknown>), id: raw?.id ?? draftId } as Record<string, unknown>)
        : ({ id: raw?.id ?? draftId } as Record<string, unknown>);
    const selectedPhotosPayload = (() => {
      const fromData = raw?.data?.selectedPhotos;
      if (Array.isArray(fromData)) return fromData as readonly any[];
      const fromTop = raw?.photos;
      if (Array.isArray(fromTop)) return fromTop as readonly any[];
      return [] as readonly any[];
    })();
    const mt = toMessageTypeKey((payloadBase as any)?.messageType ?? raw?.order_type);
    const resolved = resolveDraftMessageText(payloadBase, raw);
    const text = resolved.hasValue ? resolved.text : "";
    return {
      text,
      photos: mt === "postcard" ? [] : toMediaItemsFromPayload(selectedPhotosPayload),
      selectedPhotosPayload,
      payloadBase,
    };
  }, []);

  const ensureDraftOverride = useCallback((event: ThreadEvent) => {
    setDraftOverrides((current) => {
      if (current.has(event.id)) return current;
      const next = new Map(current);
      const selectedPhotosPayload = getDraftSelectedPhotosPayload(event);
      const payloadBase = {
        ...(event.raw?.data && typeof event.raw.data === "object" ? event.raw.data : {}),
        id: event.raw?.id ?? event.id,
      } as Record<string, unknown>;
      next.set(event.id, {
        text: event.text,
        photos: selectedPhotosPayload.length ? toMediaItemsFromPayload(selectedPhotosPayload) : event.photos,
        selectedPhotosPayload,
        payloadBase,
      });
      return next;
    });
  }, []);

  const ensureDraftOverrideFromRaw = useCallback(
    (draftId: string, raw: any): DraftOverride => {
      const override = buildDraftOverrideFromRaw(draftId, raw);
      setDraftOverrides((current) => {
        if (current.has(draftId)) return current;
        const next = new Map(current);
        next.set(draftId, override);
        return next;
      });
      return override;
    },
    [buildDraftOverrideFromRaw],
  );

  const setDraftText = useCallback((draftId: string, text: string) => {
    setDraftOverrides((current) => {
      const existing = current.get(draftId);
      if (!existing) return current;
      if (existing.text === text) return current;
      const next = new Map(current);
      next.set(draftId, { ...existing, text });
      return next;
    });
  }, []);

  const appendDraftSelectedPhotos = useCallback(
    async (draftId: string, photosToAdd: readonly any[]) => {
      if (!photosToAdd.length) return true;
      let nextSnapshot: DraftOverride | null = null;
      setDraftOverrides((current) => {
        const existing = current.get(draftId);
        if (!existing) return current;
        const nextSelected = existing.selectedPhotosPayload.concat(photosToAdd);
        const nextPhotos = toMediaItemsFromPayload(nextSelected);
        const payloadBase: Record<string, unknown> = { ...existing.payloadBase, selectedPhotos: nextSelected };
        nextSnapshot = { ...existing, payloadBase, selectedPhotosPayload: nextSelected, photos: nextPhotos };
        const next = new Map(current);
        next.set(draftId, nextSnapshot);
        return next;
      });
      if (!nextSnapshot) return false;
      return await persistDraftNow(draftId, nextSnapshot);
    },
    [persistDraftNow],
  );

  const removeDraftPhoto = useCallback(
    (draftId: string, index: number) => {
      let nextSnapshot: DraftOverride | null = null;
      setDraftOverrides((current) => {
        const existing = current.get(draftId);
        if (!existing) return current;
        if (!existing.selectedPhotosPayload.length) return current;
        const nextSelected = existing.selectedPhotosPayload.filter((_, idx) => idx !== index);
        const nextPhotos = toMediaItemsFromPayload(nextSelected);
        nextSnapshot = { ...existing, selectedPhotosPayload: nextSelected, photos: nextPhotos };
        const next = new Map(current);
        next.set(draftId, nextSnapshot);
        return next;
      });
      if (nextSnapshot) {
        // Immediate save after photo removal (legacy behavior).
        void persistDraftNow(draftId, nextSnapshot);
      }
    },
    [persistDraftNow],
  );

  const setDraftPostcardFrontUrl = useCallback(
    (draftId: string, url: string) => {
      let nextSnapshot: DraftOverride | null = null;
      setDraftOverrides((current) => {
        const existing = current.get(draftId);
        if (!existing) return current;
        const payloadBase: Record<string, unknown> = { ...existing.payloadBase };
        const postcard = (payloadBase as any).postcard && typeof (payloadBase as any).postcard === "object"
          ? { ...(payloadBase as any).postcard }
          : {};
        const frontImage =
          postcard.frontImage && typeof postcard.frontImage === "object"
            ? { ...postcard.frontImage }
            : {};
        frontImage.url = url;
        postcard.frontImage = frontImage;
        (payloadBase as any).postcard = postcard;
        nextSnapshot = { ...existing, payloadBase };
        const next = new Map(current);
        next.set(draftId, nextSnapshot);
        return next;
      });
      if (nextSnapshot) {
        void persistDraftNow(draftId, nextSnapshot).then((ok) => {
          if (!ok) return;
          void Promise.all([
            queryClient.invalidateQueries({ queryKey: ["pmreact", "letters", "by-id"] }),
            queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] }),
          ]);
        });
      }
    },
    [persistDraftNow, queryClient],
  );

  const scheduleDraftTextSave = useCallback(
    (draftId: string, message: string) => {
      const prev = lastAutosavedTextRef.current.get(draftId) ?? "";
      if (prev === message) return;
      const nextSeq = (draftAutosaveSeqRef.current.get(draftId) ?? 0) + 1;
      draftAutosaveSeqRef.current.set(draftId, nextSeq);
      const prevTimer = draftAutosaveTimerRef.current.get(draftId);
      if (prevTimer != null) {
        window.clearTimeout(prevTimer);
      }
      const timerId = window.setTimeout(() => {
        saveDraftText({ draftId, message })
          .then((saved) => {
            if (draftAutosaveSeqRef.current.get(draftId) !== nextSeq) return;
            lastAutosavedTextRef.current.set(draftId, message);
            applySavedDraft(draftId, saved);
            setDraftSaveError(null);
          })
          .catch((e) => {
            if (draftAutosaveSeqRef.current.get(draftId) !== nextSeq) return;
            setDraftSaveError(e instanceof Error ? e.message : String(e));
          })
          .finally(() => {
            draftAutosaveTimerRef.current.delete(draftId);
          });
      }, 700);
      draftAutosaveTimerRef.current.set(draftId, timerId);
    },
    [applySavedDraft],
  );

  const primeDraftAutosave = useCallback((draftId: string, message: string) => {
    lastAutosavedTextRef.current.set(draftId, message);
    draftAutosaveSeqRef.current.set(draftId, (draftAutosaveSeqRef.current.get(draftId) ?? 0) + 1);
    const timer = draftAutosaveTimerRef.current.get(draftId);
    if (timer != null) {
      window.clearTimeout(timer);
      draftAutosaveTimerRef.current.delete(draftId);
    }
  }, []);

  const cancelDraftAutosave = useCallback((draftId: string) => {
    draftAutosaveSeqRef.current.set(draftId, (draftAutosaveSeqRef.current.get(draftId) ?? 0) + 1);
    const timer = draftAutosaveTimerRef.current.get(draftId);
    if (timer != null) {
      window.clearTimeout(timer);
      draftAutosaveTimerRef.current.delete(draftId);
    }
  }, []);

  const getIsResendDraftId = useCallback(
    (draftId: string): boolean => {
      if (existingDraftsByType?.letter?.id === draftId) {
        return getIsResend(existingDraftsByType.letter.raw);
      }
      if (existingDraftsByType?.postcard?.id === draftId) {
        return getIsResend(existingDraftsByType.postcard.raw);
      }
      const override = draftOverrides.get(draftId);
      return override ? getIsResend(override.payloadBase) : false;
    },
    [draftOverrides, existingDraftsByType],
  );

  const requestDeleteDraft = useCallback(
    (draftId: string) => {
      if (!penmateId) return;
      if (!draftId) return;
      if (checkoutActive) return;
      setDeleteError(null);
      setDeleteTargetId(draftId);
      setDeleteTargetIsResend(getIsResendDraftId(draftId));
      setDeleteConfirmOpen(true);
    },
    [checkoutActive, getIsResendDraftId, penmateId],
  );

  const deleteDraftNow = useCallback(async () => {
    if (!penmateId || !deleteTargetId) {
      setDeleteError("Missing penmate or draft id.");
      return;
    }
      try {
        setIsDeleting(true);
        setDeleteError(null);
        await deleteMessage({ penmateId, messageId: deleteTargetId });
        setDeleteConfirmOpen(false);

      // Clear local overrides and trigger a thread refresh after delete.
      setDraftOverrides((current) => {
        if (!current.has(deleteTargetId)) return current;
        const next = new Map(current);
        next.delete(deleteTargetId);
        return next;
      });
      if (editingDraftId === deleteTargetId) setEditingDraftId(null);
      if (deleteTargetIsResend) {
        onResendDraftDeleted?.(deleteTargetId);
      }
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] }),
        queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "drafts"], exact: false }),
      ]);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsDeleting(false);
    }
  }, [
    deleteTargetId,
    deleteTargetIsResend,
    editingDraftId,
    onResendDraftDeleted,
    penmateId,
    queryClient,
  ]);

  const openPostcardEditor = useCallback((draftId: string) => {
    setPostcardEditorDraftId(draftId);
  }, []);

  const closePostcardEditor = useCallback(() => {
    setPostcardEditorDraftId(null);
  }, []);

  const scrollToBottom = useCallback(() => {
    const viewport = viewportElRef.current;
    if (!viewport) return;
    viewport.scrollTop = viewport.scrollHeight;
  }, []);

  const focusComposer = useCallback(() => {
    composerInputRef.current?.focus();
  }, []);

  const draftUi = useMemo<DraftUiApi>(
    () => ({
      editingDraftId,
      getDraftOverride,
      getDraftOverrideById,
      syncDraftOverrideFromRaw,
      setEditingDraftId,
      primeDraftAutosave,
      openPostcardEditor,
      closePostcardEditor,
      ensureDraftOverride,
      ensureDraftOverrideFromRaw,
      removeDraftPhoto,
      setDraftText,
      setDraftPostcardFrontUrl,
      appendDraftSelectedPhotos,
      persistDraftNow,
      scheduleDraftTextSave,
      cancelDraftAutosave,
      focusComposer,
      scrollToBottom,
      requestDeleteDraft,
    }),
    [
      cancelDraftAutosave,
      editingDraftId,
      ensureDraftOverride,
      ensureDraftOverrideFromRaw,
      focusComposer,
      getDraftOverride,
      getDraftOverrideById,
      syncDraftOverrideFromRaw,
      primeDraftAutosave,
      openPostcardEditor,
      closePostcardEditor,
      removeDraftPhoto,
      setDraftText,
      setDraftPostcardFrontUrl,
      appendDraftSelectedPhotos,
      persistDraftNow,
      scheduleDraftTextSave,
      scrollToBottom,
      requestDeleteDraft,
    ],
  );

  const sendScrollTimerRef = useRef<number | null>(null);
  const postSendGlowTimerRef = useRef<number | null>(null);
  const postSendHoldMinTimerRef = useRef<number | null>(null);
  const postSendHoldMaxTimerRef = useRef<number | null>(null);
  const postSendScrollTimersRef = useRef<number[]>([]);
  const postSendBannerTimerRef = useRef<number | null>(null);
  const [checkoutFooterPhase, setCheckoutFooterPhase] = useState<"idle" | "sending">("idle");
  const [recentlySentDraftId, setRecentlySentDraftId] = useState<string | null>(null);
  const [postSendGlowActive, setPostSendGlowActive] = useState(false);
  const [postSendHoldActive, setPostSendHoldActive] = useState(false);
  const [postSendHoldMinExpired, setPostSendHoldMinExpired] = useState(true);
  const [postSendBannerActive, setPostSendBannerActive] = useState(false);
  const [postSendBannerPending, setPostSendBannerPending] = useState(false);

  const recentlySentStillPresent =
    !!recentlySentDraftId &&
    (existingDraftsByType?.letter?.id === recentlySentDraftId ||
      existingDraftsByType?.postcard?.id === recentlySentDraftId);

  const effectiveExistingDraftsByType = useMemo(() => {
    if (!existingDraftsByType) return null;
    if (!recentlySentDraftId) return existingDraftsByType;
    const next = { ...existingDraftsByType };
    if (next.letter?.id === recentlySentDraftId) next.letter = null;
    if (next.postcard?.id === recentlySentDraftId) next.postcard = null;
    return next;
  }, [existingDraftsByType, recentlySentDraftId]);

  const handleSent = useCallback(
    (id: string) => {
      // The old draft is no longer editable once sent. Prevent any debounced autosave from
      // firing against `/drafts/:id/message` after we clear the composer (which would 404).
      primeDraftAutosave(id, "");
      setDraftSaveError(null);
      setEditingDraftId(null);
      // Ensure the newly-sent message is visible even if the user is scrolled up.
      scrollToBottom();
      if (sendScrollTimerRef.current != null) window.clearTimeout(sendScrollTimerRef.current);
      sendScrollTimerRef.current = window.setTimeout(() => {
        scrollToBottom();
        sendScrollTimerRef.current = null;
      }, 180);

      setRecentlySentDraftId(id);
      setPostSendBannerPending(true);

      // Keep the shimmer briefly after checkout exits so it "lands" nicely.
      setPostSendGlowActive(true);
      if (postSendGlowTimerRef.current != null) window.clearTimeout(postSendGlowTimerRef.current);
      postSendGlowTimerRef.current = window.setTimeout(() => {
        setPostSendGlowActive(false);
        postSendGlowTimerRef.current = null;
      }, 700);

      // Keep the footer collapsed after checkout exits to avoid showing transitional
      // draft-gating UI while the backend status propagates.
      setPostSendHoldActive(true);
      setPostSendHoldMinExpired(false);
      if (postSendHoldMinTimerRef.current != null) window.clearTimeout(postSendHoldMinTimerRef.current);
      postSendHoldMinTimerRef.current = window.setTimeout(() => {
        setPostSendHoldMinExpired(true);
        postSendHoldMinTimerRef.current = null;
      }, 800);

      // Safety valve: never keep the collapsed state forever.
      if (postSendHoldMaxTimerRef.current != null) window.clearTimeout(postSendHoldMaxTimerRef.current);
      postSendHoldMaxTimerRef.current = window.setTimeout(() => {
        setPostSendHoldActive(false);
        setPostSendGlowActive(false);
        setPostSendHoldMinExpired(true);
        setRecentlySentDraftId(null);
        postSendHoldMaxTimerRef.current = null;
      }, 7000);

      // On real networks, the thread refetch can complete after our initial scroll-to-bottom.
      // Re-assert the scroll position a few times in the next ~1.2s so the newly-sent message
      // is visible when it appears.
      for (const t of postSendScrollTimersRef.current) window.clearTimeout(t);
      postSendScrollTimersRef.current = [
        window.setTimeout(() => scrollToBottom(), 250),
        window.setTimeout(() => scrollToBottom(), 650),
        window.setTimeout(() => scrollToBottom(), 1150),
        window.setTimeout(() => scrollToBottom(), 2100),
      ];
      // After a successful send, clear the composer so the just-sent text does not linger.
      try {
        api.thread().composer.setText("");
      } catch {
        // ignore
      }
    },
    [api, primeDraftAutosave, scrollToBottom],
  );

  useEffect(() => {
    if (!postSendHoldActive) return;
    if (!recentlySentDraftId) return;
    if (!postSendHoldMinExpired) return;
    if (recentlySentStillPresent) return;
    setPostSendHoldActive(false);
    setRecentlySentDraftId(null);
    requestAnimationFrame(() => scrollToBottom());
  }, [
    postSendHoldActive,
    postSendHoldMinExpired,
    recentlySentDraftId,
    recentlySentStillPresent,
    scrollToBottom,
  ]);

  useEffect(() => {
    if (!checkoutActive) setCheckoutFooterPhase("idle");
  }, [checkoutActive]);

  const sendGlowActive = (checkoutActive && checkoutFooterPhase === "sending") || postSendGlowActive;

  const handleCheckoutSendUiStateChange = useCallback(
    (args: { phase: "idle" | "sending" }) => {
      // The phase is semantic state (the truthful `Sending…` cue depends on
      // it, slice 2.2) — reduced motion suppresses only the scroll flourish.
      setCheckoutFooterPhase(args.phase);
      if (prefersReducedMotion) return;
      if (args.phase === "sending") {
        // The send confirmation just happened; make sure we're pinned to the newest content.
        scrollToBottom();
        requestAnimationFrame(() => scrollToBottom());
      }
    },
    [prefersReducedMotion, scrollToBottom],
  );

  useEffect(() => {
    if (!postSendBannerPending) return;
    const sendingPillVisible = (checkoutActive && checkoutFooterPhase === "sending") || postSendHoldActive;
    if (sendingPillVisible) return;
    setPostSendBannerPending(false);
    setPostSendBannerActive(true);
    if (postSendBannerTimerRef.current != null) {
      window.clearTimeout(postSendBannerTimerRef.current);
    }
    postSendBannerTimerRef.current = window.setTimeout(() => {
      setPostSendBannerActive(false);
      postSendBannerTimerRef.current = null;
    }, 4500);
  }, [checkoutActive, checkoutFooterPhase, postSendBannerPending, postSendHoldActive]);

  useEffect(() => {
    return () => {
      for (const timer of draftAutosaveTimerRef.current.values()) {
        window.clearTimeout(timer);
      }
      draftAutosaveTimerRef.current.clear();
      draftAutosaveSeqRef.current.clear();
      lastAutosavedTextRef.current.clear();
      if (sendScrollTimerRef.current != null) {
        window.clearTimeout(sendScrollTimerRef.current);
        sendScrollTimerRef.current = null;
      }
      if (postSendGlowTimerRef.current != null) {
        window.clearTimeout(postSendGlowTimerRef.current);
        postSendGlowTimerRef.current = null;
      }
      if (postSendBannerTimerRef.current != null) {
        window.clearTimeout(postSendBannerTimerRef.current);
        postSendBannerTimerRef.current = null;
      }
      if (postSendHoldMinTimerRef.current != null) {
        window.clearTimeout(postSendHoldMinTimerRef.current);
        postSendHoldMinTimerRef.current = null;
      }
      if (postSendHoldMaxTimerRef.current != null) {
        window.clearTimeout(postSendHoldMaxTimerRef.current);
        postSendHoldMaxTimerRef.current = null;
      }
      for (const t of postSendScrollTimersRef.current) window.clearTimeout(t);
      postSendScrollTimersRef.current = [];
      stopAllReplays();
    };
  }, []);

  const footerCollapsed = (checkoutActive && checkoutFooterPhase === "sending") || postSendHoldActive;
  const showSendPill = (checkoutActive && checkoutFooterPhase === "sending") || postSendHoldActive;

  const postcardEditorDraft = postcardEditorDraftId
    ? draftOverrides.get(postcardEditorDraftId) ?? null
    : null;
  const postcardEditorFrontUrl = postcardEditorDraft
    ? getDraftPostcardFrontUrl(postcardEditorDraft.payloadBase)
    : "";

  const existingDraftForComposer = useMemo(() => {
    if (!effectiveExistingDraftsByType) return null;
    return effectiveExistingDraftsByType[composeMode];
  }, [composeMode, effectiveExistingDraftsByType]);

  useEffect(() => {
    if (didAutoSelectComposeModeRef.current) return;
    if (explicitModeFromProps) {
      didAutoSelectComposeModeRef.current = true;
      return;
    }
    if (isEMessageMode) {
      didAutoSelectComposeModeRef.current = true;
      return;
    }
    if (!existingDraftsByType) return;

    const letterDraft = existingDraftsByType.letter;
    const postcardDraft = existingDraftsByType.postcard;
    if (!letterDraft && !postcardDraft) {
      // Wait for the initial thread fetch to complete before finalizing the default mode.
      // `existingDraftsByType` is initialized from runtime state (often empty) before the API response arrives.
      if (emptyState === "loading") return;
      didAutoSelectComposeModeRef.current = true;
      return;
    }

    let nextMode: "letter" | "postcard" = composeMode;
    if (letterDraft && !postcardDraft) nextMode = "letter";
    if (postcardDraft && !letterDraft) nextMode = "postcard";
    if (letterDraft && postcardDraft) {
      nextMode =
        getDraftRecencyTs(postcardDraft.raw) >= getDraftRecencyTs(letterDraft.raw)
          ? "postcard"
          : "letter";
    }

    if (nextMode !== composeMode) setComposeModeRaw(nextMode);
    didAutoSelectComposeModeRef.current = true;
  }, [composeMode, emptyState, existingDraftsByType, explicitModeFromProps, isEMessageMode]);

  return (
    <ThreadPrimitive.Root
      className="aui-root aui-thread-root @container flex h-full flex-col bg-background"
      style={{
        ["--thread-max-width" as string]: "44rem",
      }}
    >
      <CheckoutContext.Provider value={checkoutActive}>
       <CheckoutSendingContext.Provider value={checkoutActive && checkoutFooterPhase === "sending"}>
        <CheckoutDisabledContext.Provider value={checkoutDisabled}>
          <HostNavContext.Provider value={{ penmateId: penmateId || null, onNavigate }}>
            <ComposeModeContext.Provider value={{ composeMode, setComposeMode }}>
              <DraftUiContext.Provider value={draftUi}>
          <div className="relative isolate flex min-h-0 w-full flex-1 bg-background">
            <SendGlow active={sendGlowActive} />
            <ThreadPrimitive.Viewport
              ref={setViewportEl as any}
              className="aui-thread-viewport relative z-10 flex min-h-0 flex-1 flex-col overflow-x-auto overflow-y-scroll scroll-smooth bg-transparent px-4 pt-4"
              onScroll={onViewportScroll}
            >
            <AssistantIf condition={({ thread }) => thread.isEmpty}>
              {emptyState === "loading" ? (
                <ThreadLoading />
              ) : emptyState === "empty" ? (
                <ThreadEmpty />
              ) : null}
            </AssistantIf>

            {/* Push short threads toward the bottom so the composer sits close to the latest message. */}
            <div className="flex-1" />

            <ThreadPrimitive.Messages
              components={{
                UserMessage,
                EditComposer,
                AssistantMessage,
              }}
            />

            {emptyState !== "loading" ? (
              <ThreadPrimitive.ViewportFooter className="aui-thread-viewport-footer sticky bottom-0 mx-auto flex w-full max-w-(--thread-max-width) flex-col gap-4 overflow-visible rounded-t-3xl bg-background pb-4 md:pb-6">
                <ThreadScrollToBottom />
                <div
                  className={cn(
                    "grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none",
                    footerCollapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]",
                  )}
                >
                  <div
                    className={cn(
                      "overflow-hidden transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none",
                      footerCollapsed ? "pointer-events-none opacity-0 scale-[0.98]" : "opacity-100 scale-100",
                    )}
                  >
                    {!checkoutActive && checkoutEntryNotice ? (
                      <div className="mx-auto w-full max-w-(--thread-max-width) px-1">
                        <div className="rounded-xl border border-input bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                          {checkoutEntryNotice}
                        </div>
                      </div>
                    ) : null}
                    {postSendBannerActive ? (
                      <div className="mx-auto mb-2 w-full max-w-(--thread-max-width) px-1">
                        <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 shadow-sm">
                          <CheckCircle2Icon aria-hidden="true" className="h-4 w-4" />
                          <span>Message Sent</span>
                        </div>
                      </div>
                    ) : null}
                    {checkoutActive ? (
                  <CheckoutPinnedCard
                    checkoutLetter={checkoutLetter || null}
                    checkoutLetterLoading={!!checkoutLetterLoading}
                    checkoutUser={checkoutUser || null}
                    checkoutUserLoading={checkoutUserLoading}
                    penmateId={penmateId || null}
                    penmate={penmate}
                    sessionId={sessionId}
                    hasIncomingReplies={hasIncomingReplies}
                    onCancel={() => onCancelCheckout?.()}
                    onSent={handleSent}
                    onSendUiStateChange={handleCheckoutSendUiStateChange}
                  />
                ) : null}
                    <Composer
                      composerInputRef={composerInputRef}
                      draftSaveError={draftSaveError}
                      isThreadLoading={draftsLoading}
                      existingDraft={existingDraftForComposer}
                      existingDraftsByType={effectiveExistingDraftsByType || null}
                      penmateId={penmateId || null}
                      penmate={penmate}
                      sessionId={sessionId}
                      messageType={messageType}
                      checkoutDraftId={checkoutDraftId || null}
                      checkoutDisabled={checkoutDisabled}
                      onEnterCheckout={onEnterCheckout}
                    />
                  </div>
                </div>
                {showSendPill && !prefersReducedMotion ? (
                  <div className="mx-auto flex items-center gap-2 rounded-full border border-input bg-white/70 px-3 py-1 text-xs text-muted-foreground shadow-sm backdrop-blur">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-[#4b46a4a3]" />
                    Sending…
                  </div>
                ) : null}
              </ThreadPrimitive.ViewportFooter>
            ) : null}
            </ThreadPrimitive.Viewport>
          </div>

          <PostcardEditorModal
            open={!!postcardEditorDraftId}
            onOpenChange={(next) => {
              if (!next) closePostcardEditor();
            }}
            draftId={postcardEditorDraftId}
            frontUrl={postcardEditorFrontUrl}
            onSelectFrontUrl={(draftId, url) => setDraftPostcardFrontUrl(draftId, url)}
          />

          <Dialog
            open={deleteConfirmOpen}
            onOpenChange={(open) => {
              setDeleteConfirmOpen(open);
              if (!open) {
                setDeleteTargetId(null);
                setDeleteTargetIsResend(false);
                setDeleteError(null);
              }
            }}
            modal={false}
          >
            <DialogContent className="w-[calc(100vw-2rem)] max-w-sm !p-4">
              <DialogTitle className="font-semibold text-base">Delete this draft?</DialogTitle>
              <DialogDescription className="sr-only">Confirm deleting the draft.</DialogDescription>
              <div className="mt-2 text-muted-foreground text-sm">This can’t be undone.</div>
              {deleteError ? (
                <div className="mt-2 text-destructive text-xs">{deleteError}</div>
              ) : null}
              <div className="mt-4 flex justify-end gap-2">
                <DialogClose asChild>
                  <Button type="button" variant="ghost" disabled={isDeleting}>
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => void deleteDraftNow()}
                  disabled={isDeleting}
                >
                  Delete
                </Button>
              </div>
            </DialogContent>
          </Dialog>
              </DraftUiContext.Provider>
            </ComposeModeContext.Provider>
          </HostNavContext.Provider>
        </CheckoutDisabledContext.Provider>
       </CheckoutSendingContext.Provider>
      </CheckoutContext.Provider>
    </ThreadPrimitive.Root>
  );
};

const CheckoutPinnedCard: FC<{
  checkoutLetter: RawMessage | null;
  checkoutLetterLoading: boolean;
  checkoutUser: PmUser | null;
  checkoutUserLoading: boolean;
  penmateId?: string | null;
  penmate?: any;
  sessionId?: number;
  hasIncomingReplies: boolean;
  onCancel: () => void;
  onSent?: (messageId: string) => void;
  onSendUiStateChange?: (args: { phase: "idle" | "sending" }) => void;
}> = ({
  checkoutLetter,
  checkoutLetterLoading,
  checkoutUser,
  checkoutUserLoading,
  penmateId,
  penmate,
  sessionId,
  hasIncomingReplies,
  onCancel,
  onSent,
  onSendUiStateChange,
}) => {
  const draftUi = useDraftUi();
  const photoViewer = usePhotoViewer();
  const checkoutIsResend = getIsResend(checkoutLetter);
  const [checklistParent] = useAutoAnimate<HTMLDivElement>();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [phoneVerifyOpen, setPhoneVerifyOpen] = useState(false);
  const [deliverySheetOpen, setDeliverySheetOpen] = useState(false);
  const [deliverySheetStep, setDeliverySheetStep] = useState<"delivery" | "facility">("delivery");
  const [returnAddressSheetOpen, setReturnAddressSheetOpen] = useState(false);
  const [photoTypeSheetOpen, setPhotoTypeSheetOpen] = useState(false);
  const [creditsSheetOpen, setCreditsSheetOpen] = useState(false);
  const [paymentSheetOpen, setPaymentSheetOpen] = useState(false);
  const [recoveryOffer, setRecoveryOffer] = useState<RecoveryOffer | null>(null);
  const [recoveryTrigger, setRecoveryTrigger] = useState<RecoveryTrigger | null>(null);
  const [recoveryMode, setRecoveryMode] = useState<"offer" | "rescue">("offer");
  // Regime D: the rescue on its own, with no recovery offer to carry it (the
  // copy comes from the letter read). Exclusive with `recoveryOffer`.
  const [recoveryRescueOnly, setRecoveryRescueOnly] = useState<RescueCopy | null>(null);
  const recoverySheetOpen = !!recoveryOffer || !!recoveryRescueOnly;
  // One line under the total after a rescue the server refused for good.
  const [recoveryNotice, setRecoveryNotice] = useState<string | null>(null);
  const [cancelPending, setCancelPending] = useState(false);
  const [recoveryReminderPending, setRecoveryReminderPending] = useState(false);
  const recoveryRequestInFlightRef = useRef(false);
  // Automatic presentations (an abandon trigger, arrival via the return
  // surface) happen at most once per checkout visit; within the visit only
  // the reminder line's tap asks again. A new checkout entry starts over.
  // The rescue rung has its own once-per-visit gate; a shown rescue quiets
  // both rungs for the rest of the visit.
  const recoveryShownThisVisitRef = useRef(false);
  const recoveryRescueShownThisVisitRef = useRef(false);
  // The letter the presented offer belongs to (same id the ladder counted).
  const recoveryPresentedLetterIdRef = useRef("");
  // One host-leave interception per checkout: the first back/leave asks,
  // later ones proceed.
  const recoveryHostLeaveAskedRef = useRef(false);
  const recoveryReturnHandledRef = useRef(false);
  // The reminder line's impression event: once per checkout visit.
  const recoveryReminderDisplayedRef = useRef(false);
  // What "Not now" completes: the cancel exit or the host navigation the
  // sheet put on hold (null when the customer simply stays in checkout).
  const recoveryExitAfterCloseRef = useRef<(() => void) | null>(null);
  const cardMountedRef = useRef(true);
  const isSendingRef = useRef(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [resendCreditsError, setResendCreditsError] = useState<string | null>(null);
  const [returnAddressSendError, setReturnAddressSendError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [resendCreditsClearing, setResendCreditsClearing] = useState(false);
  const queryClient = useQueryClient();
  const sendLetterConversionTrackedRef = useRef<Set<string>>(new Set());
  const stashedPhotosRef = useRef<any[] | null>(null);
  const deliveryOverflowPhotosRef = useRef<any[] | null>(null);
  const returnAddressErrorSigRef = useRef<string | null>(null);
  const resendCreditsClearPromiseRef = useRef<Promise<boolean> | null>(null);
  const [dpOffersByBundle, setDpOffersByBundle] = useState<Record<string, DynamicPricingOffer> | null>(null);
  const [dpOffersLoadedContextKey, setDpOffersLoadedContextKey] = useState<string | null>(null);
  const dpOfferSyncingRef = useRef(false);
  const dpOfferSyncSkippedRef = useRef(false);
  const [dpOfferSyncTick, setDpOfferSyncTick] = useState(0);
  const checkoutStartedTrackedLetterIdRef = useRef<string | null>(null);
  const confirmOrderTrackedLetterIdRef = useRef<string | null>(null);
  const paymentResumeRanRef = useRef(false);
  const [paymentResumeError, setPaymentResumeError] = useState<string | null>(null);
  const withPenmatePayload = useCallback(
    (payload: Record<string, unknown>): Record<string, unknown> =>
      attachPenmatePayload(payload, penmate, penmateId),
    [penmate, penmateId],
  );

  const toLowerString = (value: unknown): string => {
    return typeof value === "string" ? value.trim().toLowerCase() : "";
  };

  const toBool = (value: unknown): boolean => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    if (typeof value === "string") {
      const v = value.trim().toLowerCase();
      return v === "true" || v === "1" || v === "yes";
    }
    return false;
  };

  const getCouponCode = (letter: RawMessage | null): string => {
    const coupon = (letter as any)?.coupon ?? (letter as any)?.data?.coupon;
    const code = coupon && typeof coupon === "object" ? (coupon as any).code : undefined;
    return toLowerString(code);
  };

  const isFreeLetter = (letter: RawMessage | null): boolean => {
    const meta = (letter as any)?.meta ?? (letter as any)?.data?.meta ?? {};
    return (
      toBool(meta?.freeFirstLetter ?? meta?.free_first_letter) ||
      toBool(meta?.winBackLetter ?? meta?.win_back_letter)
    );
  };

  const getSelectedPhotosCount = (letter: RawMessage | null): number => {
    const v = (letter as any)?.selectedPhotos ?? (letter as any)?.data?.selectedPhotos ?? (letter as any)?.photos;
    return Array.isArray(v) ? v.length : 0;
  };

  const getNoPhotosSelected = (letter: RawMessage | null): boolean => {
    const v =
      (letter as any)?.noPhotosSelected ??
      (letter as any)?.no_photos_selected ??
      (letter as any)?.data?.noPhotosSelected ??
      (letter as any)?.data?.no_photos_selected;
    return toBool(v);
  };

  const canUseFirstMessageSendlove = (args: { letter: RawMessage | null; user: PmUser | null }): boolean => {
    const { letter, user } = args;
    if (!letter) return false;
    // Hard exclusions stay client-side whatever the server flag says: the
    // option is a print-letter construct.
    const messageType = toLowerString((letter as any)?.messageType ?? (letter as any)?.data?.messageType ?? (letter as any)?.order_type);
    if (messageType === "postcard") return false;
    const shippingMethod = toLowerString((letter as any)?.shippingMethod ?? (letter as any)?.data?.shippingMethod);
    if (shippingMethod === "emessage") return false;
    if (getIsResend(letter)) return false;
    // The server's word wins when present: whether a zero-photo version of
    // this letter would actually be FREE. Regime D arm B prices the first
    // letter, so the free-path option must not show for it; a granted
    // rescue flips the flag back on. Absent flag = older API = the
    // pre-regime world, where the local predicate below is correct.
    const meta = (letter as any)?.meta ?? (letter as any)?.data?.meta;
    const flag = meta?.freeFirstAvailableIfNoPhotos ?? meta?.free_first_available_if_no_photos;
    if (flag != null) return toBool(flag);
    if (!user || user.hasSentLetter !== false) return false;
    return true;
  };

  const selectedPhotosCount = useMemo(
    () => getSelectedPhotosCount(checkoutLetter),
    [checkoutLetter],
  );
  const stashedPhotosCount = stashedPhotosRef.current?.length ?? 0;
  const noPhotosEligible = canUseFirstMessageSendlove({ letter: checkoutLetter, user: checkoutUser });
  const noPhotosSelectedStored = getNoPhotosSelected(checkoutLetter);
  const noPhotosSelected =
    noPhotosEligible &&
    selectedPhotosCount === 0 &&
    (noPhotosSelectedStored || stashedPhotosCount > 0);

  const requirements = useMemo(
    () => getCheckoutRequirements({
      letter: checkoutLetter,
      user: checkoutUser,
      offersByBundle: dpOffersByBundle,
      noPhotosSelected,
    }),
    [checkoutLetter, checkoutUser, dpOffersByBundle, noPhotosSelected],
  );
  const hideSingleCreditOption = checkoutUser?.hasPaidInvoice === true;

  const checklistIsLoading = checkoutLetterLoading || checkoutUserLoading || !checkoutLetter || !checkoutUser;
  const checklistSkeletonCount = 5;

  const preview = useMemo(() => {
    if (!checkoutLetter) return null;
    const id = (checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? null;
    const draftId = id != null ? String(id) : null;
    const snapshot = draftId ? draftUi.getDraftOverrideById(draftId) : null;

    const messageType = toLowerString(
      (checkoutLetter as any)?.messageType ??
        (checkoutLetter as any)?.data?.messageType ??
        (checkoutLetter as any)?.order_type,
    );
    const kind = messageType === "postcard" ? "postcard" : "letter";

    const text = clampDraftText({
      messageType: kind,
      text: snapshot?.text ?? extractDraftMessageText(checkoutLetter),
    });

    const selectedPhotosPayload =
      snapshot?.selectedPhotosPayload ??
      ((checkoutLetter as any)?.selectedPhotos ??
        (checkoutLetter as any)?.data?.selectedPhotos ??
        (checkoutLetter as any)?.photos ??
        []);
    const photos = kind === "postcard" ? [] : toMediaItemsFromPayload(Array.isArray(selectedPhotosPayload) ? selectedPhotosPayload : []);

    const payloadBase = snapshot?.payloadBase ?? ((checkoutLetter as any)?.data && typeof (checkoutLetter as any)?.data === "object"
      ? (checkoutLetter as any).data
      : (checkoutLetter as any));
    const postcardFrontUrl =
      kind === "postcard" ? getDraftPostcardFrontUrl(payloadBase as any) : "";

    return { draftId, kind, text, photos, postcardFrontUrl };
  }, [checkoutLetter, draftUi, toLowerString]);

  const returnAddressSignature = useMemo(() => {
    const ra = (checkoutLetter as any)?.returnAddress ?? (checkoutLetter as any)?.data?.returnAddress ?? null;
    if (!ra || typeof ra !== "object") return "";
    const key = {
      name: (ra as any)?.name ?? null,
      firstName: (ra as any)?.firstName ?? (ra as any)?.first_name ?? null,
      lastName: (ra as any)?.lastName ?? (ra as any)?.last_name ?? null,
      address_line1: (ra as any)?.address_line1 ?? (ra as any)?.line1 ?? null,
      address_line2: (ra as any)?.address_line2 ?? (ra as any)?.line2 ?? null,
      address_city: (ra as any)?.address_city ?? (ra as any)?.city ?? null,
      address_state: (ra as any)?.address_state ?? (ra as any)?.state ?? null,
      address_zip: (ra as any)?.address_zip ?? (ra as any)?.postal_code ?? null,
      address_country: (ra as any)?.address_country ?? (ra as any)?.country ?? null,
    };
    try {
      return JSON.stringify(key);
    } catch {
      return "";
    }
  }, [checkoutLetter]);

  useEffect(() => {
    setPaymentResumeError(null);
    setSendError(null);
    setResendCreditsError(null);
    setReturnAddressSendError(null);
    setResendCreditsClearing(false);
    resendCreditsClearPromiseRef.current = null;
    stashedPhotosRef.current = null;
    returnAddressErrorSigRef.current = null;
  }, [(checkoutLetter as any)?.id]);

  useEffect(() => {
    if (!returnAddressSendError) return;
    const lastSig = returnAddressErrorSigRef.current;
    if (!lastSig) return;
    if (returnAddressSignature && returnAddressSignature !== lastSig) {
      setReturnAddressSendError(null);
      returnAddressErrorSigRef.current = null;
    }
  }, [returnAddressSendError, returnAddressSignature]);

  const dpOfferContextKey = useMemo(() => offerContextKey(checkoutLetter), [checkoutLetter]);

  const dpHasOffers = useMemo(() => {
    if (!dpOffersByBundle) return false;
    for (const key of Object.keys(dpOffersByBundle)) {
      const offer = (dpOffersByBundle as any)[key];
      if (offer) return true;
    }
    return false;
  }, [dpOffersByBundle]);

  const dpCreditPurchaseRequired = useMemo(() => {
    if (!checkoutLetter || !checkoutUser) return false;
    if (checkoutIsResend) return false;
    if (isFreeLetter(checkoutLetter)) return false;
    const meta = (checkoutLetter as any)?.meta ?? (checkoutLetter as any)?.data?.meta ?? {};
    const toInt = (v: unknown): number => {
      if (typeof v === "number" && Number.isFinite(v)) return Math.max(0, Math.round(v));
      if (typeof v === "string" && v.trim()) {
        const n = Number.parseInt(v, 10);
        return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
      }
      return 0;
    };
    const toBool = (v: unknown): boolean => {
      if (typeof v === "boolean") return v;
      if (typeof v === "number") return v !== 0;
      if (typeof v === "string") {
        const s = v.trim().toLowerCase();
        return s === "true" || s === "1" || s === "yes";
      }
      return false;
    };

    const messageType = toLowerString(
      (checkoutLetter as any)?.messageType ?? (checkoutLetter as any)?.data?.messageType ?? (checkoutLetter as any)?.order_type,
    );
    const isPostcard = messageType === "postcard";
    const firstMessage =
      meta?.firstMessage != null || meta?.first_message != null ? toBool(meta?.firstMessage ?? meta?.first_message) : false;
    const isNewUser = checkoutUser.hasSentLetter === false;
    // Legacy parity: first-time postcard checkout uses cash pricing; do not fetch credit-bundle offers.
    if (isPostcard && isNewUser && firstMessage && !checkoutIsResend) return false;

    const messageCreditsDue = toInt(meta?.messageCreditCost ?? meta?.message_credit_cost);
    const letterCreditsDue = toInt(meta?.letterCreditsDue ?? meta?.letter_credits_due);
    const letterCreditsAvailable = toInt(meta?.letterCreditAmt ?? meta?.letter_credit_amt);
    const messageCreditsAvailable = toInt(
      (checkoutUser as any)?.messageCreditBalance ?? (checkoutUser as any)?.message_credit_balance,
    );

    // Legacy parity (confirm-order.ts offer gating): fetch offers only when a purchase is actually required.
    return letterCreditsDue > letterCreditsAvailable || messageCreditsDue > messageCreditsAvailable;
  }, [checkoutIsResend, checkoutLetter, checkoutUser]);

  const dpOffersSuppressed = !!(
    checkoutUser &&
    checkoutUser.hasSentLetter === false &&
    checkoutLetter &&
    !!getCouponCode(checkoutLetter)
  );

  const penmateMetrics = useMemo(
    () => buildPenmateMetricsPayload({ penmate, letter: checkoutLetter }),
    [checkoutLetter, penmate],
  );
  const REPLAY_STOP_DELAY_MS = 15_000;

  const isDemoUser = useMemo(() => {
    const user = checkoutUser as any;
    const metrics =
      user?.deviceInfo?.metrics ??
      user?.device_info?.metrics ??
      user?.raw?.deviceInfo?.metrics ??
      user?.raw?.device_info?.metrics;
    return metrics === false;
  }, [checkoutUser]);

  useEffect(() => {
    cancelScheduledStopCheckoutReplay();
    if (checkoutLetterLoading || checkoutUserLoading) return;
    if (!checkoutLetter || !checkoutUser) return;

    if (isDemoUser) {
      stopAllReplays();
      return;
    }

    if (!requirements.paymentRequired) return;

    const letterId =
      (checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? null;
    const messageType =
      (checkoutLetter as any)?.messageType ??
      (checkoutLetter as any)?.data?.messageType ??
      (checkoutLetter as any)?.order_type;
    const shippingMethod =
      (checkoutLetter as any)?.shippingMethod ??
      (checkoutLetter as any)?.data?.shippingMethod;

    maybeStartCheckoutReplay(checkoutUser, {
      letterId,
      messageType,
      shippingMethod,
      paymentRequired: true,
    });
  }, [
    checkoutLetter,
    checkoutLetterLoading,
    checkoutUser,
    checkoutUserLoading,
    isDemoUser,
    requirements.paymentRequired,
  ]);

  // Saved-method preattach (slice 2.1, D-010): one evaluation per letter at
  // checkout entry. Eligible -> the server's default method is persisted to
  // the draft and the Payment row completes on its own; every other path
  // leaves checkout exactly as it was.
  const preattachHandledRef = useRef<Set<string>>(new Set());
  const latestCheckoutLetterRef = useRef<any>(null);
  const paymentSheetOpenRef = useRef(false);
  useEffect(() => {
    latestCheckoutLetterRef.current = checkoutLetter;
  }, [checkoutLetter]);
  useEffect(() => {
    paymentSheetOpenRef.current = paymentSheetOpen;
  }, [paymentSheetOpen]);
  useEffect(() => {
    isSendingRef.current = isSending;
  }, [isSending]);
  useEffect(() => {
    cardMountedRef.current = true;
    return () => {
      cardMountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    if (checkoutLetterLoading || checkoutUserLoading) return;
    if (!checkoutLetter || !checkoutUser) return;
    const letterId = (checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? null;
    const letterKey = letterId == null ? "" : String(letterId);
    if (!letterKey || preattachHandledRef.current.has(letterKey)) return;
    preattachHandledRef.current.add(letterKey);
    if (hasPaymentReturnParams()) {
      // A Stripe redirect resume is attaching for this letter; stay out of
      // its way. The resumed setup lands as the draft payment on its own.
      void trackMetric(
        "preattach_eligible",
        { letter_id: letterId, eligible: false, reason: "payment_return" },
        { sessionId },
      );
      return;
    }
    const hasPayment = !!((checkoutLetter as any)?.payment ?? (checkoutLetter as any)?.data?.payment);
    const decision = evaluatePreattach({
      user: checkoutUser,
      paymentRequired: requirements.paymentRequired,
      hasPayment,
      isDemo: isDemoUser,
    });
    void trackMetric(
      "preattach_eligible",
      { letter_id: letterId, eligible: decision.eligible, reason: decision.reason },
      { sessionId },
    );
    if (!decision.eligible) return;
    void (async () => {
      const method = await fetchPreattachMethod();
      if (!method) return;
      try {
        const attached = await savePreattachedPayment(method);
        if (attached) {
          void trackMetric(
            "preattach_attached",
            { letter_id: letterId, brand: method.brand ?? null },
            { sessionId },
          );
        }
      } catch {
        // Draft save failed: the ordinary setup path remains available.
      }
    })();
  }, [
    checkoutLetter,
    checkoutLetterLoading,
    checkoutUser,
    checkoutUserLoading,
    isDemoUser,
    requirements.paymentRequired,
  ]);

  useEffect(() => {
    return () => {
      scheduleStopCheckoutReplay(REPLAY_STOP_DELAY_MS);
    };
  }, []);

  const trackDraftCreated = useCallback(
    (args: { saved: RawMessage; messageType?: string; shippingMethod?: string }) => {
      const { saved, messageType, shippingMethod } = args;
      const letterIdValue = (saved as any)?.id ?? (saved as any)?.data?.id ?? null;
      if (letterIdValue == null) return;
      const payload = {
        letter_id: letterIdValue,
        ...buildPenmateMetricsPayload({ penmate, letter: saved, penmateId }),
        message_type:
          (saved as any)?.messageType ??
          (saved as any)?.data?.messageType ??
          messageType ??
          null,
        shipping_method:
          (saved as any)?.shippingMethod ??
          (saved as any)?.data?.shippingMethod ??
          shippingMethod ??
          null,
      };
      void trackMetric("draft_created", payload, { sessionId });
      trackReplayStageFromMetricPayload("message_created", "draft_created", payload);
    },
    [penmate, penmateId, sessionId],
  );

  const buildCheckoutMetricsPayload = useCallback((letter: RawMessage): Record<string, unknown> | null => {
    const letterIdValue = (letter as any)?.id ?? (letter as any)?.data?.id ?? null;
    const letterId = letterIdValue != null ? String(letterIdValue) : "";
    if (!letterId) return null;

    const toNumber = (v: unknown): number => {
      if (typeof v === "number" && Number.isFinite(v)) return v;
      if (typeof v === "string" && v.trim()) {
        const n = Number.parseFloat(v);
        return Number.isFinite(n) ? n : 0;
      }
      return 0;
    };
    const toInt = (v: unknown): number => Math.max(0, Math.round(toNumber(v)));
    const promoMeta = (letter as any)?.meta ?? (letter as any)?.data?.meta ?? {};
    const freeFirstLetter = toBool(promoMeta?.freeFirstLetter ?? promoMeta?.free_first_letter);
    const winBackLetter = toBool(promoMeta?.winBackLetter ?? promoMeta?.win_back_letter);
    const promoFreeCredits = toInt(
      freeFirstLetter || winBackLetter
        ? 0
        : (promoMeta?.firstMessagePromoFreeCredits ?? promoMeta?.first_message_promo_free_credits),
    );

    return {
      letter_id: letterIdValue ?? letterId,
      ...penmateMetrics,
      message_type: (letter as any)?.messageType ?? (letter as any)?.data?.messageType ?? (letter as any)?.order_type,
      shipping_method: (letter as any)?.shippingMethod ?? (letter as any)?.data?.shippingMethod,
      payment_required: !!requirements.paymentRequired,
      credit_option: (letter as any)?.creditOption ?? (letter as any)?.data?.creditOption ?? null,
      price: Number((letter as any)?.price) || 0,
      price_after_coupon: Number((letter as any)?.priceAfterCoupon) || 0,
      offer_redemption_id:
        (letter as any)?.offerRedemptionId ??
        (letter as any)?.offer_redemption_id ??
        (letter as any)?.data?.offerRedemptionId ??
        null,
      dp_has_offers: dpHasOffers,
      first_message_promo_free_credits: promoFreeCredits > 0 ? promoFreeCredits : null,
    };
  }, [dpHasOffers, penmateMetrics, requirements.paymentRequired]);

  const submitErrorMetric = useCallback(
    (basePayload: Record<string, unknown>, category: "payment" | "validation" | "send" | "exception") => ({
      message_type: (basePayload as any)?.message_type ?? null,
      shipping_method: (basePayload as any)?.shipping_method ?? null,
      error_category: category,
    }),
    [],
  );

  const trackSubmitLetterError = useCallback(
    (basePayload: Record<string, unknown>, category: ReplaySubmitErrorCategory) => {
      void trackMetric(
        "submit-letter-error",
        submitErrorMetric(basePayload, category),
        { sessionId },
      );
      trackReplaySubmitError(basePayload, category);
    },
    [sessionId, submitErrorMetric],
  );

  const trackSubmitLetterSuccess = useCallback(
    (args: { letter: RawMessage | null; basePayload: Record<string, unknown>; isResend?: boolean }) => {
      const { letter, basePayload } = args;
      const letterIdValue =
        (letter as any)?.id ??
        (letter as any)?.data?.id ??
        (basePayload as any)?.letter_id ??
        null;
      const letterId = letterIdValue != null ? String(letterIdValue) : "";
      const messageType =
        (letter as any)?.messageType ??
        (letter as any)?.data?.messageType ??
        (basePayload as any)?.message_type ??
        null;
      const shippingMethod =
        (letter as any)?.shippingMethod ??
        (letter as any)?.data?.shippingMethod ??
        (basePayload as any)?.shipping_method ??
        null;
      const priceAfterCoupon = Number(
        (letter as any)?.priceAfterCoupon ?? (basePayload as any)?.price_after_coupon ?? 0,
      );
      const price = Number((letter as any)?.price ?? (basePayload as any)?.price ?? 0);
      const emessageFlag =
        (letter as any)?.emessage ??
        (letter as any)?.eMessage ??
        (letter as any)?.data?.emessage ??
        (letter as any)?.data?.eMessage ??
        null;
      const emessage =
        typeof emessageFlag === "boolean"
          ? emessageFlag
          : typeof shippingMethod === "string" && shippingMethod.toLowerCase() === "emessage";
      const purchased = !!(basePayload as any)?.payment_required;
      const isResend = !!args.isResend;

      const successPayload = {
        ...basePayload,
        purchased,
        value: Number.isFinite(priceAfterCoupon) ? priceAfterCoupon : 0,
        emessage,
      };
      void trackMetric("submit-letter-success", successPayload, { sessionId });
      trackReplayStageFromMetricPayload("message_sent", "submit-letter-success", successPayload);

      if (!isResend && letterId && purchased) {
        const normalizedPriceCents = Number.isFinite(priceAfterCoupon) ? priceAfterCoupon : price;
        const conversionValue =
          Number.isFinite(normalizedPriceCents) ? Math.max(0, normalizedPriceCents) / 100 : 0;
        if (conversionValue > 0 && !sendLetterConversionTrackedRef.current.has(letterId)) {
          sendLetterConversionTrackedRef.current.add(letterId);
          pushDataLayerEvent(GTM_SEND_LETTER_EVENT, {
            transaction_id: letterId,
            value: conversionValue,
            currency: "USD",
            message_type: messageType,
            shipping_method: shippingMethod,
            emessage,
          });
        }
      }

      if (purchased) {
        const paidPayload = {
          order_id: letterIdValue,
          letter_id: letterIdValue,
          message_type: messageType,
          "$price": Number.isFinite(priceAfterCoupon) ? priceAfterCoupon : 0,
          price: Number.isFinite(priceAfterCoupon) ? priceAfterCoupon : 0,
          shipping_method: shippingMethod,
          credit_option: (basePayload as any)?.credit_option ?? null,
          offer_redemption_id: (basePayload as any)?.offer_redemption_id ?? null,
          dp_has_offers: (basePayload as any)?.dp_has_offers ?? null,
          payment_required: (basePayload as any)?.payment_required ?? null,
        };
        void trackMetric("paid-order", paidPayload, { sessionId });
        trackReplayStageFromMetricPayload("paid_signal", "paid-order", paidPayload);
        stopAllReplays();
      }

      if (emessage) {
        const normalizedPrice = Number.isFinite(price) ? price : 0;
        void trackMetric(
          "emessage-send",
          {
            order_id: letterIdValue,
            price: normalizedPrice,
            value: normalizedPrice,
          },
          { sessionId },
        );
      }
    },
    [sessionId],
  );

  useEffect(() => {
    if (checkoutLetterLoading || checkoutUserLoading) return;
    if (!checkoutLetter || !checkoutUser) return;
    const letterIdValue = (checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? null;
    const letterId = letterIdValue != null ? String(letterIdValue) : "";
    if (!letterId) return;
    if (checkoutStartedTrackedLetterIdRef.current === letterId) return;
    checkoutStartedTrackedLetterIdRef.current = letterId;

    const payload = buildCheckoutMetricsPayload(checkoutLetter);
    if (!payload) return;
    void trackMetric("checkout_started", payload, { sessionId });
    trackReplayStageFromMetricPayload("checkout_viewed", "checkout_started", payload);
  }, [
    buildCheckoutMetricsPayload,
    checkoutLetter,
    checkoutLetterLoading,
    checkoutUser,
    checkoutUserLoading,
    sessionId,
  ]);

  useEffect(() => {
    if (checkoutLetterLoading || checkoutUserLoading) return;
    if (!checkoutLetter || !checkoutUser) return;
    const letterId = (checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? null;
    if (!letterId) return;
    const key = String(letterId);
    if (confirmOrderTrackedLetterIdRef.current === key) return;
    confirmOrderTrackedLetterIdRef.current = key;

    const payload = buildCheckoutMetricsPayload(checkoutLetter);
    if (!payload) return;
    void trackMetric("confirm-order", payload, { sessionId });
  }, [
    buildCheckoutMetricsPayload,
    checkoutLetter,
    checkoutLetterLoading,
    checkoutUser,
    checkoutUserLoading,
    dpHasOffers,
    penmateMetrics,
    sessionId,
    requirements.paymentRequired,
  ]);

  useEffect(() => {
    const letterId = (checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? null;
    if (!letterId) {
      setDpOffersByBundle(null);
      setDpOffersLoadedContextKey(null);
      return;
    }

    if (!dpCreditPurchaseRequired || dpOffersSuppressed) {
      setDpOffersByBundle(null);
      setDpOffersLoadedContextKey(null);
      return;
    }

    // Non-null offers required: a context flip clears the map below, so a
    // fast reverse flip must refetch rather than early-return on the stale
    // loaded key with nothing to render.
    if (dpOffersLoadedContextKey === dpOfferContextKey && dpOffersByBundle) return;

    // Context changed (facility/shipping/coupon): stop rendering the prior
    // context's offers while the refetch is in flight.
    if (dpOffersLoadedContextKey !== null && dpOffersByBundle) setDpOffersByBundle(null);

    let cancelled = false;
    void (async () => {
      try {
        const resp = await fetchLetterOffers({ letterId: String(letterId) });
        if (cancelled) return;
        const mapped = mapOffersByBundle(resp);
        setDpOffersByBundle(mapped.offersByBundle);
        setDpOffersLoadedContextKey(dpOfferContextKey);
      } catch {
        if (cancelled) return;
        // Legacy parity: network failures should not clear any previously-selected redemption id.
        setDpOffersByBundle(null);
        setDpOffersLoadedContextKey(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [checkoutLetter, dpCreditPurchaseRequired, dpOffersSuppressed, dpOfferContextKey, dpOffersLoadedContextKey, dpOffersByBundle]);

  const dpSyncCreditOption =
    (checkoutLetter as any)?.creditOption ?? (checkoutLetter as any)?.data?.creditOption ?? null;

  useEffect(() => {
    if (!checkoutLetter) return;
    if (!dpOffersByBundle) return;
    if (dpOfferSyncingRef.current) {
      // A save holds the lock; remember to re-evaluate once it releases.
      dpOfferSyncSkippedRef.current = true;
      return;
    }
    if (checkoutIsResend) return;

    const creditOption = dpSyncCreditOption ? String(dpSyncCreditOption) : null;
    if (!creditOption) return;

    const desired = dpOffersByBundle[String(creditOption)]?.redemption_id ?? null;
    const current = getDraftOfferRedemptionId();
    if (current === desired) return;

    dpOfferSyncingRef.current = true;
    void (async () => {
      try {
        await saveCredits({ creditOption, offerRedemptionId: desired });
      } finally {
        dpOfferSyncingRef.current = false;
        if (dpOfferSyncSkippedRef.current) {
          dpOfferSyncSkippedRef.current = false;
          // A newer context was blocked by the lock; re-evaluate it now,
          // success or failure - each context still gets one attempt.
          setDpOfferSyncTick((tick) => tick + 1);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutIsResend, dpOffersByBundle, (checkoutLetter as any)?.id, dpSyncCreditOption, dpOfferSyncTick]);

  const photoTypeEditable = selectedPhotosCount > 0 || stashedPhotosCount > 0;

  const meta = (checkoutLetter as any)?.meta ?? (checkoutLetter as any)?.data?.meta ?? {};
  const freeFirstLetter = toBool(meta?.freeFirstLetter ?? meta?.free_first_letter);
  const winBackLetter = toBool(meta?.winBackLetter ?? meta?.win_back_letter);
  const processingFeeCents = Math.max(
    0,
    Number.isFinite(Number(meta?.processingFee)) ? Number(meta.processingFee) : 0,
  );
  const couponCode = getCouponCode(checkoutLetter);
  const baseSubtotalCents =
    couponCode
      ? Number((checkoutLetter as any)?.priceAfterCoupon) || 0
      : Number((checkoutLetter as any)?.price) || 0;
  const subtotalCents = Math.max(0, baseSubtotalCents);
  // Legacy confirm-order only applies processing fees when there's a payable total.
  const payable = !freeFirstLetter && !winBackLetter && subtotalCents > 0;
  const totalCents = payable ? subtotalCents + processingFeeCents : 0;

  const openDeliverySheet = (step: "delivery" | "facility") => {
    setDeliverySheetStep(step);
    setDeliverySheetOpen(true);
  };

  const openReturnAddressSheet = () => setReturnAddressSheetOpen(true);
  const openPhotoTypeSheet = () => {
    if (checkoutIsResend) return;
    setPhotoTypeSheetOpen(true);
  };
  const openCreditsSheet = () => {
    if (checkoutIsResend) return;
    setCreditsSheetOpen(true);
  };
  const openPaymentSheet = () => {
    if (paymentSheetOpen) return;
    // Synchronous ref write: the preattach save's bail must see the sheet
    // opening in the same frame, not a commit later.
    paymentSheetOpenRef.current = true;
    setPaymentSheetOpen(true);

    if (!checkoutLetter) return;
    const payload = buildCheckoutMetricsPayload(checkoutLetter);
    if (!payload) return;
    void trackMetric("add-payment", payload, { sessionId });
  };

  const getStripeKey = (): string => {
    const w = window as any;
    const key = w?.Penmate?.env?.stripeKey;
    if (typeof key !== "string" || !key.trim()) throw new Error("Penmate.env.stripeKey is not available");
    return key;
  };

  const getSaleObject = (letter: RawMessage | null): any => {
    const v = (letter as any)?.sale ?? (letter as any)?.data?.sale ?? {};
    return v && typeof v === "object" ? v : {};
  };

  const extractPaymentError = (letter: RawMessage | null): string => {
    const sale = getSaleObject(letter);
    const saleErr = sale && typeof sale === "object" ? (sale as any).error : undefined;
    const paymentErr = (letter as any)?.payment_error ?? (letter as any)?.data?.payment_error;
    const raw = typeof saleErr === "string" && saleErr.trim() ? saleErr.trim() : typeof paymentErr === "string" ? paymentErr.trim() : "";
    if (!raw) return "";
    const generic =
      "There was an error with your payment method. Please try again or use a different payment card.";
    return /paymentintent|payment_intent/i.test(raw) ? generic : raw;
  };

  const clearCheckoutUrlParams = () => {
    try {
      const url = new URL(window.location.href);
      for (const k of ["session_id", "messageId", "letterId", "draftId"]) url.searchParams.delete(k);
      window.history.replaceState({}, "", url.toString());
    } catch {
      // ignore
    }
  };

  const syncDraftOverrideAfterCheckoutSave = useCallback(
    (args: { draftId: string; next: Record<string, unknown>; saved?: any }) => {
      const { draftId, next, saved } = args;
      if (!draftId) return;
      const savedData = saved?.data;
      const shouldUseSaved =
        !!savedData && typeof savedData === "object" && Object.keys(savedData as Record<string, unknown>).length > 0;
      const raw = shouldUseSaved ? saved : { id: draftId, data: next };
      draftUi.syncDraftOverrideFromRaw(draftId, raw, { preserveText: true });
    },
    [draftUi],
  );

  // Pre-send belt, made WRITE-SAFE: POST /drafts REPLACES the data blob
  // server-side, so this must never send this client's possibly-stale
  // snapshot — another tab, another device, or a server-side change this
  // client has not re-read would be silently reverted right before pricing.
  // Re-read the letter and save THAT back. One affirmative fix rides along:
  // a legacy draft with no delivery method at all has been presented (and
  // priced) as Print & Mail the whole way, so persist what was presented
  // before the money moves.
  // What the customer is being charged FOR. If the server's copy of the
  // draft disagrees with what this card has been showing on any of these,
  // the send must not proceed on either version silently.
  const pricingSnapshot = (letter: any): string => {
    const data = letter?.data && typeof letter.data === "object" ? letter.data : {};
    const sm = toLowerString(letter?.shippingMethod ?? data.shippingMethod) === "emessage" ? "emessage" : "letter";
    const facility = data.eMessage ?? letter?.eMessage ?? null;
    const facilityId =
      facility && typeof facility === "object" ? String((facility as any).id ?? (facility as any).name ?? "") : "";
    const photos = Array.isArray(data.selectedPhotos)
      ? data.selectedPhotos.length
      : Array.isArray(letter?.selectedPhotos)
        ? letter.selectedPhotos.length
        : 0;
    return JSON.stringify({
      sm,
      facilityId,
      photos,
      photoType: toLowerString(data.photoType ?? letter?.photoType),
      creditOption: String(data.creditOption ?? letter?.creditOption ?? ""),
      noPhotos: toBool(data.noPhotosSelected ?? letter?.noPhotosSelected),
    });
  };

  const forceSaveDraftBeforeSend = async () => {
    if (!checkoutLetter) throw new Error("Draft details are still loading. Try again.");
    const draftId = String((checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? "");
    let fresh: RawMessage;
    try {
      fresh = await fetchLetter({ letterId: draftId });
    } catch {
      // The belt must never become a pre-flight gate on the money path: the
      // draft was already persisted by the normal save paths, so a failed
      // read skips the belt (and the affirmative write) rather than
      // blocking a send that would have succeeded.
      return;
    }
    // The server moved on (another tab or device): don't send under terms
    // the customer never saw, and don't write their view over the change.
    // Re-render from the server's copy and let them look again.
    if (pricingSnapshot(fresh) !== pricingSnapshot(checkoutLetter)) {
      queryClient.setQueryData(checkoutLetterQueryKey, fresh);
      throw new Error("Your draft just changed. Please review it and send again.");
    }
    const freshData = (fresh as any)?.data;
    const payloadBase = freshData && typeof freshData === "object" ? { ...(freshData as Record<string, unknown>) } : {};
    const hasShippingMethod =
      typeof (payloadBase as any).shippingMethod === "string" && !!(payloadBase as any).shippingMethod.trim();
    const next: Record<string, unknown> = {
      ...payloadBase,
      id: (checkoutLetter as any)?.id,
      ...(hasShippingMethod ? {} : { shippingMethod: "letter" }),
    };
    const payload = withPenmatePayload(next);
    const saved = await saveDraft({ draft: payload });
    syncDraftOverrideAfterCheckoutSave({ draftId, next, saved });
  };

  const autoDefaultReturnAddressRanRef = useRef(false);
  useEffect(() => {
    autoDefaultReturnAddressRanRef.current = false;
  }, [(checkoutLetter as any)?.id]);

  useEffect(() => {
    if (checkoutLetterLoading) return;
    if (!checkoutLetter) return;
    if (autoDefaultReturnAddressRanRef.current) return;

    const existingReturnAddress = (checkoutLetter as any)?.returnAddress ?? (checkoutLetter as any)?.data?.returnAddress ?? null;
    if (hasAddressLine1(existingReturnAddress)) return;

    const defaultReturnAddress =
      (checkoutLetter as any)?.defaultReturnAddress ??
      (checkoutLetter as any)?.default_from_address ??
      (checkoutLetter as any)?.data?.defaultReturnAddress ??
      (checkoutLetter as any)?.data?.default_from_address ??
      null;
    if (!hasAddressLine1(defaultReturnAddress)) return;

    const letterId = (checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? null;
    if (letterId == null) return;

    autoDefaultReturnAddressRanRef.current = true;
    void (async () => {
      try {
        const data = (checkoutLetter as any)?.data;
        const payloadBase = data && typeof data === "object" ? { ...(data as Record<string, unknown>) } : {};
        const rawLine1 =
          (defaultReturnAddress as any)?.address_line1 ??
          (defaultReturnAddress as any)?.line1 ??
          (defaultReturnAddress as any)?.email_address_line1 ??
          (defaultReturnAddress as any)?.formatted_address ??
          "";
        const normalizedDefault =
          typeof rawLine1 === "string" && rawLine1 && !(defaultReturnAddress as any)?.address_line1
            ? { ...(defaultReturnAddress as any), address_line1: rawLine1 }
            : { ...(defaultReturnAddress as any) };

        const next: Record<string, unknown> = {
          ...payloadBase,
          id: letterId,
          returnAddress: {
            addressType: "physical",
            ...normalizedDefault,
          },
        };

        const payload = withPenmatePayload(next);
        const saved = await saveDraft({ draft: payload });
        syncDraftOverrideAfterCheckoutSave({ draftId: String(letterId), next, saved });
        await queryClient.invalidateQueries({ queryKey: ["pmreact", "letters", "by-id"] });
      } catch {
        autoDefaultReturnAddressRanRef.current = false;
      }
    })();
  }, [checkoutLetter, checkoutLetterLoading, queryClient, withPenmatePayload]);

  const shouldGatePhoneVerification = (): boolean => {
    if (requirements.paymentRequired) return false;
    if (checkoutUser?.hasPurchasedCredits === true) return false;
    const verified = checkoutUser?.raw?.phone_number_verified;
    return verified !== true;
  };

  const sendInFlightRef = useRef(false);

  const finalizeSendSuccess = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] }),
      queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "drafts"] }),
      queryClient.invalidateQueries({ queryKey: ["pmreact", "letters", "by-id"] }),
      queryClient.invalidateQueries({ queryKey: ["pmreact", "auth", "validate-token"] }),
    ]);
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ["pmreact", "messaging", "thread"] }),
      queryClient.refetchQueries({ queryKey: ["pmreact", "messaging", "drafts"] }),
      queryClient.refetchQueries({ queryKey: ["pmreact", "auth", "validate-token"] }),
    ]);
    clearCheckoutUrlParams();
    draftUi.setEditingDraftId(null);
    onCancel();
  };

  const sendNow = async (opts: { skipVerify?: boolean } = {}) => {
    if (!checkoutLetter) return;
    const letterIdValue = (checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? null;
    const letterId = letterIdValue != null ? String(letterIdValue) : "";
    if (!letterId) return;

    if (checkoutIsResend) {
      const cleared = await ensureResendCreditsCleared();
      if (!cleared) return;
    }

    if (!opts.skipVerify && shouldGatePhoneVerification()) {
      setPhoneVerifyOpen(true);
      return;
    }

    if (sendInFlightRef.current) return;
    sendInFlightRef.current = true;

    const baseMetricsPayload = buildCheckoutMetricsPayload(checkoutLetter);
    if (!baseMetricsPayload) {
      sendInFlightRef.current = false;
      return;
    }
    try {
      void trackMetric("submit-letter-start", baseMetricsPayload, { sessionId });
      trackReplayStageFromMetricPayload("send_attempted", "submit-letter-start", baseMetricsPayload);
    } catch {
      // Never block sending due to analytics errors.
    }

    setSendError(null);
    setReturnAddressSendError(null);
    setIsSending(true);
    let didSucceed = false;
    try {
      onSendUiStateChange?.({ phase: "sending" });
    } catch {
      // ignore
    }
    try {
      await forceSaveDraftBeforeSend();

      const sendResp = await sendLetter({ letterId });
      const sentLetter = sendResp.data?.letter ?? null;

      const sale = getSaleObject(sentLetter);
      const paymentAction = sale?.payment_action;
      const requiresAction = !!paymentAction?.requires_action;
      const clientSecret =
        typeof paymentAction?.payment_intent_client_secret === "string"
          ? paymentAction.payment_intent_client_secret
          : "";

      if (requiresAction && !clientSecret) {
        const msg = "Sorry, there was an error sending your message. Try again.";
        setSendError(msg);
        return;
      }

      if (requiresAction && clientSecret) {
        const stripeKey = getStripeKey();
        const stripe = await loadStripe(stripeKey);
        if (!stripe) throw new Error("Stripe is not available");
        const res = await stripe.confirmCardPayment(clientSecret);
        if (res.error) {
          const errMsg = res.error.message || "Payment confirmation failed. Please try again.";
          setSendError(errMsg);
          trackSubmitLetterError(baseMetricsPayload, "payment");
          return;
        }
        const pi = (res as any).paymentIntent;
        const intent_id = typeof pi?.id === "string" ? pi.id : "";
        const payment_method = typeof pi?.payment_method === "string" ? pi.payment_method : "";
        const piSecret = typeof pi?.client_secret === "string" ? pi.client_secret : "";
        if (!intent_id || !payment_method || !piSecret) {
          const errMsg = "Payment confirmation failed. Please try again.";
          setSendError(errMsg);
          trackSubmitLetterError(baseMetricsPayload, "payment");
          return;
        }
        const confirmResp = await confirmLetterPayment({
          letterId,
          intent_id,
          payment_method,
          client_secret: piSecret,
        });
        const confirmedLetter = confirmResp.data?.letter ?? null;
        if (!confirmResp.ok) {
          const msg = extractPaymentError(confirmedLetter) || "Sorry, there was an error sending your message. Try again.";
          setSendError(msg);
          trackSubmitLetterError(baseMetricsPayload, "payment");
          // Slice 2.2: the failure response carries the fresh typed
          // payment_state — refresh the snapshot so the row and CTA
          // reflect the decline immediately, not on some later refetch.
          void queryClient.invalidateQueries({ queryKey: ["pmreact", "letters", "by-id"] });
          return;
        }

        onSent?.(letterId);
        didSucceed = true;
        try {
          trackSubmitLetterSuccess({
            letter: confirmedLetter,
            basePayload: baseMetricsPayload,
            isResend: checkoutIsResend,
          });
        } catch (error) {
          if (shouldLogTrackingErrors()) {
            // eslint-disable-next-line no-console
            console.warn("Send letter tracking failed", error);
          }
        }
        await finalizeSendSuccess();
        return;
      }

      if (!sendResp.ok) {
        const msg = extractPaymentError(sentLetter) || "Sorry, there was an error sending your message. Try again.";
        setSendError(msg);
        trackSubmitLetterError(baseMetricsPayload, "send");
        // Slice 2.2: surface the typed decline state immediately (see the
        // confirm-path note above).
        void queryClient.invalidateQueries({ queryKey: ["pmreact", "letters", "by-id"] });
        return;
      }

      onSent?.(letterId);
      didSucceed = true;
      try {
        trackSubmitLetterSuccess({
          letter: sentLetter,
          basePayload: baseMetricsPayload,
          isResend: checkoutIsResend,
        });
      } catch (error) {
        if (shouldLogTrackingErrors()) {
          // eslint-disable-next-line no-console
          console.warn("Send letter tracking failed", error);
        }
      }
      await finalizeSendSuccess();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setSendError(msg);
      trackSubmitLetterError(baseMetricsPayload, "exception");
    } finally {
      setIsSending(false);
      sendInFlightRef.current = false;
      if (!didSucceed) {
        try {
          onSendUiStateChange?.({ phase: "idle" });
        } catch {
          // ignore
        }
      }
    }
  };

  const openPostcardFrontEditor = () => {
    if (!checkoutLetter) return;
    const rawId = (checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? null;
    const draftId = rawId != null ? String(rawId) : "";
    if (!draftId) return;
    draftUi.ensureDraftOverrideFromRaw(draftId, checkoutLetter);
    draftUi.openPostcardEditor(draftId);
  };

  const openRequiredSection = (nextKey: CheckoutSectionKey | null) => {
    if (!nextKey) return;
    if (nextKey === "postcard_front") openPostcardFrontEditor();
    if (nextKey === "delivery") openDeliverySheet("delivery");
    if (nextKey === "facility") openDeliverySheet("facility");
    if (nextKey === "return_address") openReturnAddressSheet();
    if (nextKey === "photo_type") openPhotoTypeSheet();
    if (nextKey === "credits") openCreditsSheet();
    if (nextKey === "payment") openPaymentSheet();
  };

  const openNextRequired = () => {
    if (returnAddressSendError) {
      openReturnAddressSheet();
      return;
    }
    openRequiredSection(requirements.nextRequiredKey);
  };

  // In-session checkout recovery. The client asks only on an explicit
  // abandon action (PaymentSheet dismissed, checkout cancelled, host back),
  // on arrival from the return surface, or on the reminder line's tap; the
  // server owns eligibility, exposure and terms. Anything other than a
  // well-formed offer inside the bounded wait is a strict no-op, so an older
  // API without the endpoint leaves checkout exactly as it was.
  const checkoutLetterIdString = useMemo(
    () => String((checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? ""),
    [checkoutLetter],
  );
  const checkoutLetterQueryKey = useMemo(
    () => ["pmreact", "letters", "by-id", { letterId: checkoutLetterIdString }],
    [checkoutLetterIdString],
  );

  // What an abandon moment ends up presenting: a recovery offer (which may
  // carry the rescue as its second button), or the rescue on its own.
  type RecoveryPresentation =
    | { kind: "offer"; offer: RecoveryOffer; mode: "offer" | "rescue"; letterId: string }
    | { kind: "rescue"; copy: RescueCopy; letterId: string };

  const requestRecovery = async (
    trigger: RecoveryTrigger,
    opts: { explicit?: boolean } = {},
  ): Promise<RecoveryPresentation | null> => {
    const letter = (latestCheckoutLetterRef.current as RawMessage | null) ?? checkoutLetter;
    const letterId = String((letter as any)?.id ?? (letter as any)?.data?.id ?? "");
    if (!letterId) return null;
    // The gate is shared with the regime D rescue, which has its own
    // (server-side) switch: the recovery lever is checked further down, on
    // the recovery half alone.
    const decision = evaluateCheckoutAbandonGate({ trigger, letter, user: checkoutUser, requirements, isSending });
    if (!decision.eligible) return null;
    // Regime D exit-certainty: every eligible abandon action counts, whether
    // or not anything ends up presented. The rescue intent is decided by the
    // client (WHEN); whether a rescue exists is the server's (IF), read off
    // the letter.
    const abandon = trigger === "payment_sheet_dismissed" || trigger === "checkout_cancel" || trigger === "host_back";
    if (recoveryRequestInFlightRef.current || recoverySheetOpen) return null;
    // Counted only once the action is real (past the in-flight and open-sheet
    // guards): a hardware back landing while a cancel request is in flight is
    // one leave, not two abandonments.
    let rescueIntent = false;
    if (abandon) {
      const abandonments = recordRecoveryAbandonment(letterId);
      rescueIntent =
        recoveryRescueIntent({ letterId, trigger, abandonments }) && letterRescueAvailable(letter);
    }
    if (!opts.explicit) {
      if (rescueIntent ? recoveryRescueShownThisVisitRef.current : recoveryShownThisVisitRef.current) return null;
    }
    // The rescue on its own: what the read promises when no recovery offer
    // can carry it (a recovery holdout or recovery-ineligible customer, the
    // recovery lever off, a lost request). Never from the reminder's tap:
    // that line is rung 2.
    const rescueAlone = (): RecoveryPresentation | null => {
      if (!rescueIntent || opts.explicit || recoveryRescueShownThisVisitRef.current) return null;
      const copy = letterRescueCopy(letter);
      return copy ? { kind: "rescue", copy, letterId } : null;
    };
    if (!checkoutRecoveryEnabled()) return rescueAlone();
    recoveryRequestInFlightRef.current = true;
    try {
      const offer = await withRecoveryTimeout(
        requestRecoveryOffer({ letterId, trigger }),
        trigger === "return" ? RECOVERY_RETURN_TIMEOUT_MS : RECOVERY_REQUEST_TIMEOUT_MS,
      );
      if (!cardMountedRef.current) return null;
      // Stale by the time it arrived: the letter changed, a send started, or
      // the customer re-opened the payment sheet (they are progressing again).
      const latest = latestCheckoutLetterRef.current as RawMessage | null;
      const latestId = String((latest as any)?.id ?? (latest as any)?.data?.id ?? "");
      if (latestId && latestId !== letterId) return null;
      if (isSendingRef.current) return null;
      if (paymentSheetOpenRef.current) return null;
      if (!offer) return rescueAlone();
      // No rescue on the payload at an exit-certainty moment (photo letter,
      // already granted, arm A, kill switch) = no rung 3: the moment behaves
      // exactly as before regime D, gate included.
      const mode: "offer" | "rescue" =
        rescueIntent && offer.rescue_available && offer.copy.rescue_label ? "rescue" : "offer";
      if (!opts.explicit) {
        if (mode === "rescue" && recoveryRescueShownThisVisitRef.current) return null;
        if (mode === "offer" && recoveryShownThisVisitRef.current) return null;
      }
      return { kind: "offer", offer, mode, letterId };
    } finally {
      recoveryRequestInFlightRef.current = false;
    }
  };

  // Letter-scoped like checkout_started, plus the offer identity. The
  // accepted event carries the letter as it was at the moment of accepting
  // (the price the customer walked from), by design.
  // The presentation is passed in, never read from state: the caller knows
  // it before React does. A rescue on its own has no issuance to name.
  const recoveryEventPayload = (
    offer: RecoveryOffer | null,
    trigger: RecoveryTrigger,
    presentation: "offer" | "rescue",
  ): Record<string, unknown> => {
    const letter = (latestCheckoutLetterRef.current as RawMessage | null) ?? checkoutLetter;
    const base = (letter && buildCheckoutMetricsPayload(letter)) || { letter_id: checkoutLetterIdString || null };
    return {
      ...base,
      trigger_kind: recoveryTriggerKind(trigger),
      trigger,
      presentation,
      has_issuance: !!offer,
      variant_key: offer ? offer.variant_key : null,
      issuance_id: offer ? offer.issuance_id : null,
      offer_price_cents: offer ? offer.price_cents : null,
      offer_max_photos: offer ? offer.max_photos : null,
    };
  };

  const showRecoveryOffer = (
    presentation: RecoveryPresentation,
    trigger: RecoveryTrigger,
    exitAfterClose: (() => void) | null,
  ) => {
    if (!cardMountedRef.current) return;
    recoveryPresentedLetterIdRef.current = presentation.letterId;
    recoveryExitAfterCloseRef.current = exitAfterClose;
    setRecoveryTrigger(trigger);
    setRecoveryNotice(null);
    if (presentation.kind === "rescue") {
      recoveryRescueShownThisVisitRef.current = true;
      recoveryShownThisVisitRef.current = true;
      void trackMetric("checkout-recovery-rescue-displayed", recoveryEventPayload(null, trigger, "rescue"), { sessionId });
      setRecoveryMode("rescue");
      setRecoveryOffer(null);
      setRecoveryRescueOnly(presentation.copy);
      return;
    }
    const { offer, mode, letterId } = presentation;
    if (mode === "rescue") {
      recoveryRescueShownThisVisitRef.current = true;
      recoveryShownThisVisitRef.current = true;
      // Once per presentation, by design; the grant event is the server's.
      void trackMetric("checkout-recovery-rescue-displayed", recoveryEventPayload(offer, trigger, "rescue"), { sessionId });
    } else {
      recoveryShownThisVisitRef.current = true;
      markRecoverySweetenerDisplayed(letterId);
    }
    setRecoveryMode(mode);
    setRecoveryRescueOnly(null);
    setRecoveryOffer(offer);
  };

  const handlePaymentSheetDismiss = () => {
    void (async () => {
      const presentation = await requestRecovery("payment_sheet_dismissed");
      if (!presentation) return;
      showRecoveryOffer(presentation, "payment_sheet_dismissed", null);
    })();
  };

  // "Cancel sending?" confirmed: the confirm button holds in a pending state
  // for the bounded request, then the exit proceeds as before unless an
  // offer came back — in which case the exit waits on the customer's
  // decision.
  const confirmCancelCheckout = async () => {
    if (cancelPending) return;
    setCancelPending(true);
    try {
      const presentation = await requestRecovery("checkout_cancel");
      if (!cardMountedRef.current) return;
      setConfirmOpen(false);
      if (!presentation) {
        onCancel();
        return;
      }
      showRecoveryOffer(presentation, "checkout_cancel", () => onCancel());
    } finally {
      if (cardMountedRef.current) setCancelPending(false);
    }
  };

  const handleRecoveryDisplayed = (issuanceId: string) => {
    const offer = recoveryOffer;
    const trigger = recoveryTrigger;
    if (!offer || !trigger || offer.issuance_id !== issuanceId) return;
    if (!checkoutLetterIdString) return;
    // One display record and one event per issuance for the life of the
    // bundle, even when the same open offer is rendered again (soft dismiss,
    // a later visit via the return surface).
    if (!markRecoveryIssuanceDisplayed(issuanceId)) return;
    void markRecoveryOfferDisplayed({ letterId: checkoutLetterIdString, issuanceId });
    void trackMetric("checkout-recovery-displayed", recoveryEventPayload(offer, trigger, recoveryMode), { sessionId });
  };

  const finishRecoveryAccept = async (args: { offer: RecoveryOffer; trigger: RecoveryTrigger; repriced: RawMessage | null }) => {
    const { offer, trigger, repriced } = args;
    if (repriced) queryClient.setQueryData(checkoutLetterQueryKey, repriced);
    void trackMetric("checkout-recovery-accepted", recoveryEventPayload(offer, trigger, recoveryMode), { sessionId });
    recoveryExitAfterCloseRef.current = null;
    setRecoveryOffer(null);
    setRecoveryTrigger(null);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["pmreact", "letters", "by-id"] }),
      queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] }),
    ]);
    if (!cardMountedRef.current) return;
    // Land on the next required step from the re-priced letter itself
    // (Payment at the offer price), independent of render timing.
    const fresh =
      (queryClient.getQueryData(checkoutLetterQueryKey) as RawMessage | undefined) ??
      repriced ??
      (latestCheckoutLetterRef.current as RawMessage | null) ??
      checkoutLetter;
    const next = getCheckoutRequirements({
      letter: fresh,
      user: checkoutUser,
      offersByBundle: dpOffersByBundle,
      noPhotosSelected,
    }).nextRequiredKey;
    openRequiredSection(next);
  };

  const handleRecoveryAccept = async () => {
    const offer = recoveryOffer;
    const trigger = recoveryTrigger;
    const letterId = checkoutLetterIdString;
    if (!offer || !trigger || !letterId) throw new Error(RECOVERY_ACCEPT_ERROR_MESSAGE);
    let repriced: RawMessage | null;
    try {
      repriced = await acceptRecoveryOffer({ letterId, issuanceId: offer.issuance_id });
    } catch (e) {
      // A late success (timed-out request that landed) shows up as the
      // server's marker on the refetched letter; treat it as accepted. The
      // sheet is still up here, so this refetch is bounded too.
      await withRecoveryTimeout(
        queryClient.invalidateQueries({ queryKey: ["pmreact", "letters", "by-id"] }),
        RECOVERY_REQUEST_TIMEOUT_MS,
      );
      if (!cardMountedRef.current) return;
      const fresh = queryClient.getQueryData(checkoutLetterQueryKey) as RawMessage | undefined;
      if (fresh && letterHasRecoveryOffer(fresh)) {
        await finishRecoveryAccept({ offer, trigger, repriced: null });
        return;
      }
      throw e;
    }
    if (!cardMountedRef.current) return;
    await finishRecoveryAccept({ offer, trigger, repriced });
  };

  // The customer has seen the sheet and left the offer open: the letter read
  // carries the open issuance in `recovery_offer`, which is what renders the
  // order summary's reminder line. The server just returned this issuance,
  // so patching it onto the cached letter IS the truth (here and on the next
  // checkout entry); a re-read is needed only when the cache was behind the
  // server and had no field to patch — it costs a checklist skeleton blink
  // (the card shows one while the letter is fetching), so it is not routine.
  const rememberOpenRecoveryIssuance = (issuanceId: string) => {
    if (!checkoutLetterIdString) return;
    let patched = false;
    queryClient.setQueryData(checkoutLetterQueryKey, (prev: any) => {
      const field = prev?.recovery_offer;
      if (!prev || !field || typeof field !== "object") return prev;
      patched = true;
      return { ...prev, recovery_offer: { ...field, issuance_id: issuanceId } };
    });
    if (!patched) void queryClient.invalidateQueries({ queryKey: checkoutLetterQueryKey });
  };

  // Every way of closing the sheet is "Not now": the offer stays open on the
  // server (there is no in-session decline), and whatever the customer was
  // doing completes — the cancel exit, the host navigation — or they simply
  // stay in checkout with the reminder line.
  const closeRecoveryOffer = (args: { completeExit: boolean }) => {
    const offer = recoveryOffer;
    const exit = recoveryExitAfterCloseRef.current;
    recoveryExitAfterCloseRef.current = null;
    setRecoveryOffer(null);
    setRecoveryRescueOnly(null);
    setRecoveryTrigger(null);
    if (offer) rememberOpenRecoveryIssuance(offer.issuance_id);
    if (args.completeExit) exit?.();
  };

  const handleRecoveryNotNow = () => closeRecoveryOffer({ completeExit: true });

  // A letter that prices free is a landed grant, however its request ended.
  const letterPricesFree = (letter: RawMessage | null | undefined): boolean => {
    if (!letter) return false;
    const m = (letter as any)?.meta ?? (letter as any)?.data?.meta ?? {};
    if (toBool(m?.freeFirstLetter ?? m?.free_first_letter)) return true;
    const price = Number((letter as any)?.priceAfterCoupon ?? (letter as any)?.price);
    return Number.isFinite(price) && price === 0;
  };

  const finishRecoveryRescue = async () => {
    if (!cardMountedRef.current) return;
    recoveryExitAfterCloseRef.current = null;
    setRecoveryOffer(null);
    setRecoveryRescueOnly(null);
    setRecoveryTrigger(null);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["pmreact", "letters", "by-id"] }),
      queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] }),
    ]);
  };

  // Rung 3: the grant makes THIS letter free. The customer stays in checkout
  // (no exit — they are about to send), the returned letter re-renders it
  // free, and no client event fires for the grant (the server tracks it).
  const handleRecoveryRescue = async () => {
    const letterId = recoveryPresentedLetterIdRef.current || checkoutLetterIdString;
    if (!recoverySheetOpen || !letterId) throw new Error(RECOVERY_RESCUE_ERROR_MESSAGE);
    setRecoveryNotice(null);
    let rescued: RawMessage | null;
    try {
      rescued = await redeemFreeRescue({ letterId });
    } catch (e) {
      // A late success (the bounded request timed out but the grant landed)
      // or an idempotent repeat shows up as a letter that prices free on the
      // refetch: that is a grant, whatever the request said. The sheet is
      // still up here, so this refetch is bounded too.
      await withRecoveryTimeout(
        queryClient.invalidateQueries({ queryKey: ["pmreact", "letters", "by-id"] }),
        RECOVERY_REQUEST_TIMEOUT_MS,
      );
      if (!cardMountedRef.current) return;
      const fresh = queryClient.getQueryData(checkoutLetterQueryKey) as RawMessage | undefined;
      if (fresh && letterPricesFree(fresh)) {
        await finishRecoveryRescue();
        return;
      }
      if (isRescueUnavailableError(e)) {
        // The server said no for good: the sheet closes on the paid letter
        // (no exit — the customer meant to send), the line under the total
        // says why, and the refetch above already dropped a stale rescue flag.
        closeRecoveryOffer({ completeExit: false });
        setRecoveryNotice(e instanceof Error && e.message ? e.message : RECOVERY_RESCUE_UNAVAILABLE_MESSAGE);
        return;
      }
      throw e;
    }
    // The cache work is safe (and wanted) even if the card unmounted while
    // the grant was in flight: a stale paid letter must not survive.
    if (rescued) queryClient.setQueryData(checkoutLetterQueryKey, rescued);
    await finishRecoveryRescue();
  };

  // The reminder line: an explicit ask, so it bypasses the once-per-visit
  // gate and gets the return bound (nothing is racing it). No offer back
  // (the issuance is gone, or the request did not make it) re-reads the
  // letter so the line follows the server rather than dead-tapping. It
  // renders only where a tap can succeed — the same client gate the request
  // goes through (paid checkout, not glossy / postcard / resend, not
  // sending) — so a customer who switched to Glossy after a Not now is never
  // left with a control that cannot present.
  const recoveryReminder = useMemo(() => {
    const reminder = letterRecoveryReminder(checkoutLetter);
    if (!reminder) return null;
    const decision = evaluateRecoveryTrigger({
      trigger: "return",
      letter: checkoutLetter,
      user: checkoutUser,
      requirements,
      isSending,
    });
    return decision.eligible ? reminder : null;
  }, [checkoutLetter, checkoutUser, requirements, isSending]);

  // Applied: the server says what the letter would have cost and what was
  // saved; the summary shows the regular subtotal and a discount row (the
  // amount lives there and nowhere else), and the label-only tag line takes
  // the reminder's slot (the two are exclusive by construction: a reminder
  // needs no marker, the applied shape needs one).
  const recoveryApplied = useMemo(() => letterRecoveryApplied(checkoutLetter), [checkoutLetter]);
  const recoveryDiscountShown =
    !!recoveryApplied &&
    recoveryApplied.regularPriceCents != null &&
    recoveryApplied.savingsCents != null &&
    recoveryApplied.savingsCents > 0;

  // Same letter-scoped shape as the CTA events on my-penmates (a reminder
  // always carries an open issuance by construction).
  const recoveryReminderEventPayload = (reminder: RecoveryReminder): Record<string, unknown> => {
    const letter = (latestCheckoutLetterRef.current as RawMessage | null) ?? checkoutLetter;
    const base = (letter && buildCheckoutMetricsPayload(letter)) || { letter_id: checkoutLetterIdString || null };
    return { ...base, issuance_id: reminder.issuanceId, has_issuance: true };
  };

  useEffect(() => {
    if (!recoveryReminder || recoveryReminderDisplayedRef.current) return;
    recoveryReminderDisplayedRef.current = true;
    void trackMetric("checkout-recovery-reminder-displayed", recoveryReminderEventPayload(recoveryReminder), { sessionId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recoveryReminder]);

  const handleRecoveryReminderTap = () => {
    if (recoveryReminderPending || recoverySheetOpen || !recoveryReminder) return;
    setRecoveryReminderPending(true);
    void trackMetric("checkout-recovery-reminder-tapped", recoveryReminderEventPayload(recoveryReminder), { sessionId });
    void (async () => {
      try {
        const presentation = await requestRecovery("return", { explicit: true });
        if (!cardMountedRef.current) return;
        if (presentation) {
          showRecoveryOffer(presentation, "return", null);
          return;
        }
        void queryClient.invalidateQueries({ queryKey: checkoutLetterQueryKey });
      } finally {
        if (cardMountedRef.current) setRecoveryReminderPending(false);
      }
    })();
  };

  // Host back (Angular header back / hardware back) while checkout is
  // active: the host's route guard asks before leaving and
  // waits (bounded) for `pmreact:checkout_leave_decided`. Same recovery
  // flow; "Not now" exits checkout and hands the navigation back via
  // `pmreact:checkout_leave_proceed`. Re-subscribed every render so the
  // handler always sees the current checkout state.
  useEffect(() => {
    const dispatchHost = (name: string, detail: Record<string, unknown>) => {
      try {
        window.dispatchEvent(new CustomEvent(name, { detail }));
      } catch {
        // no-op
      }
    };
    const onLeaveRequested = (evt: Event) => {
      const detail = ((evt as CustomEvent)?.detail ?? {}) as Record<string, unknown>;
      const requestId = detail.requestId != null ? String(detail.requestId) : "";
      if (!requestId) return;
      const reply = (proceed: boolean) => dispatchHost("pmreact:checkout_leave_decided", { requestId, proceed });
      const draftId = detail.draftId != null ? String(detail.draftId) : "";
      if (draftId && checkoutLetterIdString && draftId !== checkoutLetterIdString) {
        reply(true);
        return;
      }
      // An offer is already up: leaving over it is "Not now" for THIS
      // navigation — the sheet closes and the host proceeds; a cancel exit
      // the sheet was holding is not replayed (the page is leaving anyway).
      if (recoverySheetOpen) {
        closeRecoveryOffer({ completeExit: false });
        reply(true);
        return;
      }
      // Already asked once this checkout: let them go.
      if (recoveryHostLeaveAskedRef.current) {
        reply(true);
        return;
      }
      recoveryHostLeaveAskedRef.current = true;
      void (async () => {
        const presentation = await requestRecovery("host_back");
        if (!presentation) {
          reply(true);
          return;
        }
        showRecoveryOffer(presentation, "host_back", () => {
          onCancel();
          dispatchHost("pmreact:checkout_leave_proceed", { requestId });
        });
        reply(false);
      })();
    };
    window.addEventListener("pmreact:checkout_leave_requested", onLeaveRequested);
    return () => window.removeEventListener("pmreact:checkout_leave_requested", onLeaveRequested);
  });

  // Return surface (my-penmates CTA): the deep link carries `recoveryOffer=1`;
  // once the letter is in, consume the flag and present the offer on arrival.
  // "Not now" leaves the customer in checkout on their draft, with the
  // reminder line.
  useEffect(() => {
    if (recoveryReturnHandledRef.current) return;
    if (checkoutLetterLoading || checkoutUserLoading) return;
    if (!checkoutLetter || !checkoutUser) return;
    // Only a draft can carry an offer; a sent letter is about to be bounced
    // out of checkout by the app's guard.
    const status = String((checkoutLetter as any)?.aasm_state ?? (checkoutLetter as any)?.status ?? "").toLowerCase();
    if (status && status !== "draft") return;
    recoveryReturnHandledRef.current = true;
    if (!consumeRecoveryReturnFlag(checkoutLetterIdString)) return;
    void (async () => {
      const presentation = await requestRecovery("return");
      if (!presentation) return;
      showRecoveryOffer(presentation, "return", null);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutLetter, checkoutUser, checkoutLetterLoading, checkoutUserLoading]);

  const saveDelivery = async (args: { shippingMethod: "letter" | "emessage"; eMessage: any | null }) => {
    if (!checkoutLetter) throw new Error("Draft details are still loading. Try again.");
    const draftId = String((checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? "");
    const data = (checkoutLetter as any)?.data;
    const payloadBase = data && typeof data === "object" ? { ...(data as Record<string, unknown>) } : {};
    const currentShippingMethod = toLowerString(
      (checkoutLetter as any)?.shippingMethod ?? (checkoutLetter as any)?.data?.shippingMethod,
    ) === "emessage"
      ? "emessage"
      : "letter";
    const selectedPhotosBase = Array.isArray((payloadBase as any)?.selectedPhotos)
      ? ((payloadBase as any).selectedPhotos as any[])
      : Array.isArray((checkoutLetter as any)?.selectedPhotos)
        ? ((checkoutLetter as any).selectedPhotos as any[])
        : [];
    const noPhotosSelectedValue =
      (payloadBase as any)?.noPhotosSelected ??
      (payloadBase as any)?.no_photos_selected ??
      (checkoutLetter as any)?.noPhotosSelected ??
      (checkoutLetter as any)?.no_photos_selected ??
      (checkoutLetter as any)?.data?.noPhotosSelected ??
      (checkoutLetter as any)?.data?.no_photos_selected;
    const needsClearNoPhotos = args.shippingMethod === "emessage" && toBool(noPhotosSelectedValue);
    const restoredPhotos = needsClearNoPhotos && stashedPhotosRef.current ? stashedPhotosRef.current.slice() : null;
    const preservedFacility =
      (payloadBase as any)?.eMessage ??
      (checkoutLetter as any)?.eMessage ??
      (payloadBase as any)?.e_message ??
      (checkoutLetter as any)?.e_message ??
      null;

    if (checkoutIsResend) {
      if (args.shippingMethod !== currentShippingMethod) {
        throw new Error("Delivery method can’t be changed for a resend.");
      }
      const next: Record<string, unknown> = {
        ...payloadBase,
        id: (checkoutLetter as any)?.id,
        shippingMethod: currentShippingMethod,
        // Allow facility updates for E-message resends; otherwise preserve.
        eMessage: currentShippingMethod === "emessage" ? args.eMessage : preservedFacility,
      };
      const payload = withPenmatePayload(next);
      const saved = await saveDraft({ draft: payload });
      syncDraftOverrideAfterCheckoutSave({ draftId, next, saved });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["pmreact", "letters", "by-id"] }),
        queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] }),
      ]);
      return;
    }

    // Legacy parity: E-message supports up to 4 photos. If the user switches back to Print & Mail,
    // restore overflow photos that were removed during the E-message toggle.
    let nextSelectedPhotos: any[] = (needsClearNoPhotos ? restoredPhotos ?? selectedPhotosBase : selectedPhotosBase).slice();
    let shouldWriteSelectedPhotos = needsClearNoPhotos;
    if (args.shippingMethod === "emessage") {
      if (nextSelectedPhotos.length > EMESSAGE_MAX_SELECTED_PHOTOS) {
        deliveryOverflowPhotosRef.current = nextSelectedPhotos.slice(EMESSAGE_MAX_SELECTED_PHOTOS);
        nextSelectedPhotos = nextSelectedPhotos.slice(0, EMESSAGE_MAX_SELECTED_PHOTOS);
        shouldWriteSelectedPhotos = true;
      } else {
        deliveryOverflowPhotosRef.current = null;
      }
    } else if (args.shippingMethod === "letter") {
      if (deliveryOverflowPhotosRef.current && deliveryOverflowPhotosRef.current.length) {
        nextSelectedPhotos = nextSelectedPhotos
          .concat(deliveryOverflowPhotosRef.current)
          .slice(0, DEFAULT_MAX_SELECTED_PHOTOS);
        deliveryOverflowPhotosRef.current = null;
        shouldWriteSelectedPhotos = true;
      }
    }
    const next: Record<string, unknown> = {
      ...payloadBase,
      id: (checkoutLetter as any)?.id,
      shippingMethod: args.shippingMethod,
      // Legacy preserves eMessage when toggling emessage <-> letter so the facility remains preselected.
      eMessage: args.shippingMethod === "emessage" ? args.eMessage : preservedFacility,
      ...(shouldWriteSelectedPhotos ? { selectedPhotos: nextSelectedPhotos } : null),
      ...(needsClearNoPhotos ? { noPhotosSelected: false } : null),
    };

    const payload = withPenmatePayload(next);
    const saved = await saveDraft({ draft: payload });
    syncDraftOverrideAfterCheckoutSave({ draftId, next, saved });
    if (needsClearNoPhotos) stashedPhotosRef.current = null;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["pmreact", "letters", "by-id"] }),
      queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] }),
    ]);
  };

  const savePhotoType = async (args: { selection: PhotoTypeSelection }) => {
    if (!checkoutLetter) throw new Error("Draft details are still loading. Try again.");
    if (checkoutIsResend) throw new Error("Photo type can’t be changed for a resend.");
    const draftId = String((checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? "");
    const data = (checkoutLetter as any)?.data;
    const payloadBase = data && typeof data === "object" ? { ...(data as Record<string, unknown>) } : {};
    const selectedPhotos = Array.isArray((payloadBase as any)?.selectedPhotos)
      ? ((payloadBase as any).selectedPhotos as any[])
      : Array.isArray((checkoutLetter as any)?.selectedPhotos)
        ? ((checkoutLetter as any).selectedPhotos as any[])
        : [];

    const selection = args.selection;
    if (selection === "none") {
      if (!canUseFirstMessageSendlove({ letter: checkoutLetter, user: checkoutUser })) {
        throw new Error("No Photos is only available for your first Print & Mail letter.");
      }
      if (selectedPhotos.length > 0) {
        stashedPhotosRef.current = selectedPhotos.slice();
      } else {
        stashedPhotosRef.current = null;
      }

      const next: Record<string, unknown> = {
        ...payloadBase,
        id: (checkoutLetter as any)?.id,
        selectedPhotos: [],
        photoType: "regular",
        noPhotosSelected: true,
      };
      const payload = withPenmatePayload(next);
      const saved = await saveDraft({ draft: payload });
      syncDraftOverrideAfterCheckoutSave({ draftId, next, saved });
    } else {
      const restoredPhotos =
        selectedPhotos.length === 0 && stashedPhotosRef.current && stashedPhotosRef.current.length > 0
          ? stashedPhotosRef.current.slice()
          : null;
      const next: Record<string, unknown> = {
        ...payloadBase,
        id: (checkoutLetter as any)?.id,
        photoType: selection,
        noPhotosSelected: false,
        ...(restoredPhotos ? { selectedPhotos: restoredPhotos } : null),
      };
      const payload = withPenmatePayload(next);
      const saved = await saveDraft({ draft: payload });
      syncDraftOverrideAfterCheckoutSave({ draftId, next, saved });
      stashedPhotosRef.current = null;
    }

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["pmreact", "letters", "by-id"] }),
      queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] }),
    ]);
  };

  const saveReturnAddress = async (args: { returnAddress: any }) => {
    if (!checkoutLetter) throw new Error("Draft details are still loading. Try again.");
    const draftId = String((checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? "");
    const data = (checkoutLetter as any)?.data;
    const payloadBase = data && typeof data === "object" ? { ...(data as Record<string, unknown>) } : {};
    const existing =
      (payloadBase as any)?.returnAddress && typeof (payloadBase as any).returnAddress === "object"
        ? (payloadBase as any).returnAddress
        : (checkoutLetter as any)?.returnAddress;
    const next: Record<string, unknown> = {
      ...payloadBase,
      id: (checkoutLetter as any)?.id,
      returnAddress: {
        addressType: "physical",
        ...(existing && typeof existing === "object" ? existing : {}),
        ...(args.returnAddress && typeof args.returnAddress === "object" ? args.returnAddress : {}),
      },
    };

    const payload = withPenmatePayload(next);
    const saved = await saveDraft({ draft: payload });
    syncDraftOverrideAfterCheckoutSave({ draftId, next, saved });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["pmreact", "letters", "by-id"] }),
      queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] }),
    ]);
  };

  const canOpenRow = (key: string) => {
    if (checkoutIsResend) {
      if (key === "photo_type") return false;
      if (key === "postcard_front") return false;
      if (key === "credits") return false;
    }
    if (key === "delivery" || key === "facility" || key === "return_address" || key === "postcard_front") return true;
    // Match legacy: the photo printing chooser is only actionable when there are photos to apply it to.
    if (key === "photo_type") return photoTypeEditable;
    if (key === "credits") return true;
    if (key === "payment") return true;
    return false;
  };

  const saveCredits = async (args: { creditOption: string; offerRedemptionId: string | null }) => {
    if (!checkoutLetter) throw new Error("Draft details are still loading. Try again.");
    const draftId = String((checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? "");
    const data = (checkoutLetter as any)?.data;
    const payloadBase = data && typeof data === "object" ? { ...(data as Record<string, unknown>) } : {};
    const next: Record<string, unknown> = {
      ...payloadBase,
      id: (checkoutLetter as any)?.id,
      creditOption: args.creditOption,
      offerRedemptionId: args.offerRedemptionId,
    };
    const payload = withPenmatePayload(next);
    const saved = await saveDraft({ draft: payload });
    syncDraftOverrideAfterCheckoutSave({ draftId, next, saved });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["pmreact", "letters", "by-id"] }),
      queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] }),
    ]);
  };

  const clearOfferRedemptionId = async () => {
    if (!checkoutLetter) throw new Error("Draft details are still loading. Try again.");
    const draftId = String((checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? "");
    const data = (checkoutLetter as any)?.data;
    const payloadBase = data && typeof data === "object" ? { ...(data as Record<string, unknown>) } : {};
    const next: Record<string, unknown> = {
      ...payloadBase,
      id: (checkoutLetter as any)?.id,
      offerRedemptionId: null,
    };
    const payload = withPenmatePayload(next);
    const saved = await saveDraft({ draft: payload });
    syncDraftOverrideAfterCheckoutSave({ draftId, next, saved });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["pmreact", "letters", "by-id"] }),
      queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] }),
    ]);
  };

  const clearCreditsSelection = async () => {
    if (!checkoutLetter) throw new Error("Draft details are still loading. Try again.");
    const draftId = String((checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? "");
    const data = (checkoutLetter as any)?.data;
    const payloadBase = data && typeof data === "object" ? { ...(data as Record<string, unknown>) } : {};
    const next: Record<string, unknown> = {
      ...payloadBase,
      id: (checkoutLetter as any)?.id,
      creditOption: null,
      offerRedemptionId: null,
    };
    const payload = withPenmatePayload(next);
    const saved = await saveDraft({ draft: payload });
    syncDraftOverrideAfterCheckoutSave({ draftId, next, saved });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["pmreact", "letters", "by-id"] }),
      queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] }),
    ]);
  };

  // Preattach persists the saved default with legacy coupon parity:
  // returning customers drop the coupon (as the sheet flow does), but a
  // first-message customer keeps theirs. The payload is rebuilt from the
  // FRESHEST letter at save time (not the evaluation-time snapshot), and
  // the save bails if a payment appeared meanwhile or the customer is in
  // the Payment sheet — the draft save is a whole-data replace, so a stale
  // base would drop concurrent writes (returnAddress auto-save, manual
  // attach). Returns whether the attach was persisted.
  const savePreattachedPayment = async (payment: any): Promise<boolean> => {
    const latest = latestCheckoutLetterRef.current;
    if (!latest) return false;
    if (paymentSheetOpenRef.current) return false;
    const draftId = String((latest as any)?.id ?? (latest as any)?.data?.id ?? "");
    // Freshness must include the same local truth other checkout savers
    // update: their draft override lands synchronously after a save, while
    // the query letter only refreshes after invalidation. Merge canonical
    // letter data with the override as overlay — a post-save override
    // carries the newer fields (e.g. a just-saved returnAddress) and wins;
    // a sparse entry-time override is a subset and changes nothing.
    const override = draftId ? draftUi.getDraftOverrideById(draftId) : null;
    const overrideData = (override as any)?.data;
    if ((override as any)?.payment ?? overrideData?.payment) return false;
    if ((latest as any)?.payment ?? (latest as any)?.data?.payment) return false;
    const letterData = (latest as any)?.data;
    const payloadBase: Record<string, unknown> = {
      ...(letterData && typeof letterData === "object" ? (letterData as Record<string, unknown>) : {}),
      ...(overrideData && typeof overrideData === "object" ? (overrideData as Record<string, unknown>) : {}),
    };
    const hasSent =
      (checkoutUser as any)?.hasSentLetter === true || (checkoutUser as any)?.raw?.has_sent_letter === true;
    const coupon = hasSent ? null : ((payloadBase as any).coupon ?? null);
    const next: Record<string, unknown> = {
      ...payloadBase,
      id: (latest as any)?.id,
      payment,
      coupon,
    };
    const payload = withPenmatePayload(next);
    const saved = await saveDraft({ draft: payload });
    syncDraftOverrideAfterCheckoutSave({ draftId, next, saved });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["pmreact", "letters", "by-id"] }),
      queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] }),
    ]);
    return true;
  };

  const savePayment = async (args: { payment: any }) => {
    if (!checkoutLetter) throw new Error("Draft details are still loading. Try again.");
    const draftId = String((checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? "");
    const data = (checkoutLetter as any)?.data;
    const payloadBase = data && typeof data === "object" ? { ...(data as Record<string, unknown>) } : {};
    const next: Record<string, unknown> = {
      ...payloadBase,
      id: (checkoutLetter as any)?.id,
      payment: args.payment,
      coupon: null,
    };
    const payload = withPenmatePayload(next);
    const saved = await saveDraft({ draft: payload });
    syncDraftOverrideAfterCheckoutSave({ draftId, next, saved });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["pmreact", "letters", "by-id"] }),
      queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] }),
    ]);
  };

  useEffect(() => {
    if (paymentResumeRanRef.current) return;
    if (!checkoutLetter || checkoutLetterLoading) return;

    const checkoutLetterId = String((checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? "");
    if (!checkoutLetterId) return;

    const url = new URL(window.location.href);
    const sessionId = url.searchParams.get("session_id");
    if (!sessionId || !/^cs_/i.test(sessionId)) return;

    // Prefer `letterId` when present; `messageId` is a legacy param name that may be set for other purposes.
    const messageId =
      url.searchParams.get("letterId") ||
      url.searchParams.get("draftId") ||
      url.searchParams.get("messageId");
    if (!messageId || messageId !== checkoutLetterId) return;

    paymentResumeRanRef.current = true;
    void (async () => {
      try {
        const status = await fetchBillingCheckoutStatus({ sessionId });
        const lastError = status.setupIntent?.last_setup_error ?? null;
        const lastErrorDeclineCode =
          lastError && typeof lastError === "object" && typeof (lastError as any).decline_code === "string"
            ? String((lastError as any).decline_code).trim().toLowerCase()
            : "";
        const lastErrorMessage =
          lastError && typeof lastError === "object" && typeof (lastError as any).message === "string"
            ? String((lastError as any).message).trim()
            : "";

        const failWithMessage = (msg: string) => {
          setPaymentResumeError(msg);
          url.searchParams.delete("session_id");
          window.history.replaceState({}, "", url.toString());
          paymentResumeRanRef.current = false;
          // The session is terminal here; its stored save-billing choice
          // can never be applied.
          clearSaveBillingChoice(sessionId);
        };

        if (lastErrorDeclineCode === "generic_decline") {
          failWithMessage("Payment was declined. Please try again or choose a different payment method.");
          return;
        }
        if (lastErrorMessage) {
          failWithMessage(lastErrorMessage);
          return;
        }

        const setupStatus = toLowerString(status.setupIntent?.status);
        if (setupStatus && setupStatus !== "succeeded") {
          const canceled = setupStatus === "canceled";
          failWithMessage(canceled ? "Payment was canceled." : "Payment authorization failed. Please try again.");
          return;
        }

        const checkoutStatus = toLowerString(status.checkout_session?.status);
        if (checkoutStatus && checkoutStatus !== "complete") {
          const msg =
            checkoutStatus === "expired"
              ? "Payment session expired. Please try again."
              : "Payment was not completed. Please try again.";
          failWithMessage(msg);
          return;
        }

        const paymentMethodId = status.setupIntent?.payment_method;
        if (!paymentMethodId || typeof paymentMethodId !== "string") {
          // Terminal like the branches above: the session is complete, so a
          // reload would refetch the same missing method forever.
          failWithMessage("Payment authorization failed. Please try again.");
          return;
        }
        const details = await fetchBillingPaymentMethod({ id: paymentMethodId });
        await savePayment({
          payment: {
            id: paymentMethodId,
            paymentIntentId: status.setupIntent?.id ?? status.setup_intent_id ?? null,
            paymentIntentStatus: status.setupIntent?.status ?? null,
            ...details,
          },
        });
        // Slice 2.6: a redirect-based attach applies the entry checkbox
        // value stored before the Stripe round-trip (fallback ON — the
        // legacy default).
        if (saveBillingChoiceEnabled()) {
          const save = readSaveBillingChoice(sessionId);
          let applied = false;
          try {
            await toggleSaveBilling({ save });
            applied = true;
            void queryClient.invalidateQueries({ queryKey: ["pmreact", "auth", "validate-token"] });
          } catch {
            // Best-effort: the attach already landed.
          }
          void trackMetric("save_billing_choice", {
            letter_id: checkoutLetterId,
            save,
            applied,
            context: "attach",
          });
          clearSaveBillingChoice(sessionId);
        }
        for (const k of ["session_id", "messageId", "letterId", "draftId"]) url.searchParams.delete(k);
        window.history.replaceState({}, "", url.toString());
      } catch {
        // Leave params intact so the user can retry by reloading (or opening Payment).
        paymentResumeRanRef.current = false;
      }
    })();
  }, [checkoutLetter, checkoutLetterLoading]);

  const getDraftCreditOptionRaw = (): string | null => {
    const v = (checkoutLetter as any)?.creditOption ?? (checkoutLetter as any)?.data?.creditOption;
    const s = typeof v === "string" || typeof v === "number" ? String(v) : "";
    return s ? s : null;
  };

  const getDraftOfferRedemptionIdRaw = (): string | null => {
    const v =
      (checkoutLetter as any)?.offerRedemptionId ??
      (checkoutLetter as any)?.offer_redemption_id ??
      (checkoutLetter as any)?.data?.offerRedemptionId;
    return typeof v === "string" && v ? v : null;
  };

  const ensureResendCreditsCleared = useCallback(async (): Promise<boolean> => {
    if (!checkoutIsResend) return true;
    if (!checkoutLetter || checkoutLetterLoading) return false;

    const creditOption = getDraftCreditOptionRaw();
    const offerRedemptionId = getDraftOfferRedemptionIdRaw();
    if (!creditOption && !offerRedemptionId) {
      setResendCreditsError(null);
      return true;
    }
    if (resendCreditsClearPromiseRef.current) return await resendCreditsClearPromiseRef.current;

    setResendCreditsClearing(true);
    const promise = (async () => {
      try {
        await clearCreditsSelection();
        setResendCreditsError(null);
        return true;
      } catch {
        setResendCreditsError("Resends can't use credits. Please try again.");
        return false;
      } finally {
        setResendCreditsClearing(false);
        resendCreditsClearPromiseRef.current = null;
      }
    })();
    resendCreditsClearPromiseRef.current = promise;
    return await promise;
  }, [
    checkoutIsResend,
    checkoutLetter,
    checkoutLetterLoading,
    clearCreditsSelection,
    getDraftCreditOptionRaw,
    getDraftOfferRedemptionIdRaw,
  ]);

  const getDraftCreditOption = (): string | null => {
    if (checkoutIsResend) return null;
    return getDraftCreditOptionRaw();
  };

  const getDraftOfferRedemptionId = (): string | null => {
    if (checkoutIsResend) return null;
    return getDraftOfferRedemptionIdRaw();
  };

  useEffect(() => {
    if (!checkoutLetter || checkoutLetterLoading) return;
    if (!checkoutUser) return;
    const current = getDraftOfferRedemptionId();
    if (!current) return;
    if (!dpCreditPurchaseRequired || dpOffersSuppressed) {
      void (async () => {
        try {
          await clearOfferRedemptionId();
        } catch {
          // ignore
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    (checkoutLetter as any)?.id,
    checkoutLetterLoading,
    checkoutUser,
    dpCreditPurchaseRequired,
    dpOffersSuppressed,
  ]);

  useEffect(() => {
    if (!checkoutIsResend) {
      setResendCreditsError(null);
      return;
    }
    if (!checkoutLetter || checkoutLetterLoading) return;

    const creditOption = getDraftCreditOptionRaw();
    const offerRedemptionId = getDraftOfferRedemptionIdRaw();
    if (!creditOption && !offerRedemptionId) {
      setResendCreditsError(null);
      return;
    }

    void ensureResendCreditsCleared();
  }, [
    checkoutIsResend,
    checkoutLetter,
    checkoutLetterLoading,
    ensureResendCreditsCleared,
  ]);

  const selectedOffer = useMemo(() => {
    if (!dpOffersByBundle) return null;
    const redemptionId = getDraftOfferRedemptionId();
    if (redemptionId) {
      for (const key of Object.keys(dpOffersByBundle)) {
        const offer = (dpOffersByBundle as any)[key];
        if (offer && offer.redemption_id === redemptionId) return offer;
      }
    }
    const creditOption = getDraftCreditOption();
    if (!creditOption) return null;
    return (dpOffersByBundle as any)[String(creditOption)] ?? null;
  }, [checkoutIsResend, dpOffersByBundle, checkoutLetter]);

  const selectedCreditOption = useMemo(() => getDraftCreditOption(), [checkoutIsResend, checkoutLetter]);
  const selectedCreditOptionInt = useMemo(() => {
    if (!selectedCreditOption) return 0;
    const n = Number.parseInt(selectedCreditOption, 10);
    return Number.isFinite(n) ? Math.max(0, n) : 0;
  }, [selectedCreditOption]);

  const selectedOfferSavingsCents = useMemo(() => getOfferSavingsCents(selectedOffer), [selectedOffer]);
  const selectedOfferBonusCredits = useMemo(() => getOfferBonusCredits(selectedOffer), [selectedOffer]);
  const selectedOfferOriginalSubtotalCents = useMemo(() => {
    const bundlePrice = selectedOffer ? Number((selectedOffer as any)?.bundle_price_cents ?? 0) : 0;
    if (Number.isFinite(bundlePrice) && bundlePrice > 0) return Math.round(bundlePrice);
    const unitPriceCents = meta?.creditOptions ? Number(meta.creditOptions[String(selectedCreditOptionInt)] ?? 0) : 0;
    if (Number.isFinite(unitPriceCents) && unitPriceCents > 0 && selectedCreditOptionInt > 0) {
      return Math.max(0, Math.round(unitPriceCents * selectedCreditOptionInt));
    }
    return 0;
  }, [selectedOffer, meta, selectedCreditOptionInt]);

	  const subtotalLabel = useMemo(() => {
	    const messageType = toLowerString(
	      (checkoutLetter as any)?.messageType ?? (checkoutLetter as any)?.data?.messageType ?? (checkoutLetter as any)?.order_type,
	    );
	    const isPostcard = messageType === "postcard";
	    if (isPostcard) return "Subtotal: Postcard";

	    if (!requirements.creditsPurchaseRequired || selectedCreditOptionInt <= 0) return "Subtotal";

	    if (selectedCreditOptionInt > 1) {
	      const totalCredits = selectedCreditOptionInt + Math.max(selectedOfferBonusCredits, 0);
	      return `Subtotal: (${totalCredits} credits)`;
	    }

    if (selectedCreditOptionInt === 1) {
      const shippingMethod = toLowerString(
        (checkoutLetter as any)?.shippingMethod ?? (checkoutLetter as any)?.data?.shippingMethod,
      );
      const photoTypeRaw = toLowerString(
        (checkoutLetter as any)?.photoType ??
          (checkoutLetter as any)?.photo_type ??
          (checkoutLetter as any)?.data?.photoType ??
          (checkoutLetter as any)?.data?.photo_type,
      );
      const photoType = photoTypeRaw === "3x5" ? "glossy" : photoTypeRaw;
      const photoLabel =
        shippingMethod === "emessage"
          ? "photo"
          : photoType === "glossy"
            ? "glossy photo"
            : "regular photo";
      return selectedOfferBonusCredits > 0
        ? `Subtotal: 1 message + 1 ${photoLabel} (+${selectedOfferBonusCredits} bonus)`
        : `Subtotal: 1 message + 1 ${photoLabel}`;
    }

    return "Subtotal";
  }, [checkoutLetter, requirements.creditsPurchaseRequired, selectedCreditOptionInt, selectedOfferBonusCredits]);

  const freeCreditsAmount = useMemo(() => {
    const toNumberSafe = (v: unknown): number => {
      const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : 0;
      return Number.isFinite(n) ? n : 0;
    };
    const toInt = (v: unknown): number => Math.max(0, Math.round(toNumberSafe(v)));
    return toInt(meta?.freeCreditsAmount ?? meta?.free_credits_amount);
  }, [meta]);

  const firstMessagePromoFreeCredits = useMemo(() => {
    const toNumberSafe = (v: unknown): number => {
      const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : 0;
      return Number.isFinite(n) ? n : 0;
    };
    const toInt = (v: unknown): number => Math.max(0, Math.round(toNumberSafe(v)));
    return toInt(meta?.firstMessagePromoFreeCredits ?? meta?.first_message_promo_free_credits);
  }, [meta]);

  const summaryMessageType = toLowerString(
    (checkoutLetter as any)?.messageType ?? (checkoutLetter as any)?.data?.messageType ?? (checkoutLetter as any)?.order_type,
  );
  const summaryShippingMethod = toLowerString(
    (checkoutLetter as any)?.shippingMethod ?? (checkoutLetter as any)?.data?.shippingMethod,
  );
  const summaryHasFacility = useMemo(() => {
    const eMessage = (checkoutLetter as any)?.eMessage ?? (checkoutLetter as any)?.data?.eMessage;
    if (!eMessage || typeof eMessage !== "object") return false;
    const id = (eMessage as any)?.id;
    const name = (eMessage as any)?.name;
    return (typeof id === "number" && id > 0) || (typeof id === "string" && id) || (typeof name === "string" && name);
  }, [checkoutLetter]);
  const summaryPricingMethod =
    summaryShippingMethod === "emessage" && summaryHasFacility ? "emessage" : "letter";
  const summaryIsPostcard = summaryMessageType === "postcard";
  const summaryIsPrintMail = summaryPricingMethod === "letter" && !summaryIsPostcard;
  const summaryIsCreditBundleFlow =
    !checkoutIsResend && (requirements.creditsPurchaseRequired || selectedCreditOptionInt > 0);
  const shouldShowSummary = summaryShippingMethod !== "emessage" || summaryHasFacility;

  // The first-message promo line is the server's call (meta.firstMessagePromo,
  // with the server's amount): a first-timer whose promo is suppressed (the
  // free-first regime's arm B) gets no line, and no client-side guess about
  // "first message" is made from hasSentLetter any more.
  const firstMessagePromo = toBool(meta?.firstMessagePromo ?? meta?.first_message_promo);
  const shouldShowFirstMessagePromoCredits =
    firstMessagePromo &&
    !freeFirstLetter &&
    payable &&
    totalCents > 0 &&
    firstMessagePromoFreeCredits > 0 &&
    !dpHasOffers &&
    summaryIsPrintMail;
  const shouldShowLegacyFreeCreditsPromo =
    !freeFirstLetter &&
    payable &&
    selectedCreditOptionInt > 0 &&
    freeCreditsAmount > 0 &&
    totalCents > 0 &&
    !dpHasOffers &&
    !shouldShowFirstMessagePromoCredits;
  const shouldShowFreeCreditsPromo = shouldShowFirstMessagePromoCredits || shouldShowLegacyFreeCreditsPromo;
  const freeCreditsPromoAmount = shouldShowFirstMessagePromoCredits ? firstMessagePromoFreeCredits : freeCreditsAmount;
  // The wording follows the server's flag on either path: "First message"
  // only when the server calls this a first-message promo.
  const freeCreditsPromoIsFirstMessage = firstMessagePromo;
  const processingFeeLabel = summaryIsCreditBundleFlow ? "Processing fee" : "Tax / Processing fee";

  const promoCreditsForTotal = shouldShowFreeCreditsPromo ? freeCreditsPromoAmount : 0;
  const totalCreditsSelected = useMemo(() => {
    if (selectedCreditOptionInt <= 0) return 0;
    // "Total credits" should reflect the purchased bundle plus any bonus/free credits shown in the summary.
    return selectedCreditOptionInt + Math.max(selectedOfferBonusCredits, 0) + Math.max(promoCreditsForTotal, 0);
  }, [selectedCreditOptionInt, selectedOfferBonusCredits, promoCreditsForTotal]);

  const sendConfirmVariant = useMemo(() => {
    const shippingMethod = toLowerString(
      (checkoutLetter as any)?.shippingMethod ?? (checkoutLetter as any)?.data?.shippingMethod,
    );
    const isEmessage = shippingMethod === "emessage";
    return isEmessage && !hasIncomingReplies ? "emessage-reminder" : "default";
  }, [checkoutLetter, hasIncomingReplies]);

	  const computeMinSatisfyingCreditOption = useCallback(
	    (args: { due: number; available: number; photoCount: number }): string | null => {
	      if (!checkoutLetter) return null;
	      const meta = (checkoutLetter as any)?.meta ?? (checkoutLetter as any)?.data?.meta ?? {};
	      const creditOptions = meta?.creditOptions ?? meta?.credit_options;
	      if (!creditOptions || typeof creditOptions !== "object") return null;
	      const sizes = Object.keys(creditOptions)
	        .map((k) => Number.parseInt(k, 10))
	        .filter((n) => Number.isFinite(n) && n > 0)
	        .sort((a, b) => a - b);
	      if (sizes.length === 0) return null;
	      const messageType = toLowerString(
	        (checkoutLetter as any)?.messageType ??
	          (checkoutLetter as any)?.data?.messageType ??
	          (checkoutLetter as any)?.order_type,
	      );
	      const isPostcard = messageType === "postcard";
	      const isSingleDisabled = !isPostcard && args.photoCount > 1;

	      const candidates = sizes.filter((s) => !(s === 1 && (isSingleDisabled || hideSingleCreditOption)));
	      const match =
	        candidates.find((s) => {
	          if (s <= 0) return false;
	          if (s === 1) return args.available + 1 >= args.due;
	          const offer = dpOffersByBundle?.[String(s)] ?? null;
	          const effective = getOfferEffectiveCredits({ bundleSize: s, offer });
	          return args.available + effective >= args.due;
	        }) ?? candidates[candidates.length - 1];
	      return match != null ? String(match) : null;
	    },
	    [checkoutLetter, dpOffersByBundle, hideSingleCreditOption],
	  );

  const creditAutoAdjustRef = useRef(false);
  const postcardCashCreditOptionFixRef = useRef(false);
  useEffect(() => {
    if (!checkoutLetter) return;
    if (!requirements.creditsPurchaseRequired) return;
    if (creditAutoAdjustRef.current) return;

    const due = requirements.creditsDue;
    const available = requirements.creditsAvailable;
    if (due <= 0) return;

    const photoCount = getSelectedPhotosCount(checkoutLetter);
    const selected = selectedCreditOptionInt;
    const messageType = toLowerString(
      (checkoutLetter as any)?.messageType ??
        (checkoutLetter as any)?.data?.messageType ??
        (checkoutLetter as any)?.order_type,
    );
    const isPostcard = messageType === "postcard";
    const isSingleDisabled = !isPostcard && photoCount > 1;

    // Legacy semantics: `creditOption === "1"` represents the "single message/postcard" purchase option,
    // which is valid (and should persist) as long as it isn't disabled (letters w/ >1 photo).
    // It is not a literal "1 credit" top-up amount.
    const isSinglePurchase = selected === 1 && !isSingleDisabled && !hideSingleCreditOption;

	    const effectiveSelected = selected > 1 ? selected + Math.max(selectedOfferBonusCredits, 0) : selected;
    const isSufficient =
      isSinglePurchase || (selected > 0 && selected !== 1 && available + effectiveSelected >= due);

    if (isSufficient) return;

    const desired = computeMinSatisfyingCreditOption({ due, available, photoCount });
    if (!desired) return;
    if (desired === selectedCreditOption) return;

    const desiredOfferRedemptionId = dpOffersByBundle?.[desired]?.redemption_id ?? null;
    creditAutoAdjustRef.current = true;
    void (async () => {
      try {
        await saveCredits({ creditOption: desired, offerRedemptionId: desiredOfferRedemptionId });
      } finally {
        creditAutoAdjustRef.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    (checkoutLetter as any)?.id,
    requirements.creditsPurchaseRequired,
    requirements.creditsDue,
    requirements.creditsAvailable,
    selectedCreditOption,
    selectedCreditOptionInt,
    dpOffersByBundle,
    hideSingleCreditOption,
  ]);

  useEffect(() => {
    if (!checkoutLetter || !checkoutUser) return;
    if (checkoutIsResend) return;
    if (hideSingleCreditOption) return;
    if (postcardCashCreditOptionFixRef.current) return;

    const meta = (checkoutLetter as any)?.meta ?? (checkoutLetter as any)?.data?.meta ?? {};
    const messageType = toLowerString(
      (checkoutLetter as any)?.messageType ?? (checkoutLetter as any)?.data?.messageType ?? (checkoutLetter as any)?.order_type,
    );
    const isPostcard = messageType === "postcard";
    const firstMessage = meta?.firstMessage === true || meta?.first_message === true;
    const isNewUser = checkoutUser.hasSentLetter === false;
    const isFirstMessagePostcardCashPricing = isPostcard && isNewUser && firstMessage;

    if (!isFirstMessagePostcardCashPricing) return;
    if (selectedCreditOptionInt === 1) return;

    // Backend cash pricing for first-time postcards is independent of credit bundles. Persist "1" to avoid
    // accidentally treating this as a credit purchase in invoicing (which could grant extra credits).
    postcardCashCreditOptionFixRef.current = true;
    void (async () => {
      try {
        await saveCredits({ creditOption: "1", offerRedemptionId: null });
      } finally {
        postcardCashCreditOptionFixRef.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    (checkoutLetter as any)?.id,
    checkoutUser?.hasSentLetter,
    checkoutIsResend,
    selectedCreditOptionInt,
    hideSingleCreditOption,
  ]);

  return (
    <div className="pm-checkout-pinned rounded-2xl border bg-card mb-2 px-4 py-3 shadow-sm [contain:layout_paint]">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-medium text-sm">Ready to Send?</div>
          <div className="mt-0.5 text-muted-foreground text-xs">
            Confirm delivery details and send your message.
          </div>
          {checkoutLetterLoading ? (
            <div className="mt-0.5 text-muted-foreground text-xs">Loading draft details…</div>
          ) : null}
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={() => setConfirmOpen(true)}>
          Cancel
        </Button>
      </div>

      {paymentResumeError ? (
        <div className="ph-mask mt-3 rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {paymentResumeError}
        </div>
      ) : null}
      {returnAddressSendError ? (
        <div className="mt-3 rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {returnAddressSendError}
        </div>
      ) : null}
      {resendCreditsError ? (
        <div className="mt-3 rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {resendCreditsError}
        </div>
      ) : null}
      {sendError ? (
        <div className="ph-mask mt-3 rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {sendError}
        </div>
      ) : null}

      <div className="mt-3 space-y-2" ref={checklistParent}>
        {checklistIsLoading ? (
          <div aria-hidden="true" className="space-y-2">
            {Array.from({ length: checklistSkeletonCount }).map((_, idx) => (
              <div
                key={idx}
                className="flex w-full items-start gap-2 rounded-xl px-2 py-1.5"
              >
                <span className="mt-0.5 text-muted-foreground/40">
                  <SquareIcon className="size-4" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="h-4 w-44 animate-pulse rounded bg-muted/60" />
                </div>
                <span className="mt-0.5 text-muted-foreground/30">
                  <ChevronRightIcon className="size-4" aria-hidden="true" />
                </span>
              </div>
            ))}
          </div>
        ) : (
          requirements.sections.map((s) => (
            <button
              key={s.key}
              type="button"
              className="flex w-full items-start gap-2 rounded-xl px-2 py-1.5 text-left hover:bg-muted/20 disabled:opacity-60 disabled:hover:bg-transparent"
              disabled={!canOpenRow(s.key)}
              onClick={() => {
                if (s.key === "postcard_front") openPostcardFrontEditor();
                if (s.key === "delivery") openDeliverySheet("delivery");
                if (s.key === "facility") openDeliverySheet("facility");
                if (s.key === "return_address") openReturnAddressSheet();
                if (s.key === "photo_type") openPhotoTypeSheet();
                if (s.key === "credits") openCreditsSheet();
                if (s.key === "payment") openPaymentSheet();
              }}
              data-testid={`checkout-row-${s.key}`}
            >
              <span className="mt-0.5 text-muted-foreground">
                {s.complete ? (
                  <CheckCircle2Icon className="size-4 text-emerald-600" aria-hidden="true" />
                ) : (
                  <SquareIcon className="size-4" aria-hidden="true" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm">
                  {s.title}
                  {s.detail ? (
                    <span className={cn("text-muted-foreground", s.key === "payment" && "ph-mask")}>
                      {" · "}
                      {s.detail}
                    </span>
                  ) : null}
                </div>
              </div>
              {canOpenRow(s.key) ? (
                <span className="mt-0.5 text-muted-foreground">
                  <ChevronRightIcon className="size-4" aria-hidden="true" />
                </span>
              ) : null}
            </button>
          ))
        )}
      </div>

      {shouldShowSummary ? (
        <div className="mt-4 rounded-xl bg-muted/30 px-3 py-2">
          {(() => {
            const creditsRequired = requirements.creditsDue;
            const creditsAvailable = requirements.creditsAvailable;

            const showCreditSummary =
              !requirements.paymentRequired && creditsRequired > 0 && creditsAvailable >= creditsRequired;

            if (showCreditSummary) {
              return (
                <>
                  <div className="text-xs font-medium text-muted-foreground">Credit summary</div>
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Credits available</span>
                    <span>
                      {creditsAvailable} {creditsAvailable === 1 ? "credit" : "credits"}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Credits required</span>
                    <span>
                      {creditsRequired} {creditsRequired === 1 ? "credit" : "credits"}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-sm font-medium">
                    <span>Total</span>
                    <span>{formatUsd(totalCents)}</span>
                  </div>
                </>
              );
            }

            return (
              <>
                <div className="text-xs font-medium text-muted-foreground">Order summary</div>
                <div className="mt-1 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground" data-testid="checkout-summary-subtotal-label">
                    {subtotalLabel}
                  </span>
                  <span data-testid="checkout-summary-subtotal-value">
                    {formatUsd(payable ? (recoveryDiscountShown ? recoveryApplied!.regularPriceCents! : subtotalCents) : 0)}
                  </span>
                </div>
                {payable && recoveryDiscountShown ? (
                  <div className="mt-1 flex items-center justify-between text-sm" data-testid="checkout-recovery-discount-row">
                    <span className="text-emerald-800">{recoveryApplied!.discountLabel}</span>
                    <span className="text-emerald-800">- {formatUsd(recoveryApplied!.savingsCents!)}</span>
                  </div>
                ) : null}
                {shouldShowFreeCreditsPromo ? (() => {
                  const isFirstMessage = freeCreditsPromoIsFirstMessage;
                  return (
                    <div className="mt-1 text-sm">
                      <div className="text-emerald-800">
                        {isFirstMessage ? "First message:" : "You've earned"} {freeCreditsPromoAmount} extra credits
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {isFirstMessage ? "Send your first message and get free credits." : "Your credits will be added after your message is sent."}
                      </div>
                    </div>
                  );
                })() : null}
                {winBackLetter && !freeFirstLetter ? (
                  <div className="mt-1 text-sm">
                    <div className="text-emerald-800">Your next message is free</div>
                  </div>
                ) : null}
                {payable && requirements.creditsPurchaseRequired && selectedCreditOptionInt > 0 && selectedOfferSavingsCents > 0 ? (
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Credits</span>
                    <span>{formatUsd(selectedOfferOriginalSubtotalCents)}</span>
                  </div>
                ) : null}
                {payable && selectedOfferSavingsCents > 0 ? (
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span className="text-emerald-800">Promo Discount</span>
                    <span className="text-emerald-800">- {formatUsd(selectedOfferSavingsCents)}</span>
                  </div>
                ) : null}
                {payable && selectedOfferBonusCredits > 0 ? (
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span className="text-emerald-800">Bonus credits</span>
                    <span className="text-emerald-800">
                      + {selectedOfferBonusCredits} {selectedOfferBonusCredits === 1 ? "credit" : "credits"}
                    </span>
                  </div>
                ) : null}
                {payable && requirements.creditsPurchaseRequired && selectedCreditOptionInt > 1 ? (
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span className="text-emerald-800">Total credits</span>
                    <span className="text-emerald-800">
                      {totalCreditsSelected} {totalCreditsSelected === 1 ? "credit" : "credits"}
                    </span>
                  </div>
                ) : null}
                {payable && processingFeeCents > 0 ? (
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{processingFeeLabel}</span>
                    <span>{formatUsd(processingFeeCents)}</span>
                  </div>
                ) : null}
                <div className="mt-2 flex items-center justify-between text-sm font-medium">
                  <span>Total</span>
                  <span>{formatUsd(totalCents)}</span>
                </div>
                {recoveryReminder ? (
                  <button
                    type="button"
                    className="mt-2 flex w-full items-center gap-1.5 text-left text-sm font-medium text-[#00796b] disabled:opacity-70"
                    onClick={handleRecoveryReminderTap}
                    disabled={recoveryReminderPending}
                    data-testid="checkout-recovery-reminder"
                  >
                    {recoveryReminderPending ? (
                      <RefreshCwIcon className="size-3.5 shrink-0 animate-spin" aria-hidden="true" />
                    ) : (
                      <TagIcon className="size-3.5 shrink-0" aria-hidden="true" />
                    )}
                    <span className="min-w-0 flex-1">{recoveryReminder.label}</span>
                    <span className="shrink-0 font-semibold">Apply</span>
                  </button>
                ) : null}
                {payable && recoveryApplied ? (
                  <div
                    className="mt-2 flex items-center gap-1.5 text-sm font-medium text-[#00796b]"
                    data-testid="checkout-recovery-applied"
                  >
                    <TagIcon className="size-3.5 shrink-0" aria-hidden="true" />
                    <span className="min-w-0 flex-1">{recoveryApplied.label}</span>
                  </div>
                ) : null}
                {recoveryNotice ? (
                  <div className="mt-2 text-xs text-muted-foreground" data-testid="checkout-recovery-notice">
                    {recoveryNotice}
                  </div>
                ) : null}
                {requirements.paymentRequired &&
                !!((checkoutLetter as any)?.payment ?? (checkoutLetter as any)?.data?.payment) ? (
                  <div className="mt-2 text-xs text-muted-foreground" data-testid="charge-timing-reassurance">
                    Your payment method may be authorized but will not be charged until your message is
                    approved.
                  </div>
                ) : null}
                {requirements.creditsPurchaseRequired ? (
                  <div className="mt-2 text-xs text-muted-foreground">
                    {selectedCreditOptionInt === 1
                      ? "Credits: 1 message + photo"
                      : `Credits: ${requirements.creditsDue} due / ${requirements.creditsAvailable} available`}
                  </div>
                ) : null}
              </>
            );
          })()}
        </div>
      ) : null}

      <div className="mt-4 flex w-full flex-col gap-2">
        <Button
          type="button"
          className="w-full border-sky-200 bg-sky-50 text-sky-900 shadow-sm hover:bg-sky-100 hover:text-sky-900"
          variant="outline"
          onClick={() => setPreviewOpen(true)}
          disabled={checklistIsLoading}
          data-testid="checkout-preview"
        >
          <span className="grid w-full grid-cols-[1fr_auto_1fr] items-center">
            <span className="flex justify-start">
              <EyeIcon aria-hidden="true" className="h-4 w-4 opacity-70" />
            </span>
            <span className="justify-self-center">Preview message</span>
            <span className="flex justify-end" />
          </span>
        </Button>
        {(() => {
          const hasBlockingReturnAddressError = !!returnAddressSendError;
          const hasNextRequired = !!requirements.nextRequiredKey;
          const canContinue = hasNextRequired || hasBlockingReturnAddressError;
          const disabled =
            checklistIsLoading ||
            isSending ||
            resendCreditsClearing ||
            !!resendCreditsError;
          const variant: "default" = "default";
          const label = canContinue
            ? hasBlockingReturnAddressError
              ? "Fix return address"
              : "Continue"
            : isSending
              ? "Sending…"
              : "Send now";
          const onClick = canContinue ? openNextRequired : () => setSendConfirmOpen(true);
          const buttonClassName = canContinue
            ? "shadow-sm"
            : "bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 hover:text-white focus-visible:ring-emerald-500/30";
          return (
            <Button
              type="button"
              className={cn("w-full", buttonClassName)}
              variant={variant}
              onClick={onClick}
              disabled={disabled}
              data-testid={canContinue ? "checkout-continue" : "checkout-send-now"}
            >
              <span className="grid w-full grid-cols-[1fr_auto_1fr] items-center">
                <span className="flex justify-start" />
                <span className="justify-self-center">{label}</span>
                <span className="flex justify-end">
                  {canContinue ? (
                    <ChevronRightIcon aria-hidden="true" className="h-4 w-4 opacity-80" />
                  ) : (
                    <CheckCircle2Icon aria-hidden="true" className="h-4 w-4 opacity-90" />
                  )}
                </span>
              </span>
            </Button>
          );
        })()}
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen} modal={false}>
        <DialogContent className="flex h-[calc(100vh-2rem)] supports-[height:100dvh]:h-[calc(100dvh-2rem)] max-h-[calc(100vh-2rem)] supports-[height:100dvh]:max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-none flex-col !gap-0 !p-0 [&>button.absolute]:hidden">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <DialogTitle className="font-semibold text-base">Preview message</DialogTitle>
            <DialogDescription className="sr-only">Preview your message before sending.</DialogDescription>
            <DialogClose asChild>
              <Button type="button" variant="secondary" size="sm">
                Done
              </Button>
            </DialogClose>
          </div>
          <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
            {preview ? (
              <div className="pm-draft-card mx-auto flex w-full max-w-(--thread-max-width) flex-col gap-2 rounded-2xl border border-border bg-sky-50 px-4 py-3 text-[#2a2a2a] shadow-sm dark:bg-sky-950/30">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground text-xs font-medium">
                    {preview.kind === "postcard" ? "Postcard draft" : "Letter draft"}
                  </span>
                  {checkoutIsResend ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                      <RotateCcwIcon aria-hidden="true" className="h-3 w-3" />
                      Resend
                    </span>
                  ) : null}
                </div>

                {preview.text ? (
                  <div className="ph-no-capture whitespace-pre-wrap">{preview.text}</div>
                ) : (
                  <div className="text-muted-foreground text-sm">No message text.</div>
                )}

                {preview.kind === "postcard" && preview.postcardFrontUrl ? (
                  <div className="ph-no-capture mt-2">
                    <img
                      src={preview.postcardFrontUrl}
                      alt="Postcard front"
                      className="h-48 w-full rounded-md object-cover [image-orientation:from-image]"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                ) : null}

                {preview.photos.length > 0 ? (
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {preview.photos.map((p, idx) => (
                      <button
                        key={`${p.url}-${idx}`}
                        type="button"
                        className="ph-no-capture relative aspect-square overflow-hidden rounded-md border border-border bg-white"
                        onClick={() =>
                          photoViewer.open(preview.photos, idx, {
                            initialView: preview.photos.length > 1 ? "grid" : "viewer",
                          })
                        }
                        aria-label={`View draft photo ${idx + 1}`}
                      >
                        <img
                          src={p.url}
                          alt={p.filename || "Draft photo"}
                          className="h-full w-full object-cover [image-orientation:from-image]"
                          loading="lazy"
                          decoding="async"
                        />
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="text-muted-foreground text-sm">Loading preview…</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open && cancelPending) return;
          setConfirmOpen(open);
        }}
        modal={false}
      >
        <DialogContent className="w-[calc(100vw-2rem)] max-w-sm !p-4">
          <DialogTitle className="font-semibold text-base">Cancel sending?</DialogTitle>
          <DialogDescription className="sr-only">Confirm cancelling checkout.</DialogDescription>
          <div className="mt-2 text-muted-foreground text-sm">
            You can continue editing your draft afterwards.
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={cancelPending}>
                Keep going
              </Button>
            </DialogClose>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void confirmCancelCheckout()}
              disabled={cancelPending}
              data-testid="checkout-cancel-confirm"
            >
              {cancelPending ? "Cancelling…" : "Cancel"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={sendConfirmOpen} onOpenChange={setSendConfirmOpen} modal={false}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-sm !p-4">
          <DialogTitle className="font-semibold text-base">
            Send Message?
          </DialogTitle>
          <DialogDescription className="sr-only">Confirm sending this message.</DialogDescription>
          <div className="mt-2 text-muted-foreground text-sm">
            {sendConfirmVariant === "emessage-reminder" ? (
              <>
                <div>E-messages may be reviewed by the facility.</div>
                <div className="mt-2">
                  If your message is not approved for E-message delivery, it will automatically be sent via USPS per facility requirements.
                </div>
                <div className="mt-2">
                  Do not send anything inappropriate or your message may be rejected.
                </div>
                <div className="mt-2">You will be notified by email when your message is delivered.</div>
              </>
            ) : (
              <>Are you ready to send this message?</>
            )}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={isSending}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="button"
              onClick={() => {
                setSendConfirmOpen(false);
                void sendNow();
              }}
              disabled={isSending || resendCreditsClearing || !!resendCreditsError}
            >
              {sendConfirmVariant === "emessage-reminder" ? "OK, Send" : "Yes, send"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <PhoneVerificationSheet
        open={phoneVerifyOpen}
        onOpenChange={setPhoneVerifyOpen}
        onVerified={async () => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["pmreact", "auth", "validate-token"] }),
            queryClient.refetchQueries({ queryKey: ["pmreact", "auth", "validate-token"] }),
          ]);
          await sendNow({ skipVerify: true });
        }}
      />

      <CreditsSheet
        open={creditsSheetOpen}
        onOpenChange={setCreditsSheetOpen}
        checkoutLetter={checkoutLetter}
        creditsDue={requirements.creditsDue}
        creditsAvailable={requirements.creditsAvailable}
        offersByBundle={dpOffersByBundle}
        hideSingleCreditOption={hideSingleCreditOption}
        onSave={saveCredits}
      />

      <DeliveryFacilitySheet
        open={deliverySheetOpen}
        onOpenChange={setDeliverySheetOpen}
        initialStep={deliverySheetStep}
        checkoutLetter={checkoutLetter}
        lockShippingMethod={checkoutIsResend}
        defaultState={typeof (penmate as any)?.state === "string" ? (penmate as any).state : null}
        onSave={saveDelivery}
      />

      <ReturnAddressSheet
        open={returnAddressSheetOpen}
        onOpenChange={setReturnAddressSheetOpen}
        checkoutLetter={checkoutLetter}
        onSave={saveReturnAddress}
      />

      <PhotoTypeSheet
        open={photoTypeSheetOpen}
        onOpenChange={setPhotoTypeSheetOpen}
        checkoutLetter={checkoutLetter}
        noPhotosEligible={noPhotosEligible}
        noPhotosSelected={noPhotosSelected}
        onSave={savePhotoType}
      />

      <PaymentSheet
        open={paymentSheetOpen}
        onOpenChange={(open: boolean) => {
          paymentSheetOpenRef.current = open;
          setPaymentSheetOpen(open);
        }}
        checkoutLetter={checkoutLetter}
        onSave={savePayment}
        saveBillingActive={(checkoutUser as any)?.saveBilling === true}
        onDismiss={handlePaymentSheetDismiss}
      />

      <CheckoutRecoverySheet
        open={recoverySheetOpen}
        offer={recoveryOffer}
        rescueOnly={recoveryRescueOnly}
        mode={recoveryMode}
        onDisplayed={handleRecoveryDisplayed}
        onAccept={handleRecoveryAccept}
        onRescue={handleRecoveryRescue}
        onNotNow={handleRecoveryNotNow}
      />
    </div>
  );
};

const ThreadScrollToBottom: FC = () => {
  return (
    <ThreadPrimitive.ScrollToBottom asChild>
      <TooltipIconButton
        tooltip="Scroll to bottom"
        variant="outline"
        className="aui-thread-scroll-to-bottom -top-12 absolute z-10 self-center rounded-full p-4 disabled:invisible dark:bg-background dark:hover:bg-accent"
      >
        <ArrowDownIcon />
      </TooltipIconButton>
    </ThreadPrimitive.ScrollToBottom>
  );
};

const ThreadLoading: FC = () => {
  // Use the shared web component for consistent skeleton across Angular and React.
  // The element is registered by the host page via `skeleton-loader.js`.
  // Use absolute positioning to fill the viewport, ignoring the parent's pt-4 px-4 padding
  return (
    <div className="absolute inset-0">
      {/* @ts-expect-error - pm-skeleton-loader is a custom element */}
      <pm-skeleton-loader loading-text="Loading…" />
    </div>
  );
};

const ThreadEmpty: FC = () => {
  const draftUi = useDraftUi();
  const { setComposeMode } = useComposeMode();

  const jumpToComposer = (mode: "letter" | "postcard") => {
    setComposeMode(mode);
    requestAnimationFrame(() => {
      draftUi.scrollToBottom();
      draftUi.focusComposer();
    });
  };

  return (
    <div className="mx-auto my-auto flex w-full max-w-(--thread-max-width) grow flex-col items-center justify-center px-4">
      <div className="w-full rounded-2xl border border-border bg-card p-5 shadow-sm">
        <h1 className="text-base font-semibold text-[hsl(var(--pm-messenger-heading))]">Send your first message</h1>
        <p className="mt-1 text-muted-foreground text-sm">
          Start the conversation with a message, postcard, or photos.
        </p>

        <div className="mt-4 flex flex-col gap-3">
          <button
            type="button"
            className="flex w-full items-start gap-3 rounded-xl border border-input bg-muted/10 px-4 py-3 text-left transition-colors hover:bg-muted/20"
            onClick={() => jumpToComposer("letter")}
          >
            <MessageCircleIcon aria-hidden="true" className="mt-0.5 h-5 w-5 text-[hsl(var(--pm-messenger-heading))]" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-[#222]">Write a message</div>
              <div className="mt-0.5 text-xs text-muted-foreground">Type below, then review and send.</div>
            </div>
          </button>

          <button
            type="button"
            className="flex w-full items-start gap-3 rounded-xl border border-input bg-muted/10 px-4 py-3 text-left transition-colors hover:bg-muted/20"
            onClick={() => jumpToComposer("letter")}
          >
            <ImagePlusIcon aria-hidden="true" className="mt-0.5 h-5 w-5 text-[hsl(var(--pm-messenger-heading))]" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-[#222]">Add photos</div>
              <div className="mt-0.5 text-xs text-muted-foreground">Tap the photo button in the composer.</div>
            </div>
          </button>

          <button
            type="button"
            className="flex w-full items-start gap-3 rounded-xl border border-input bg-muted/10 px-4 py-3 text-left transition-colors hover:bg-muted/20"
            onClick={() => jumpToComposer("postcard")}
          >
            <ImageIcon aria-hidden="true" className="mt-0.5 h-5 w-5 text-[hsl(var(--pm-messenger-heading))]" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-[#222]">Send a postcard</div>
              <div className="mt-0.5 text-xs text-muted-foreground">Choose a design first, then add your note.</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

const DraftPhotosStrip: FC<{
  draftId: string;
  photos: readonly MediaItem[];
  uploads?: readonly DraftPhotoUpload[];
  onRemoveUpload?: (id: string) => void;
}> = ({
  draftId,
  photos,
  uploads,
  onRemoveUpload,
}) => {
  const draftUi = useDraftUi();
  const photoViewer = usePhotoViewer();
  const checkoutActive = useCheckoutActive();

  const uploadItems = uploads ?? [];
  if (!photos.length && !uploadItems.length) return null;

  return (
    <div className="ph-no-capture pm-draft-photos-strip mb-2 flex w-full flex-row items-center gap-2 overflow-x-auto px-1.5 pt-0.5 pb-1 transform-gpu will-change-transform">
      {uploadItems.map((u) => (
        <div
          key={u.id}
          className="relative size-14 flex-none overflow-hidden rounded-[14px] border bg-muted transform-gpu"
          data-testid="draft-photo-upload"
          aria-label={`Uploading ${u.filename}`}
        >
          {u.previewUrl ? (
            <img
              src={u.previewUrl}
              alt={u.filename}
              className="block h-full w-full object-cover opacity-80 [image-orientation:from-image]"
              loading="eager"
              decoding="async"
            />
          ) : (
            <div className="h-full w-full bg-muted" />
          )}
          <div className="absolute inset-0 bg-black/10" />
          {u.status === "error" ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-1 text-center text-[10px] text-white">
              <div className="font-medium">Failed</div>
              <button
                type="button"
                className="rounded bg-white/20 px-1.5 py-0.5 text-[10px] hover:bg-white/30"
                onClick={() => onRemoveUpload?.(u.id)}
                disabled={checkoutActive}
              >
                Dismiss
              </button>
            </div>
          ) : (
            <>
              <div className="absolute bottom-0 left-0 right-0 px-1 pb-1">
                <div
                  className="h-1.5 w-full overflow-hidden rounded-full bg-white/30"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={u.percent}
                  data-testid="draft-photo-upload-progress"
                >
                  <div
                    className="h-full rounded-full bg-white/80 transition-[width]"
                    style={{ width: `${Math.max(0, Math.min(100, u.percent))}%` }}
                  />
                </div>
              </div>
              <div className="absolute left-1 top-1 rounded bg-black/35 px-1 py-0.5 text-[10px] text-white">
                {u.status === "queued"
                  ? "Queued"
                  : u.status === "preparing"
                    ? "Preparing"
                    : u.status === "finalizing"
                      ? "Finalizing"
                      : `${u.percent}%`}
              </div>
            </>
          )}
        </div>
      ))}
      {photos.map((p, idx) => (
        <button
          key={`${p.url}:${idx}`}
          type="button"
          className="relative size-14 flex-none overflow-hidden rounded-[14px] border bg-muted transform-gpu"
          onClick={() =>
            photoViewer.open(photos, idx, {
              initialView: photos.length > 1 ? "grid" : "viewer",
              canRemove: !checkoutActive,
              onRemove: checkoutActive
                ? undefined
                : (_photo, removeIdx) => draftUi.removeDraftPhoto(draftId, removeIdx),
            })
          }
          aria-label={`View draft photo ${idx + 1}`}
        >
          <img
            src={p.url}
            alt={p.filename || `Draft photo ${idx + 1}`}
            className="block h-full w-full object-cover [image-orientation:from-image]"
            loading="eager"
            decoding="async"
          />
        </button>
      ))}
    </div>
  );
};

const DraftPostcardFrontStrip: FC<{ frontUrl: string }> = ({ frontUrl }) => {
  const draftUi = useDraftUi();
  const checkoutActive = useCheckoutActive();

  if (!frontUrl) return null;

  return (
    <div className="ph-no-capture pm-draft-postcard-front-strip mb-2 flex w-full flex-row items-center gap-2 px-1.5 pt-0.5 pb-1">
      <button
        type="button"
        className="relative h-14 w-20 flex-none overflow-hidden rounded-[14px] border bg-muted"
        onClick={() => {
          if (checkoutActive) return;
          if (!draftUi.editingDraftId) return;
          draftUi.openPostcardEditor(draftUi.editingDraftId);
        }}
        aria-label="Edit postcard front"
      >
        <img
          src={frontUrl}
          alt="Postcard front"
          className="block h-full w-full object-cover [image-orientation:from-image]"
          loading="eager"
          decoding="async"
        />
      </button>
        <div className="text-muted-foreground text-xs">Postcard front</div>
    </div>
  );
};

const Composer: FC<{
  composerInputRef: React.RefObject<HTMLTextAreaElement | null>;
  draftSaveError: string | null;
  isThreadLoading: boolean;
  existingDraft: { id: string; type: "letter" | "postcard"; raw: any } | null;
  existingDraftsByType: {
    letter: { id: string; type: "letter"; raw: any } | null;
    postcard: { id: string; type: "postcard"; raw: any } | null;
  } | null;
  penmateId: string | null;
  penmate?: any;
  sessionId?: number;
  messageType?: string;
  checkoutDraftId: string | null;
  checkoutDisabled: boolean;
  onEnterCheckout?: (draftId: string) => void;
}> = ({
  composerInputRef,
  draftSaveError,
  isThreadLoading,
  existingDraft,
  existingDraftsByType,
  penmateId,
  penmate,
  sessionId,
  messageType,
  checkoutDraftId,
  checkoutDisabled,
  onEnterCheckout,
}) => {
  const draftUi = useDraftUi();
  const api = useAssistantApi();
  const queryClient = useQueryClient();
  const { composeMode, setComposeMode } = useComposeMode();
  const routeMessageType = typeof messageType === "string" ? messageType.toLowerCase().trim() : "";
  const isEMessageMode = routeMessageType === "emessage";
  const checkoutActive = !!checkoutDraftId;
  const editingDraftId = draftUi.editingDraftId;
  const editingDraft = editingDraftId ? draftUi.getDraftOverrideById(editingDraftId) : null;
  const composerText = useAssistantState(({ thread }) => thread.composer.text);
  const existingDraftMessageText = useMemo(() => {
    if (!existingDraft) return "";
    const draftId = String(existingDraft.id);
    const override = draftUi.getDraftOverrideById(draftId);
    const resolved = resolveDraftMessageText(
      override?.payloadBase ?? existingDraft.raw?.data,
      existingDraft.raw,
    );
    return clampDraftText({
      messageType: existingDraft.type,
      text: resolved.text,
    });
  }, [draftUi, existingDraft]);
  const existingDraftHasMessage = !!existingDraftMessageText.trim();
  const editingMessageType = useMemo(() => {
    const raw = (editingDraft?.payloadBase as any)?.messageType;
    return typeof raw === "string" && raw ? raw.toLowerCase() : "";
  }, [editingDraft?.payloadBase]);
  const postcardFrontUrl = useMemo(
    () => getDraftPostcardFrontUrl(editingDraft?.payloadBase),
    [editingDraft?.payloadBase],
  );
  const [checkoutStartErrorState, setCheckoutStartErrorState] = useState<{
    draftId: string | null;
    textSignature: string;
    message: string;
  } | null>(null);
  const [sendAttemptedDraftId, setSendAttemptedDraftId] = useState<string | null>(null);
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const [switchConfirmOpen, setSwitchConfirmOpen] = useState(false);
  const [switchTargetMode, setSwitchTargetMode] = useState<"letter" | "postcard">("letter");
  const [switchTransferText, setSwitchTransferText] = useState<string>("");
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const [isPostcardPickerLoading, setIsPostcardPickerLoading] = useState(false);
  const [postcardPickerError, setPostcardPickerError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [photoUploadProgress, setPhotoUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [photoUploads, setPhotoUploads] = useState<readonly DraftPhotoUpload[]>([]);
  const [photoUploadError, setPhotoUploadError] = useState<string | null>(null);
  const [photoUploadWarning, setPhotoUploadWarning] = useState<string | null>(null);
  const [expandedEditorOpen, setExpandedEditorOpen] = useState(false);
  const expandedEditorTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const expandedEditorCreatePromiseRef = useRef<Promise<DraftCreateResult> | null>(null);
  const expandedEditorLatestTextRef = useRef<string>("");
  const latestComposerValueRef = useRef<string>("");
  const suppressDraftSyncRef = useRef(false);
  const activeDraftId = editingDraftId
    ? String(editingDraftId)
    : existingDraft?.id != null
      ? String(existingDraft.id)
      : null;
  const existingDraftIdForType = existingDraft?.id ?? null;
  const existingDraftIsResend = !!(existingDraft && getIsResend(existingDraft.raw));
  const editingDraftIsResend = !!(editingDraft && getIsResend(editingDraft.payloadBase));
  const resendDraftNeedsMessage = existingDraftIsResend && !existingDraftHasMessage;
  const activeDraftMessageType = editingDraftId
    ? toComposerMode(editingMessageType || composeMode)
    : existingDraft?.type ?? composeMode;
  const activeDraftText = editingDraftId
    ? clampDraftText({
        messageType: activeDraftMessageType,
        text: typeof composerText === "string" ? composerText : "",
      })
    : existingDraft
      ? existingDraftMessageText
      : clampDraftText({
          messageType: activeDraftMessageType,
          text: typeof composerText === "string" ? composerText : "",
        });
  const showBlankDraftError =
    !!activeDraftId && sendAttemptedDraftId === activeDraftId && !activeDraftText.trim();
  const blankDraftErrorText =
    existingDraftIsResend
      ? "This resend is missing message text. Edit it to send."
      : activeDraftMessageType === "postcard"
      ? "Add a message to send your postcard."
      : "Add a message before sending.";
  const checkoutStartErrorMessage =
    checkoutStartErrorState &&
    checkoutStartErrorState.draftId === activeDraftId &&
    checkoutStartErrorState.textSignature === activeDraftText.trim()
      ? checkoutStartErrorState.message
      : null;
  const checkoutErrorMessage = showBlankDraftError ? blankDraftErrorText : checkoutStartErrorMessage;
  const withPenmatePayload = useCallback(
    (payload: Record<string, unknown>): Record<string, unknown> =>
      attachPenmatePayload(payload, penmate, penmateId),
    [penmate, penmateId],
  );
  const trackDraftCreated = useCallback(
    (args: { saved: RawMessage; messageType?: string; shippingMethod?: string }) => {
      const { saved, messageType, shippingMethod } = args;
      const letterIdValue = (saved as any)?.id ?? (saved as any)?.data?.id ?? null;
      if (letterIdValue == null) return;
      const payload = {
        letter_id: letterIdValue,
        ...buildPenmateMetricsPayload({ penmate, letter: saved, penmateId }),
        message_type:
          (saved as any)?.messageType ??
          (saved as any)?.data?.messageType ??
          messageType ??
          null,
        shipping_method:
          (saved as any)?.shippingMethod ??
          (saved as any)?.data?.shippingMethod ??
          shippingMethod ??
          null,
      };
      void trackMetric("draft_created", payload, { sessionId });
      trackReplayStageFromMetricPayload("message_created", "draft_created", payload);
    },
    [penmate, penmateId, sessionId],
  );

  // Keep the draft bubble state in sync with the composer while editing,
  // and persist text changes using the legacy debounced endpoint.
  useEffect(() => {
    if (!editingDraftId || !editingDraft) return;
    if (suppressDraftSyncRef.current) {
      suppressDraftSyncRef.current = false;
      return;
    }
    const mt = toComposerMode(editingMessageType);
    const clamped = clampDraftText({ messageType: mt, text: composerText });
    draftUi.setDraftText(editingDraftId, clamped);
    draftUi.scheduleDraftTextSave(editingDraftId, clamped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingDraftId, composerText]);

  useEffect(() => {
    if (!sendAttemptedDraftId) return;
    if (!activeDraftId || sendAttemptedDraftId !== activeDraftId) {
      setSendAttemptedDraftId(null);
      return;
    }
    if (activeDraftText.trim()) {
      setSendAttemptedDraftId(null);
    }
  }, [activeDraftId, activeDraftText, sendAttemptedDraftId]);

  useEffect(() => {
    if (!checkoutStartErrorState) return;
    if (checkoutStartErrorMessage) return;
    setCheckoutStartErrorState(null);
  }, [checkoutStartErrorMessage, checkoutStartErrorState]);

  const editingShippingMethod = useMemo(() => {
    const raw = (editingDraft?.payloadBase as any)?.shippingMethod;
    return typeof raw === "string" && raw ? raw.toLowerCase() : "";
  }, [editingDraft?.payloadBase]);
  const maxSelectedPhotos =
    isEMessageMode || editingShippingMethod === "emessage"
      ? EMESSAGE_MAX_SELECTED_PHOTOS
      : DEFAULT_MAX_SELECTED_PHOTOS;
  const isUploadingPhotos =
    (!!photoUploadProgress && photoUploadProgress.done < photoUploadProgress.total) ||
    photoUploads.some((u) => u.status !== "error");

  const photoUploadsRef = useRef(photoUploads);
  useEffect(() => {
    const prev = photoUploadsRef.current;
    const next = photoUploads;
    const nextIds = new Set(next.map((u) => u.id));
    for (const u of prev) {
      if (!nextIds.has(u.id)) {
        try {
          URL.revokeObjectURL(u.previewUrl);
        } catch {
          // ignore
        }
      }
    }
    photoUploadsRef.current = next;
  }, [photoUploads]);

  useEffect(() => {
    return () => {
      for (const u of photoUploadsRef.current) {
        try {
          URL.revokeObjectURL(u.previewUrl);
        } catch {
          // ignore
        }
      }
    };
  }, []);

  const emessageDraftAppliedRef = useRef<Set<string>>(new Set());
  const ensureDraftIsEMessage = useCallback(
    async (draft: { id: string; type: "letter" | "postcard"; raw: any } | null): Promise<boolean> => {
      if (!draft) return false;
      if (!isEMessageMode) return true;
      if (draft.type !== "letter") return false;
      if (checkoutActive) return true;

      const draftId = String(draft.id);
      if (emessageDraftAppliedRef.current.has(draftId)) return true;

      try {
        const override = draftUi.ensureDraftOverrideFromRaw(draftId, draft.raw);
        const current =
          typeof (override.payloadBase as any)?.shippingMethod === "string"
            ? String((override.payloadBase as any).shippingMethod).toLowerCase()
            : typeof (draft.raw as any)?.shippingMethod === "string"
              ? String((draft.raw as any).shippingMethod).toLowerCase()
              : typeof (draft.raw as any)?.data?.shippingMethod === "string"
                ? String((draft.raw as any).data.shippingMethod).toLowerCase()
                : "";

        if (current === "emessage") {
          emessageDraftAppliedRef.current.add(draftId);
          return true;
        }

        const snapshot: DraftOverride = {
          ...override,
          payloadBase: {
            ...override.payloadBase,
            shippingMethod: "emessage",
          },
        };
        const ok = await draftUi.persistDraftNow(draftId, snapshot);
        if (ok) {
          emessageDraftAppliedRef.current.add(draftId);
          void queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] });
        }
        return ok;
      } catch {
        return false;
      }
    },
    [checkoutActive, draftUi, isEMessageMode, queryClient],
  );

  const resumeDraft = useCallback(
    (draft: { id: string; type: "letter" | "postcard"; raw: any }) => {
      const draftId = draft.id;
      const override = draftUi.ensureDraftOverrideFromRaw(draftId, draft.raw);
      const nextText = clampDraftText({
        messageType: draft.type,
        text: override?.text ?? extractDraftMessageText(draft.raw),
      });
      setComposeMode(draft.type);
      draftUi.primeDraftAutosave(draftId, nextText);
      draftUi.setEditingDraftId(draftId);
      api.thread().composer.setText(nextText);
      requestAnimationFrame(() => {
        draftUi.scrollToBottom();
        draftUi.focusComposer();
      });
    },
    [api, draftUi, setComposeMode],
  );

  const attemptSwitchMode = useCallback(
    (nextMode: "letter" | "postcard") => {
      if (isEMessageMode) return;
      if (checkoutActive) return;
      if (nextMode === composeMode) return;
      setSwitchError(null);

      const currentDraft = existingDraftsByType ? existingDraftsByType[composeMode] : null;
      const targetDraft = existingDraftsByType ? existingDraftsByType[nextMode] : null;
      const hasActiveDraft = !!(editingDraftId && editingDraft);
      const currentDraftIsResend = !!(currentDraft && getIsResend(currentDraft.raw));
      const activeDraftIsResend = hasActiveDraft && editingDraftIsResend;
      if (currentDraftIsResend || activeDraftIsResend) return;

      // If there is no current draft, switch immediately.
      if (!currentDraft && !hasActiveDraft) {
        setComposeMode(nextMode);
        if (nextMode === "postcard") {
          const clamped = clampDraftText({ messageType: "postcard", text: (composerText || "").trim() });
          if (clamped !== composerText) api.thread().composer.setText(clamped);
        }
        if (targetDraft) {
          resumeDraft(targetDraft);
        }
        return;
      }

      // Switching away from a mode with an existing draft requires confirmation (draft is deleted).
      const transferSource = hasActiveDraft ? composerText : extractDraftMessageText(currentDraft.raw);
      setSwitchTransferText(
        clampDraftText({ messageType: nextMode, text: (transferSource || "").trim() }),
      );
      setSwitchTargetMode(nextMode);
      setSwitchConfirmOpen(true);
    },
    [
      api,
      checkoutActive,
      composeMode,
      composerText,
      draftUi,
      editingDraftIsResend,
      editingDraftId,
      existingDraftsByType,
      isEMessageMode,
      resumeDraft,
      setComposeMode,
    ],
  );

  const shouldGateComposer = !isThreadLoading && !editingDraftId && !!existingDraftIdForType;
  const locked = isThreadLoading || shouldGateComposer || checkoutActive;

  useEffect(() => {
    latestComposerValueRef.current = typeof composerText === "string" ? composerText : "";
  }, [composerText]);

  const ensureDraftForExpandedEditor = useCallback(
    async (nextText: string): Promise<DraftCreateResult | null> => {
      if (checkoutActive) return null;
      if (editingDraftId && editingDraft) {
        return { draftId: String(editingDraftId), override: editingDraft };
      }
      if (!penmateId) return null;

      const messageTypeToCreate = isEMessageMode ? "letter" : composeMode;
      expandedEditorLatestTextRef.current = nextText;
      const clamped = clampDraftText({ messageType: messageTypeToCreate, text: nextText });
      if (!clamped.trim()) return null;

      if (expandedEditorCreatePromiseRef.current) {
        return await expandedEditorCreatePromiseRef.current;
      }

      const shippingMethod =
        messageTypeToCreate === "postcard" ? "letter" : isEMessageMode ? "emessage" : "letter";
      const payloadBase: Record<string, unknown> = {
        messageType: messageTypeToCreate,
        shippingMethod,
        penmate_id: penmateId,
        message: clamped,
        summary: clamped,
      };
      const payload = withPenmatePayload(payloadBase);

      const createPromise = saveDraft({ draft: payload })
        .then((saved) => {
          const draftId = saved && (saved as any).id != null ? String((saved as any).id) : "";
          if (!draftId) throw new Error("Draft save did not return an id.");
          trackDraftCreated({
            saved,
            messageType: messageTypeToCreate,
            shippingMethod,
          });
          const override = draftUi.ensureDraftOverrideFromRaw(draftId, saved);
          draftUi.primeDraftAutosave(draftId, clamped);
          draftUi.setEditingDraftId(draftId);
          const latest = expandedEditorLatestTextRef.current;
          api.thread().composer.setText(clampDraftText({ messageType: messageTypeToCreate, text: latest }));
          void queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] });
          return { draftId, override };
        })
        .finally(() => {
          expandedEditorCreatePromiseRef.current = null;
        });

      expandedEditorCreatePromiseRef.current = createPromise;
      return createPromise;
    },
    [
      api,
      checkoutActive,
      composeMode,
      draftUi,
      editingDraft,
      editingDraftId,
      isEMessageMode,
      penmateId,
      queryClient,
      withPenmatePayload,
    ],
  );

  // Legacy behavior: start a server-backed draft on first non-empty typing so the user's
  // progress is saved even if they never hit "Review & send".
  useEffect(() => {
    if (locked) return;
    if (checkoutActive) return;
    if (isStartingCheckout || isSwitching) return;
    if (editingDraftId && editingDraft) return;
    if (!penmateId) return;
    const nextText = composerText || "";
    if (!nextText.trim()) return;
    void ensureDraftForExpandedEditor(nextText);
  }, [
    checkoutActive,
    composerText,
    editingDraft,
    editingDraftId,
    ensureDraftForExpandedEditor,
    isStartingCheckout,
    isSwitching,
    locked,
    penmateId,
  ]);

  const openExpandedEditor = useCallback(() => {
    if (locked) return;
    expandedEditorLatestTextRef.current = composerText || "";
    setExpandedEditorOpen(true);
    const currentText = composerText || "";
    if (!editingDraftId && currentText.trim()) {
      void ensureDraftForExpandedEditor(currentText);
    }
  }, [composerText, editingDraftId, ensureDraftForExpandedEditor, locked]);

  useEffect(() => {
    if (!expandedEditorOpen) return;
    const focus = () => expandedEditorTextareaRef.current?.focus();
    const raf = requestAnimationFrame(focus);
    return () => cancelAnimationFrame(raf);
  }, [expandedEditorOpen]);

  const resumeExistingDraft = useCallback(() => {
    const draftId = existingDraftIdForType;
    if (!draftId) return;
    if (!existingDraft) return;
    if (getIsResend(existingDraft.raw) && existingDraftHasMessage) return;
    void (async () => {
      await ensureDraftIsEMessage(existingDraft);
      resumeDraft(existingDraft);

      // Touch the draft when the user re-enters edit mode so it sorts near the composer.
      const id = String(draftId);
      const snapshot =
        draftUi.getDraftOverrideById(id) ?? draftUi.ensureDraftOverrideFromRaw(id, existingDraft.raw);
      const ok = await draftUi.persistDraftNow(id, snapshot);
      if (!ok) return;
      void queryClient
        .invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] })
        .then(() => requestAnimationFrame(() => draftUi.scrollToBottom()));
    })();
  }, [
    draftUi,
    ensureDraftIsEMessage,
    existingDraft,
    existingDraftHasMessage,
    existingDraftIdForType,
    queryClient,
    resumeDraft,
  ]);

  const startCheckoutForExistingDraft = useCallback(() => {
    if (!existingDraftIdForType) return;
    if (!onEnterCheckout) return;
    if (!existingDraftHasMessage) {
      setSendAttemptedDraftId(String(existingDraftIdForType));
      resumeExistingDraft();
      return;
    }
    void (async () => {
      await ensureDraftIsEMessage(existingDraft);
      const draftId = String(existingDraftIdForType);
      onEnterCheckout(draftId);

      // Touch the draft on "Review & send" so it sorts to the bottom of the thread
      // (drafts are private and should feel closer to the composer than historical messages).
      if (!existingDraft) return;
      const snapshot =
        draftUi.getDraftOverrideById(draftId) ?? draftUi.ensureDraftOverrideFromRaw(draftId, existingDraft.raw);
      const ok = await draftUi.persistDraftNow(draftId, snapshot);
      if (!ok) return;
      void queryClient
        .invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] })
        .then(() => requestAnimationFrame(() => draftUi.scrollToBottom()));
    })();
  }, [
    draftUi,
    ensureDraftIsEMessage,
    existingDraft,
    existingDraftHasMessage,
    existingDraftIdForType,
    onEnterCheckout,
    queryClient,
    resumeExistingDraft,
  ]);

  useEffect(() => {
    if (!isEMessageMode) return;
    if (checkoutActive) return;
    const letterDraft = existingDraftsByType ? existingDraftsByType.letter : null;
    if (!letterDraft) return;
    void ensureDraftIsEMessage(letterDraft);
  }, [checkoutActive, ensureDraftIsEMessage, existingDraftsByType, isEMessageMode]);

  const startCheckoutFromComposer = useCallback(async () => {
    if (checkoutActive) return;
    setCheckoutStartErrorState(null);

    const fallbackText = typeof composerText === "string" ? composerText : "";
    const trimmed = (latestComposerValueRef.current || fallbackText).trim();
    if (!trimmed) return;

    const effectiveType =
      editingDraftId && editingDraft
        ? toComposerMode(editingMessageType)
        : composeMode;
    const clampedText = clampDraftText({ messageType: effectiveType, text: trimmed });

    setIsStartingCheckout(true);
    let checkoutDraftId: string | null = editingDraftId ? String(editingDraftId) : null;
    try {
      if (!penmateId) throw new Error("Missing penmate id.");

      const draftResult =
        editingDraftId && editingDraft
          ? { draftId: String(editingDraftId), override: editingDraft }
          : await ensureDraftForExpandedEditor(clampedText);
      if (!draftResult) throw new Error("Draft save did not return an id.");

      const { draftId } = draftResult;
      checkoutDraftId = draftId;
      const base = draftResult.override;
      const snapshot: DraftOverride = { ...base, text: clampedText };
      if (base.text !== clampedText) {
        draftUi.cancelDraftAutosave(draftId);
        await draftUi.persistDraftNow(draftId, snapshot);
      }
      if (checkoutDisabled) {
        draftUi.setEditingDraftId(null);
        suppressDraftSyncRef.current = true;
        api.thread().composer.setText("");
        void queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] });
        return;
      }

      if (!onEnterCheckout) return;
      onEnterCheckout(draftId);
    } catch (e) {
      setCheckoutStartErrorState({
        draftId: checkoutDraftId,
        textSignature: clampedText,
        message: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setIsStartingCheckout(false);
    }
  }, [
    api,
    checkoutActive,
    checkoutDisabled,
    composerText,
    ensureDraftForExpandedEditor,
    draftUi,
    editingDraft,
    editingDraftId,
    composeMode,
    onEnterCheckout,
    penmateId,
    queryClient,
    editingMessageType,
  ]);

  const showPostcardComposer = composeMode === "postcard" || editingMessageType === "postcard";
  const canSend = !!(composerText || "").trim();

  const ensureDraftForPhotoUpload = useCallback(async (): Promise<string> => {
    if (checkoutActive) throw new Error("Checkout is active.");
    if (showPostcardComposer) throw new Error("Photos are not supported for postcards.");
    if (editingDraftId && editingDraft) return editingDraftId;
    if (expandedEditorCreatePromiseRef.current) {
      const pending = await expandedEditorCreatePromiseRef.current;
      return pending.draftId;
    }
    if (!penmateId) throw new Error("Missing penmate id.");
    const text = (composerText || "").trim();
    const payloadBase: Record<string, unknown> = {
      messageType: "letter",
      shippingMethod: isEMessageMode ? "emessage" : "letter",
      penmate_id: penmateId,
      message: text,
      summary: text,
      selectedPhotos: [],
    };
    const payload = withPenmatePayload(payloadBase);
    const saved = await saveDraft({ draft: payload });
    const draftId = saved && (saved as any).id != null ? String((saved as any).id) : "";
    if (!draftId) throw new Error("Draft save did not return an id.");
    trackDraftCreated({
      saved,
      messageType: "letter",
      shippingMethod: isEMessageMode ? "emessage" : "letter",
    });
    draftUi.ensureDraftOverrideFromRaw(draftId, saved);
    draftUi.primeDraftAutosave(draftId, text);
    draftUi.setEditingDraftId(draftId);
    api.thread().composer.setText(text);
    void queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] });
    return draftId;
  }, [
    api,
    checkoutActive,
    composerText,
    draftUi,
    editingDraft,
    editingDraftId,
    isEMessageMode,
    penmateId,
    queryClient,
    showPostcardComposer,
    withPenmatePayload,
  ]);

	  const addPhotosFromFiles = useCallback(
	    async (files: readonly File[]) => {
      if (checkoutActive) return;
      if (showPostcardComposer) return;
      setPhotoUploadError(null);
      setPhotoUploadWarning(null);

      const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
      const images = filterImageFiles(files);
      if (!images.length) {
        setPhotoUploadError("Only photos are supported.");
        return;
      }

      const oversized = images.filter((f) => (typeof f.size === "number" ? f.size : 0) > MAX_IMAGE_BYTES);
      const sized = images.filter((f) => (typeof f.size === "number" ? f.size : 0) <= MAX_IMAGE_BYTES);
      if (!sized.length) {
        setPhotoUploadError("Photos must be 10MB or smaller.");
        return;
      }
      if (oversized.length) {
        setPhotoUploadWarning("Some photos were larger than 10MB and were skipped.");
      }

      const existingCount = editingDraft?.selectedPhotosPayload?.length ?? 0;
      const remaining = Math.max(0, maxSelectedPhotos - existingCount);
      if (remaining <= 0) {
        setPhotoUploadWarning(`Max ${maxSelectedPhotos} photos allowed.`);
        return;
      }

      const toUpload = sized.slice(0, remaining);
      if (sized.length > remaining) {
        setPhotoUploadWarning(`Max ${maxSelectedPhotos} photos allowed. Added first ${remaining}.`);
      }

	      let draftId: string;
	      try {
	        draftId = await ensureDraftForPhotoUpload();
	      } catch (e) {
	        setPhotoUploadError(e instanceof Error ? e.message : String(e));
	        return;
	      }

	      const batchId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
	      const uploadPlaceholders: DraftPhotoUpload[] = toUpload.map((f, idx) => ({
	        id: `${batchId}-${idx}`,
	        filename: f.name || `Photo ${idx + 1}`,
	        previewUrl:
	          typeof URL !== "undefined" && typeof URL.createObjectURL === "function" ? URL.createObjectURL(f) : "",
	        status: "queued",
	        percent: 0,
	      }));
	      setPhotoUploads((prev) => [...prev, ...uploadPlaceholders]);

	      setPhotoUploadProgress({ done: 0, total: toUpload.length });
	      for (let i = 0; i < toUpload.length; i++) {
	        const uploadId = uploadPlaceholders[i]?.id;
	        let keepUploadTile = false;
	        try {
	          const original = toUpload[i]!;
	          if (uploadId) {
	            setPhotoUploads((prev) =>
	              prev.map((u) => (u.id === uploadId ? { ...u, status: "preparing", percent: 0 } : u)),
	            );
	          }
	          const compressed = await compressImageFile(original);
	          const rawName = (compressed && typeof compressed.name === "string" && compressed.name) ? compressed.name : "photo.jpg";
	          const safeBase = rawName.replace(/[^a-zA-Z0-9._-]+/g, "_");
	          const dot = safeBase.lastIndexOf(".");
	          const ts = Date.now();
          const filename =
            dot > 0
	              ? `${safeBase.slice(0, dot)}_${ts}${safeBase.slice(dot)}`
	              : `${safeBase}_${ts}.jpg`;
	          const { url, key } = await createUploadPhotoUrl({ filename });
	          if (uploadId) {
	            setPhotoUploads((prev) =>
	              prev.map((u) => (u.id === uploadId ? { ...u, status: "uploading", percent: 0 } : u)),
	            );
	          }
	          await putFileToPresignedUrl({
	            url,
	            file: compressed,
	            onProgress: (p) => {
	              if (!uploadId) return;
	              setPhotoUploads((prev) =>
	                prev.map((u) =>
	                  u.id === uploadId && u.status !== "error" ? { ...u, percent: p.percent } : u,
	                ),
	              );
	            },
	          });
	          if (uploadId) {
	            setPhotoUploads((prev) =>
	              prev.map((u) => (u.id === uploadId ? { ...u, status: "finalizing", percent: 100 } : u)),
	            );
	          }
	          const completed = await uploadPhotoComplete({ key });
	          if (!completed?.url) throw new Error("Upload did not return a URL");
	          await draftUi.appendDraftSelectedPhotos(draftId, [
	            {
              url: completed.url,
              img_path: completed.img_path,
              filename: compressed.name,
	              provider: "device",
	            },
	          ]);
	        } catch (e) {
	          setPhotoUploadError(e instanceof Error ? e.message : String(e));
	          keepUploadTile = true;
	          if (uploadId) {
	            setPhotoUploads((prev) =>
	              prev.map((u) =>
	                u.id === uploadId
	                  ? { ...u, status: "error", error: e instanceof Error ? e.message : String(e) }
	                  : u,
	              ),
	            );
	          }
	          // Continue uploading remaining photos.
	        } finally {
	          if (uploadId && !keepUploadTile) {
	            setPhotoUploads((prev) => prev.filter((u) => u.id !== uploadId));
	          }
	          setPhotoUploadProgress((p) => {
	            if (!p) return p;
	            return { ...p, done: Math.min(p.total, p.done + 1) };
	          });
        }
      }
	      setPhotoUploadProgress(null);
	    },
	    [
	      checkoutActive,
	      compressImageFile,
      draftUi,
      editingDraft?.selectedPhotosPayload,
      ensureDraftForPhotoUpload,
      maxSelectedPhotos,
	      showPostcardComposer,
	    ],
	  );

  const openPostcardPicker = useCallback(async () => {
    if (isEMessageMode) return;
    if (checkoutActive) return;
    setPostcardPickerError(null);

    // If we're already editing a postcard draft, just open the picker.
    if (editingDraftId && editingMessageType === "postcard") {
      draftUi.openPostcardEditor(editingDraftId);
      return;
    }

    // If a postcard draft exists, resume it and open the picker.
    const existingPostcardDraft = existingDraftsByType ? existingDraftsByType.postcard : null;
    if (existingPostcardDraft) {
      resumeDraft(existingPostcardDraft);
      requestAnimationFrame(() => {
        draftUi.openPostcardEditor(existingPostcardDraft.id);
      });
      return;
    }

    // Otherwise create a new postcard draft (design-first; text can be empty).
    if (!penmateId) {
      setPostcardPickerError("Missing penmate id.");
      return;
    }

    setIsPostcardPickerLoading(true);
    try {
      setComposeMode("postcard");
      const text = clampDraftText({
        messageType: "postcard",
        text: (composerText || "").trim(),
      });
      const payloadBase: Record<string, unknown> = {
        messageType: "postcard",
        shippingMethod: "letter",
        penmate_id: penmateId,
        message: text,
        summary: text,
      };
      const payload = withPenmatePayload(payloadBase);
      const saved = await saveDraft({ draft: payload });
      const draftId = saved && (saved as any).id != null ? String((saved as any).id) : "";
      if (!draftId) throw new Error("Draft save did not return an id.");
      trackDraftCreated({
        saved,
        messageType: "postcard",
        shippingMethod: "letter",
      });
      draftUi.ensureDraftOverrideFromRaw(draftId, saved);
      draftUi.primeDraftAutosave(draftId, text);
      draftUi.setEditingDraftId(draftId);
      api.thread().composer.setText(text);
      await queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] });
      requestAnimationFrame(() => {
        draftUi.scrollToBottom();
        draftUi.focusComposer();
        draftUi.openPostcardEditor(draftId);
      });
    } catch (e) {
      setPostcardPickerError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsPostcardPickerLoading(false);
    }
  }, [
    api,
    checkoutActive,
    composeMode,
    composerText,
    draftUi,
    editingDraftId,
    editingMessageType,
    existingDraftsByType,
    isEMessageMode,
    penmateId,
    queryClient,
    resumeDraft,
    setComposeMode,
    withPenmatePayload,
  ]);

  return (
    <ComposerPrimitive.Root className="aui-composer-root relative flex w-full flex-col">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = e.target.files ? Array.from(e.target.files) : [];
          try {
            // Some environments (notably jsdom) can be strict about programmatically clearing file inputs.
            e.target.value = "";
          } catch {
            // ignore
          }
          if (!files.length) return;
          void addPhotosFromFiles(files);
        }}
      />
      {showPostcardComposer ? (
        <div className="aui-composer-attachment-dropzone flex w-full flex-col rounded-2xl border border-input bg-card px-1 pt-2 outline-none shadow-sm transition-shadow has-[textarea:focus-visible]:border-ring has-[textarea:focus-visible]:ring-2 has-[textarea:focus-visible]:ring-ring/20">
          {!isEMessageMode && !checkoutActive ? (
            <div className="mb-1 flex w-full items-center justify-center px-3 pt-0.5">
              <div className="inline-flex items-center rounded-full border border-border bg-muted/20 p-0.5">
                <button
                  type="button"
                  className={cn(
                    "h-8 rounded-full px-3 text-sm font-medium transition-colors",
                    composeMode === "letter"
                      ? "bg-card shadow-sm outline outline-2 outline-[#4b46a4a3]"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  onClick={() => attemptSwitchMode("letter")}
                  disabled={checkoutActive || isSwitching}
                  aria-pressed={composeMode === "letter"}
                >
                  Letter
                </button>
                <button
                  type="button"
                  className={cn(
                    "h-8 rounded-full px-3 text-sm font-medium transition-colors",
                    composeMode === "postcard"
                      ? "bg-card shadow-sm outline outline-2 outline-[#4b46a4a3]"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  onClick={() => attemptSwitchMode("postcard")}
                  disabled={checkoutActive || isSwitching}
                  aria-pressed={composeMode === "postcard"}
                >
                  Postcard
                </button>
              </div>
            </div>
          ) : null}

          {composeMode === "postcard" && !isEMessageMode && !checkoutActive && !shouldGateComposer ? (
            <div className="mb-2 flex w-full flex-col gap-2 px-3">
              <Button
                type="button"
                variant="outline"
                className="w-full bg-white/70 shadow-sm hover:bg-white"
                onClick={() => void openPostcardPicker()}
                disabled={isPostcardPickerLoading || isSwitching}
              >
                {isPostcardPickerLoading
                  ? "Preparing postcard…"
                  : existingDraftsByType?.postcard
                    ? "Edit postcard design"
                    : "Choose postcard design"}
              </Button>
              {postcardPickerError ? (
                <div className="text-destructive text-xs">{postcardPickerError}</div>
              ) : null}
            </div>
          ) : null}

	          {!checkoutActive && editingDraftId && editingDraft ? (
	            editingMessageType !== "postcard" ? (
	              <DraftPhotosStrip
	                draftId={editingDraftId}
	                photos={editingDraft.photos}
	                uploads={photoUploads}
	                onRemoveUpload={(id) => setPhotoUploads((prev) => prev.filter((u) => u.id !== id))}
	              />
	            ) : (
	              <DraftPostcardFrontStrip frontUrl={postcardFrontUrl} />
	            )
	          ) : null}

          {/* Postcards do not support composer attachments; use the postcard design modal instead. */}
          {locked ? (
            <div className="mb-1 flex w-full flex-col gap-2 px-4 pt-2 pb-3">
              <div className="text-muted-foreground text-sm text-center">
                {isThreadLoading
                  ? "Loading…"
                  : checkoutActive
                    ? "Review your message or click Cancel to edit."
                    : shouldGateComposer
                    ? checkoutDisabled
                      ? "You have an existing message. Edit it, or delete it."
                      : existingDraftIsResend
                        ? existingDraftHasMessage
                          ? "You are resending a message. Review & send it, or delete it below."
                          : "This resend is missing message text. Edit it to send."
                        : "You have an existing draft. Review & send it, or edit it."
                      : "Composer is locked."}
              </div>
              {checkoutActive ? null : shouldGateComposer ? (
                <div className="mt-3 flex w-full flex-col gap-2">
                  {checkoutDisabled ? (
                    <>
                      <Button type="button" className="w-full" variant="secondary" onClick={resumeExistingDraft}>
                        Edit message
                      </Button>
                      <Button
                        type="button"
                        className="w-full"
                        variant="destructive"
                        onClick={() => draftUi.requestDeleteDraft(existingDraftIdForType!)}
                      >
                        Delete message
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button type="button" className="w-full" onClick={startCheckoutForExistingDraft}>
                        Review &amp; send
                      </Button>
                      {!existingDraftIsResend && composeMode === "postcard" && !isEMessageMode ? (
                        <Button type="button" className="w-full bg-white/70 shadow-sm hover:bg-white" variant="outline" onClick={() => void openPostcardPicker()}>
                          Edit postcard design
                        </Button>
                      ) : null}
                      {existingDraftIsResend && !resendDraftNeedsMessage ? (
                        <Button
                          type="button"
                          className="w-full"
                          variant="destructive"
                          onClick={() => draftUi.requestDeleteDraft(existingDraftIdForType!)}
                        >
                          Delete message
                        </Button>
                      ) : (
                        <Button type="button" className="w-full" variant="secondary" onClick={resumeExistingDraft}>
                          {composeMode === "postcard" ? "Edit postcard message" : "Edit message"}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              ) : null}
            </div>
          ) : (
            <ComposerPrimitive.Input
              placeholder="Send a message..."
              className="aui-composer-input mb-1 max-h-32 min-h-14 w-full resize-none bg-transparent px-4 pt-2 pb-3 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-0"
              rows={1}
              autoFocus
              aria-label="Message input"
              ref={composerInputRef as any}
              maxLength={showPostcardComposer ? POSTCARD_MAX_CHARS : undefined}
              submitOnEnter={false}
              onChange={(e) => {
                latestComposerValueRef.current = e.target.value;
              }}
            />
          )}

          {locked ? null : (
            <ComposerAction
              useNativeSend={!penmateId}
              disabled={isStartingCheckout || !canSend || isUploadingPhotos}
              onSend={() => void startCheckoutFromComposer()}
              onExpand={openExpandedEditor}
              expandDisabled={isStartingCheckout}
              expandTooltip="Expand editor"
            />
          )}
      </div>
      ) : (
        <div className="aui-composer-attachment-dropzone flex w-full flex-col rounded-2xl border border-input bg-card px-1 pt-2 outline-none shadow-sm transition-shadow has-[textarea:focus-visible]:border-ring has-[textarea:focus-visible]:ring-2 has-[textarea:focus-visible]:ring-ring/20">
        {!isEMessageMode && !checkoutActive ? (
          <div className="mb-1 flex w-full items-center justify-center px-3 pt-0.5">
            <div className="inline-flex items-center rounded-full border border-border bg-muted/20 p-0.5">
              <button
                type="button"
                className={cn(
                  "h-8 rounded-full px-3 text-sm font-medium transition-colors",
                  composeMode === "letter"
                    ? "bg-card shadow-sm outline outline-2 outline-[#4b46a4a3]"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={() => attemptSwitchMode("letter")}
                disabled={checkoutActive || isSwitching}
                aria-pressed={composeMode === "letter"}
              >
                Letter
              </button>
              <button
                type="button"
                className={cn(
                  "h-8 rounded-full px-3 text-sm font-medium transition-colors",
                  composeMode === "postcard"
                    ? "bg-card shadow-sm outline outline-2 outline-[#4b46a4a3]"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={() => attemptSwitchMode("postcard")}
                disabled={checkoutActive || isSwitching}
                aria-pressed={composeMode === "postcard"}
              >
                Postcard
              </button>
            </div>
          </div>
        ) : null}

        {!checkoutActive && editingDraftId && editingDraft ? (
          editingMessageType !== "postcard" ? (
            <DraftPhotosStrip
              draftId={editingDraftId}
              photos={editingDraft.photos}
              uploads={photoUploads}
              onRemoveUpload={(id) => setPhotoUploads((prev) => prev.filter((u) => u.id !== id))}
            />
          ) : (
            <DraftPostcardFrontStrip frontUrl={postcardFrontUrl} />
          )
        ) : null}
        {locked ? (
          <div className="mb-1 flex w-full flex-col gap-2 px-4 pt-2 pb-3">
            <div className="text-muted-foreground text-sm text-center">
              {isThreadLoading
                ? "Loading…"
                : checkoutActive
                  ? "Review your message or click Cancel to edit."
                  : shouldGateComposer
                    ? checkoutDisabled
                      ? "You have an existing message. Edit it, or delete it."
                      : existingDraftIsResend
                        ? existingDraftHasMessage
                          ? "You have a resend draft. Review & send it. You can update return address or facility during checkout."
                          : "This resend is missing message text. Edit it to send."
                        : "You have an existing draft. Review & send it, or edit it."
                    : "Composer is locked."}
            </div>
            {checkoutActive ? null : shouldGateComposer ? (
              <div className="mt-3 flex w-full flex-col gap-2">
                {checkoutDisabled ? (
                  <>
                    <Button type="button" className="w-full" variant="secondary" onClick={resumeExistingDraft}>
                      Edit message
                    </Button>
                    <Button
                      type="button"
                      className="w-full"
                      variant="destructive"
                      onClick={() => draftUi.requestDeleteDraft(existingDraftIdForType!)}
                    >
                      Delete message
                    </Button>
                  </>
                ) : (
                  <>
                    <Button type="button" className="w-full" onClick={startCheckoutForExistingDraft}>
                      Review &amp; send
                    </Button>
                    {existingDraftIsResend && !resendDraftNeedsMessage ? (
                      <Button
                        type="button"
                        className="w-full"
                        variant="destructive"
                        onClick={() => draftUi.requestDeleteDraft(existingDraftIdForType!)}
                      >
                        Delete message
                      </Button>
                    ) : (
                      <Button type="button" className="w-full" variant="secondary" onClick={resumeExistingDraft}>
                        Edit message
                      </Button>
                    )}
                  </>
                )}
              </div>
            ) : null}
          </div>
        ) : (
          <ComposerPrimitive.Input
            placeholder="Send a message..."
            className="aui-composer-input mb-1 max-h-32 min-h-14 w-full resize-none bg-transparent px-4 pt-2 pb-3 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-0"
            rows={1}
            autoFocus
            aria-label="Message input"
            ref={composerInputRef as any}
            maxLength={showPostcardComposer ? POSTCARD_MAX_CHARS : undefined}
            submitOnEnter={false}
            onChange={(e) => {
              latestComposerValueRef.current = e.target.value;
            }}
          />
        )}
        {locked ? null : (
          <ComposerAction
            useNativeSend={!penmateId}
            disabled={
              isStartingCheckout ||
              !canSend ||
              isUploadingPhotos
            }
            onSend={() => void startCheckoutFromComposer()}
            onAddPhotos={
              showPostcardComposer
                ? undefined
                : () => {
                    if (isUploadingPhotos) return;
                    setPhotoUploadError(null);
                    setPhotoUploadWarning(null);
                    fileInputRef.current?.click();
                  }
            }
            addPhotosDisabled={
              showPostcardComposer ? undefined : isUploadingPhotos || isStartingCheckout
            }
            addPhotosTooltip={
              showPostcardComposer ? undefined : `Add photos (max ${maxSelectedPhotos})`
            }
            onExpand={openExpandedEditor}
            expandDisabled={isUploadingPhotos || isStartingCheckout}
            expandTooltip="Expand editor"
          />
        )}
      </div>
      )}

      <Dialog
        open={switchConfirmOpen}
        onOpenChange={(open) => {
          setSwitchConfirmOpen(open);
          if (!open) {
            setSwitchError(null);
            setIsSwitching(false);
          }
        }}
        modal={false}
      >
        <DialogContent className="w-[calc(100vw-2rem)] max-w-sm !p-4">
          <DialogTitle className="font-semibold text-base">
            Switch to {switchTargetMode === "postcard" ? "Postcard" : "Letter"}?
          </DialogTitle>
          <DialogDescription className="sr-only">Confirm switching composer mode.</DialogDescription>
          <div className="mt-2 text-muted-foreground text-sm">
            Your existing {composeMode === "postcard" ? "postcard" : "letter"} draft will be deleted.
            {switchTargetMode === "postcard"
              ? " We’ll move your text (limited to 200 characters)."
              : " We’ll move your text."}
          </div>
          {switchError ? <div className="mt-2 text-destructive text-xs">{switchError}</div> : null}
          <div className="mt-4 flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={isSwitching}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="button"
              variant="destructive"
              disabled={isSwitching || !penmateId}
              onClick={() => {
                if (isSwitching) return;
                if (!penmateId) return;
                const currentDraftId =
                  editingDraftId && editingDraft
                    ? editingDraftId
                    : existingDraftsByType && existingDraftsByType[composeMode]
                      ? existingDraftsByType[composeMode]!.id
                      : null;
                if (!currentDraftId) {
                  setSwitchConfirmOpen(false);
                  setComposeMode(switchTargetMode);
                  return;
                }

                setIsSwitching(true);
                setSwitchError(null);
                void (async () => {
                  try {
                    await deleteMessage({ penmateId, messageId: currentDraftId });
                    draftUi.setEditingDraftId(null);

                    setComposeMode(switchTargetMode);
                    await Promise.all([
                      queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"] }),
                      queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "drafts"], exact: false }),
                    ]);

                    const targetDraft = existingDraftsByType ? existingDraftsByType[switchTargetMode] : null;
                    if (targetDraft) {
                      resumeDraft(targetDraft);
                    } else {
                      api.thread().composer.setText(switchTransferText);
                      requestAnimationFrame(() => {
                        draftUi.scrollToBottom();
                        draftUi.focusComposer();
                      });
                    }

                    setSwitchConfirmOpen(false);
                  } catch (e) {
                    setSwitchError(e instanceof Error ? e.message : String(e));
                  } finally {
                    setIsSwitching(false);
                  }
                })();
              }}
            >
              Delete &amp; switch
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {checkoutErrorMessage ? (
        <div className="mt-2 px-2 text-center text-destructive text-sm font-medium">{checkoutErrorMessage}</div>
      ) : null}
      {isUploadingPhotos && photoUploadProgress ? (
        <div className="mt-2 px-2 text-muted-foreground text-xs">
          Uploading photos… {Math.min(photoUploadProgress.done, photoUploadProgress.total)}/{photoUploadProgress.total}
        </div>
      ) : null}
      {photoUploadWarning ? (
        <div className="mt-2 px-2 text-muted-foreground text-xs">{photoUploadWarning}</div>
      ) : null}
      {photoUploadError ? (
        <div className="mt-2 px-2 text-destructive text-xs">{photoUploadError}</div>
      ) : null}
      {showPostcardComposer && !canSend && !checkoutActive ? (
        <div className="mt-2 px-2 text-muted-foreground text-xs text-center">
          Add a message to send your postcard (max {POSTCARD_MAX_CHARS} characters).
        </div>
      ) : null}
      {editingDraftId && draftSaveError ? (
        <div className="mt-2 px-2 text-destructive text-xs">
          Couldn’t save draft changes. Check your connection and try again.
        </div>
      ) : null}
      <Dialog open={expandedEditorOpen} onOpenChange={setExpandedEditorOpen} modal={false}>
        <DialogContent
          className="flex h-[calc(100vh-2rem)] supports-[height:100dvh]:h-[calc(100dvh-2rem)] max-h-[calc(100vh-2rem)] supports-[height:100dvh]:max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-none flex-col !gap-0 !p-0 [&>button.absolute]:hidden"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            requestAnimationFrame(() => expandedEditorTextareaRef.current?.focus());
          }}
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <DialogTitle className="font-semibold text-base">Edit message</DialogTitle>
            <DialogDescription className="sr-only">Full screen editor</DialogDescription>
            <DialogClose asChild>
              <Button type="button" variant="secondary" size="sm">
                Done
              </Button>
            </DialogClose>
          </div>
          <div className="flex min-h-0 flex-1 flex-col px-4 py-3">
            <textarea
              ref={expandedEditorTextareaRef}
              className="w-full flex-1 resize-none rounded-2xl border border-input bg-background px-4 py-3 text-base leading-6 outline-none focus-visible:ring-2 focus-visible:ring-ring/20"
              aria-label="Full screen message input"
              value={composerText}
              maxLength={showPostcardComposer ? POSTCARD_MAX_CHARS : undefined}
              onChange={(e) => {
                const next = clampDraftText({
                  messageType: showPostcardComposer ? "postcard" : "letter",
                  text: e.target.value,
                });
                latestComposerValueRef.current = next;
                api.thread().composer.setText(next);
                if (!editingDraftId && next.trim()) {
                  void ensureDraftForExpandedEditor(next);
                }
              }}
            />
            {showPostcardComposer ? (
              <div className="mt-2 text-xs text-muted-foreground text-right">
                {composerText.length}/{POSTCARD_MAX_CHARS}
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </ComposerPrimitive.Root>
  );
};

const ComposerAction: FC<{
  onSend: () => void;
  disabled: boolean;
  useNativeSend: boolean;
  onAddPhotos?: () => void;
  addPhotosDisabled?: boolean;
  addPhotosTooltip?: string;
  onExpand?: () => void;
  expandDisabled?: boolean;
  expandTooltip?: string;
}> = ({
  onSend,
  disabled,
  useNativeSend,
  onAddPhotos,
  addPhotosDisabled,
  addPhotosTooltip,
  onExpand,
  expandDisabled,
  expandTooltip,
}) => {
  return (
    <div className="aui-composer-action-wrapper relative mx-2 mb-2 flex items-center justify-between">
      <div className="flex items-center gap-1">
        {onAddPhotos ? (
          <TooltipIconButton
            tooltip={addPhotosTooltip || "Add photos"}
            side="bottom"
            type="button"
            variant="ghost"
            size="icon"
            className="aui-composer-add-photos size-8 rounded-full p-1 font-semibold text-xs hover:bg-muted-foreground/15 dark:border-muted-foreground/15 dark:hover:bg-muted-foreground/30 [&_svg]:!size-6"
            aria-label="Add photos"
            onClick={onAddPhotos}
            disabled={!!addPhotosDisabled}
          >
            <ImagePlusIcon className="stroke-[1.75px]" />
          </TooltipIconButton>
        ) : onExpand ? null : (
          <div className="size-8" />
        )}

        {onExpand ? (
          <TooltipIconButton
            tooltip={expandTooltip || "Expand editor"}
            side="bottom"
            type="button"
            variant="ghost"
            size="icon"
            className="aui-composer-expand-editor size-8 rounded-full p-1 font-semibold text-xs hover:bg-muted-foreground/15 dark:border-muted-foreground/15 dark:hover:bg-muted-foreground/30 [&_svg]:!size-6"
            aria-label="Expand editor"
            onClick={onExpand}
            disabled={!!expandDisabled}
          >
            <Maximize2Icon className="stroke-[1.75px]" />
          </TooltipIconButton>
        ) : null}
      </div>

      <AssistantIf condition={({ thread }) => !thread.isRunning}>
        {useNativeSend ? (
          <ComposerPrimitive.Send asChild>
            <TooltipIconButton
              tooltip="Send message"
              side="bottom"
              type="submit"
              variant="default"
              size="icon"
              className="aui-composer-send size-8 rounded-full"
              aria-label="Send message"
              disabled={disabled}
            >
              <ArrowUpIcon className="aui-composer-send-icon size-4" />
            </TooltipIconButton>
          </ComposerPrimitive.Send>
        ) : (
          <TooltipIconButton
            tooltip="Send message"
            side="bottom"
            type="button"
            variant="default"
            size="icon"
            className="aui-composer-send size-8 rounded-full"
            aria-label="Send message"
            onClick={onSend}
            disabled={disabled}
          >
            <ArrowUpIcon className="aui-composer-send-icon size-4" />
          </TooltipIconButton>
        )}
      </AssistantIf>

      <AssistantIf condition={({ thread }) => thread.isRunning}>
        <ComposerPrimitive.Cancel asChild>
          <Button
            type="button"
            variant="default"
            size="icon"
            className="aui-composer-cancel size-8 rounded-full"
            aria-label="Stop generating"
          >
            <SquareIcon className="aui-composer-cancel-icon size-3 fill-current" />
          </Button>
        </ComposerPrimitive.Cancel>
      </AssistantIf>
    </div>
  );
};

const MessageError: FC = () => {
  return (
    <MessagePrimitive.Error>
      <ErrorPrimitive.Root className="aui-message-error-root mt-2 rounded-md border border-destructive bg-destructive/10 p-3 text-destructive text-sm dark:bg-destructive/5 dark:text-red-200">
        <ErrorPrimitive.Message className="aui-message-error-message line-clamp-2" />
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
};

const AssistantMessage: FC = () => {
  const pmEvent = useAssistantState(
    ({ message }) => (message?.metadata as any)?.custom?.pmEvent as ThreadEvent | undefined,
  );
  const sortTs = useAssistantState(
    ({ message }) => (message?.metadata as any)?.custom?.sortTs as number | undefined,
  );

  return (
    <MessagePrimitive.Root
      className="aui-assistant-message-root fade-in slide-in-from-bottom-1 relative mx-auto w-full max-w-(--thread-max-width) animate-in py-3 duration-150"
      data-role="assistant"
    >
      <div className="aui-assistant-message-content-wrapper px-2">
        <MessageFrame role="assistant" timestampMs={sortTs}>
          <div className="aui-assistant-message-content w-full wrap-break-word rounded-t-2xl rounded-br-2xl rounded-bl-[4px] bg-white px-4 py-2.5 text-[#2a2a2a] leading-relaxed shadow-sm">
            {pmEvent ? (
              <PmMessageContent event={pmEvent} />
            ) : (
              <MessagePrimitive.Parts
                components={{
                  Text: MessageText,
                  tools: { Fallback: ToolFallback },
                }}
              />
            )}
            <MessageError />
          </div>
        </MessageFrame>
      </div>

      <div className="aui-assistant-message-footer mt-1 ml-2 flex">
        <BranchPicker />
      </div>
    </MessagePrimitive.Root>
  );
};

const UserMessage: FC = () => {
  const draftUi = useDraftUi();
  const api = useAssistantApi();
  const photoViewer = usePhotoViewer();
  const checkoutActive = useCheckoutActive();
  const checkoutSending = useCheckoutSending();
  const checkoutDisabled = useCheckoutDisabled();
  const prefersReducedMotion = usePrefersReducedMotion();
  const hostNav = useHostNav();
  const { setComposeMode } = useComposeMode();
  const [deliveryInfoOpen, setDeliveryInfoOpen] = useState(false);

  const pmEvent = useAssistantState(
    ({ message }) => (message?.metadata as any)?.custom?.pmEvent as ThreadEvent | undefined,
  );
  const sortTs = useAssistantState(
    ({ message }) => (message?.metadata as any)?.custom?.sortTs as number | undefined,
  );

  const isDraft = !!pmEvent && isDraftEvent(pmEvent);
  const isResendDraft = isDraft && pmEvent ? getIsResend(pmEvent.raw) : false;
  const draftOverride = useMemo(() => {
    if (!pmEvent) return null;
    if (!isDraft) return null;
    return draftUi.getDraftOverride(pmEvent);
  }, [draftUi, isDraft, pmEvent]);
  const resendNeedsMessage = (() => {
    if (!isResendDraft) return false;
    const resolved = resolveDraftMessageText(
      draftOverride?.payloadBase ?? pmEvent?.raw?.data,
      pmEvent?.raw,
    );
    if (!resolved.hasValue) return !((draftOverride?.text || "").trim());
    return !resolved.text.trim();
  })();
  const isEditing =
    isDraft && pmEvent
      ? draftUi.editingDraftId === pmEvent.id && !checkoutActive && (!isResendDraft || resendNeedsMessage)
      : false;
  const needsInfo = !!pmEvent && !!(pmEvent.raw as any)?.needs_info;

  const metaRight = useMemo((): React.ReactNode => {
    if (!pmEvent) return null;
    if (isDraft) return null;

    const raw = pmEvent.raw as any;
    const needsInfoRaw = !!raw?.needs_info;
    const delivered = !!raw?.delivery_date || !!raw?.tracking_history?.delivery_completed;
    const trackingAvailable = !!raw?.tracking_available;

    const status = typeof raw?.status === "string" ? raw.status : "";
    const processingLike = /ready_to_be_mailed|processing/i.test(status);
    const canOpenDeliveryManager =
      !!hostNav.penmateId && !!hostNav.onNavigate && (trackingAvailable || delivered || processingLike);

    if (needsInfoRaw) {
      const canOpenNeedsInfo = !!hostNav.penmateId && !!hostNav.onNavigate;
      return (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className={cn(
            "h-6 rounded-full border px-2 py-0 text-[11px] font-medium leading-none gap-1 shadow-sm",
            "border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100",
          )}
          onClick={() => {
            if (!canOpenNeedsInfo) return;
            const returnTo = window.location.pathname + window.location.search;
            hostNav.onNavigate!({
              to: "needs-info",
              penmateId: hostNav.penmateId!,
              messageId: pmEvent.id,
              returnTo,
            });
          }}
        >
          <AlertTriangleIcon aria-hidden="true" className="h-3.5 w-3.5" />
          <span>Action needed</span>
          <ChevronRightIcon aria-hidden="true" className="h-3.5 w-3.5 opacity-70" />
        </Button>
      );
    }

    const state: "pre_mail" | "mailed" | "delivered" = delivered
      ? "delivered"
      : trackingAvailable
        ? "mailed"
        : "pre_mail";

    const label = "Delivery details";
    const Icon = state === "delivered" ? CheckCircle2Icon : state === "mailed" ? TruckIcon : InfoIcon;
    const chipClassName = cn(
      "h-6 rounded-full border px-2 py-0 text-[11px] font-medium leading-none gap-1 shadow-sm",
      state === "delivered"
        ? "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
        : state === "mailed"
          ? "border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100"
          : "border-border bg-card text-foreground/80 hover:bg-accent",
    );

    return (
      <>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className={chipClassName}
          onClick={() => {
            if (canOpenDeliveryManager) {
              const returnTo = window.location.pathname + window.location.search;
              hostNav.onNavigate!({
                to: "tracking",
                penmateId: hostNav.penmateId!,
                messageId: pmEvent.id,
                returnTo,
              });
              return;
            }
            setDeliveryInfoOpen(true);
          }}
        >
          <Icon aria-hidden="true" className="h-3.5 w-3.5" />
          <span>{label}</span>
          <ChevronRightIcon aria-hidden="true" className="h-3.5 w-3.5 opacity-70" />
        </Button>

        <Dialog open={deliveryInfoOpen} onOpenChange={setDeliveryInfoOpen} modal={false}>
          <DialogContent className="w-[calc(100vw-2rem)] max-w-sm !p-4">
            <DialogTitle className="font-semibold text-base">Delivery details</DialogTitle>
            <DialogDescription className="mt-2 text-muted-foreground text-sm">
              Your message has been sent. All messages require facility review. You will be notified when delivery is
              completed.
            </DialogDescription>
            <div className="mt-4 flex justify-end">
              <DialogClose asChild>
                <Button type="button" variant="secondary">
                  OK
                </Button>
              </DialogClose>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }, [deliveryInfoOpen, hostNav, isDraft, pmEvent]);

  return (
    <MessagePrimitive.Root
      className="aui-user-message-root fade-in slide-in-from-bottom-1 mx-auto grid w-full max-w-(--thread-max-width) animate-in auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] content-start gap-y-2 px-2 py-3 duration-150 [&:where(>*)]:col-start-2"
      data-role="user"
    >
      <UserMessageAttachments />

      <div className="aui-user-message-content-wrapper relative col-start-2 min-w-0">
        <MessageFrame
          role="user"
          timestampMs={sortTs}
          statusLabel={pmEvent && !isDraft ? "Sent" : undefined}
          metaRight={metaRight}
        >
          {isDraft && pmEvent && draftOverride ? (
            checkoutDisabled ? (
              <div className="aui-user-message-content w-full wrap-break-word rounded-t-2xl rounded-bl-2xl rounded-br-[4px] bg-[#f4fbfe] px-4 py-2.5 text-[#2a2a2a] shadow-sm">
                {(() => {
                  const next: ThreadEvent = {
                    ...pmEvent,
                    text: draftOverride.text,
                    photos: draftOverride.photos.slice(),
                    postcardFrontUrl:
                      pmEvent.kind === "postcard"
                        ? getDraftPostcardFrontUrl(draftOverride.payloadBase) || pmEvent.postcardFrontUrl
                        : pmEvent.postcardFrontUrl,
                  };
                  return <PmMessageContent event={next} />;
                })()}
              </div>
            ) : (
              <div className="pm-draft-card flex w-full flex-col gap-2 rounded-t-2xl rounded-bl-2xl rounded-br-[4px] border border-border bg-sky-50 px-4 py-3 text-[#2a2a2a] shadow-sm dark:bg-sky-950/30">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-xs font-medium">Draft</span>
                  {getIsResend(pmEvent.raw) || getIsResend(draftOverride.payloadBase) ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                      <RotateCcwIcon aria-hidden="true" className="h-3 w-3" />
                      Resend
                    </span>
                  ) : null}
                </div>

                {isEditing ? (
                  <div className="text-muted-foreground text-sm">
                    {pmEvent.kind === "postcard" ? "Editing postcard…" : "Editing message…"}
                  </div>
                ) : (
                  <>
                    {draftOverride.text ? (
                      <div className="ph-no-capture whitespace-pre-wrap">
                        {draftOverride.text}
                      </div>
                    ) : null}
                    {pmEvent.kind === "postcard" ? (
                      (() => {
                        const frontUrl =
                          getDraftPostcardFrontUrl(draftOverride.payloadBase) ||
                          pmEvent.postcardFrontUrl ||
                          "";
                        if (!frontUrl) return null;
                        return (
                      <button
                        type="button"
                        className="ph-no-capture w-full overflow-hidden rounded-md"
                        onClick={() => {
                          if (checkoutActive) return;
                          if (isResendDraft) return;
                          draftUi.ensureDraftOverride(pmEvent);
                          setComposeMode("postcard");
                          draftUi.openPostcardEditor(pmEvent.id);
                        }}
                        aria-label="Edit postcard front"
                      >
                        <img
                          src={frontUrl}
                          alt="Postcard front"
                          className="h-40 w-full object-cover [image-orientation:from-image]"
                          loading="lazy"
                          decoding="async"
                        />
                      </button>
                        );
                      })()
                    ) : null}
                    {pmEvent.kind !== "postcard" && draftOverride.photos.length ? (
                      <div className="grid grid-cols-3 gap-2">
                        {draftOverride.photos.map((p, idx) => (
                          <button
                            key={`${p.url}:${idx}`}
                            type="button"
                            className="ph-no-capture aspect-square w-full overflow-hidden rounded-md"
                            onClick={() => {
                              draftUi.ensureDraftOverride(pmEvent);
                              photoViewer.open(draftOverride.photos, idx, {
                                initialView:
                                  draftOverride.photos.length > 1 ? "grid" : "viewer",
                                canRemove: !checkoutActive && !isResendDraft,
                                onRemove: checkoutActive || isResendDraft
                                  ? undefined
                                  : (_photo, removeIdx) =>
                                      draftUi.removeDraftPhoto(pmEvent.id, removeIdx),
                              });
                            }}
                            aria-label={`View draft photo ${idx + 1}`}
                          >
                            <img
                              src={p.url}
                              alt={p.filename || "Draft photo"}
                              className="h-full w-full object-cover [image-orientation:from-image]"
                              loading="lazy"
                              decoding="async"
                            />
                          </button>
                        ))}
                      </div>
                    ) : null}

                    <div className="mt-3 border-t pt-3">
                      {checkoutActive ? (
                        checkoutSending ? (
                          <div className="text-muted-foreground text-sm" aria-live="polite" data-testid="draft-sending">
                            Sending…
                          </div>
                        ) : (
                          <div className="text-muted-foreground text-sm" data-testid="draft-in-checkout">
                            Preparing to send…
                          </div>
                        )
                      ) : (
                        <>
                          {(() => {
                            const isPostcard = toComposerMode(pmEvent.kind) === "postcard";
                            const editLabel = isEditing
                              ? isPostcard
                                ? "View postcard"
                                : "View message"
                              : isResendDraft
                                ? resendNeedsMessage
                                  ? isPostcard
                                    ? "Edit postcard"
                                    : "Edit message"
                                  : "Review & send"
                                : isPostcard
                                  ? "Edit postcard"
                                  : "Edit message";
                            const deleteLabel = isPostcard ? "Delete postcard" : "Delete message";
                            return (
                              <>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="w-full bg-white/70 shadow-sm hover:bg-white"
                                  onClick={() => {
                                    if (checkoutActive) return;
                                    if (isResendDraft && !resendNeedsMessage) {
                                      try {
                                        window.dispatchEvent(
                                          new CustomEvent("pmreact:checkout_started", {
                                            detail: {
                                              penmateId: hostNav.penmateId || undefined,
                                              draftId: pmEvent.id,
                                            },
                                          }),
                                        );
                                      } catch {
                                        // no-op
                                      }
                                      return;
                                    }
                                    if (isEditing) {
                                      draftUi.setEditingDraftId(null);
                                      return;
                                    }
                                    draftUi.ensureDraftOverride(pmEvent);
                                    const mt = toComposerMode(pmEvent.kind);
                                    setComposeMode(mt);
                                    const nextText = clampDraftText({ messageType: mt, text: draftOverride.text });
                                    draftUi.primeDraftAutosave(pmEvent.id, nextText);
                                    draftUi.setEditingDraftId(pmEvent.id);
                                    api.thread().composer.setText(nextText);
                                    requestAnimationFrame(() => {
                                      draftUi.scrollToBottom();
                                      draftUi.focusComposer();
                                    });
                                  }}
                                  disabled={checkoutActive}
                                >
                                  {editLabel}
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  className="mt-2 w-full text-destructive hover:bg-destructive/10 hover:text-destructive"
                                  onClick={() => draftUi.requestDeleteDraft(pmEvent.id)}
                                  disabled={checkoutActive}
                                >
                                  {deleteLabel}
                                </Button>
                              </>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            )
          ) : (
            <>
              <div
                className={cn(
                  "aui-user-message-content w-full wrap-break-word rounded-t-2xl rounded-bl-2xl rounded-br-[4px] px-4 py-2.5 text-[#2a2a2a] shadow-sm",
                  needsInfo ? "border border-rose-200 bg-rose-50" : "bg-[#f4fbfe]",
                )}
              >
                {pmEvent ? <PmMessageContent event={pmEvent} /> : <MessagePrimitive.Parts />}
              </div>
            </>
          )}
        </MessageFrame>
      </div>

      <BranchPicker className="aui-user-branch-picker -mr-1 col-span-full col-start-1 row-start-3 justify-end" />
    </MessagePrimitive.Root>
  );
};

const EditComposer: FC = () => {
  return (
    <MessagePrimitive.Root className="aui-edit-composer-wrapper mx-auto flex w-full max-w-(--thread-max-width) flex-col px-2 py-3">
      <ComposerPrimitive.Root className="aui-edit-composer-root ml-auto flex w-full max-w-[85%] flex-col rounded-2xl bg-muted">
        <ComposerPrimitive.Input
          className="aui-edit-composer-input min-h-14 w-full resize-none bg-transparent p-4 text-foreground text-sm outline-none"
          autoFocus
          submitOnEnter={false}
        />
        <div className="aui-edit-composer-footer mx-3 mb-3 flex items-center gap-2 self-end">
          <ComposerPrimitive.Cancel asChild>
            <Button variant="ghost" size="sm">
              Cancel
            </Button>
          </ComposerPrimitive.Cancel>
          <ComposerPrimitive.Send asChild>
            <Button size="sm">Update</Button>
          </ComposerPrimitive.Send>
        </div>
      </ComposerPrimitive.Root>
    </MessagePrimitive.Root>
  );
};

const BranchPicker: FC<BranchPickerPrimitive.Root.Props> = ({
  className,
  ...rest
}) => {
  return (
    <BranchPickerPrimitive.Root
      hideWhenSingleBranch
      className={cn(
        "aui-branch-picker-root -ml-2 mr-2 inline-flex items-center text-muted-foreground text-xs",
        className,
      )}
      {...rest}
    >
      <BranchPickerPrimitive.Previous asChild>
        <TooltipIconButton tooltip="Previous">
          <ChevronLeftIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Previous>
      <span className="aui-branch-picker-state font-medium">
        <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
      </span>
      <BranchPickerPrimitive.Next asChild>
        <TooltipIconButton tooltip="Next">
          <ChevronRightIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Next>
    </BranchPickerPrimitive.Root>
  );
};
